import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import TimecodeInput from './TimecodeInput';
import { API_CONFIG } from '../config';
import {
  readBiddingShotsPerPage,
  writeBiddingShotsPerPage,
  getUserPreferences,
  subscribeUserPreferences,
  updateUserPreferences,
} from '../utils/userPreferences';
import { Shot as ShotType, ShotAnnotation, ExpenseAssignment, isAdmin } from '../types';
import ArtistRatesModal from './ArtistRatesModal';
import ShotThumbnailUpload from './ShotThumbnailUpload';
import ShotVideoPlayer from './ShotVideoPlayer';
import DeleteConfirmModal from './DeleteConfirmModal';
import ConflictResolutionModal from './ConflictResolutionModal';
import ParticipantDetailsModal from './ParticipantDetailsModal';
import AnnotationModal from './AnnotationModal';
import AutofillModal from './AutofillModal';
import BulkApproveModal from './BulkApproveModal';
import BiddingSettingsModal from './BiddingSettingsModal';
import ExportBidVideoModal from './ExportBidVideoModal';
import ExportBiddingExcelModal from './ExportBiddingExcelModal';
import AiBidPanel from './AiBidPanel';
import { detectShotNamePattern, hasUnfilledShotsAfterFilled, ShotNamePattern } from '../utils/shotNamePattern';
import { parseColumnPaste } from '../utils/parseColumnPaste';
import { getCurrencySymbol } from '../utils/currency';
import { ICONS } from '../constants';
import ContextMenu, { ContextMenuItem } from './ContextMenu';
import ConfirmationModal from './ui/ConfirmationModal';
import BiddingRevisionBanner, { BiddingRevisionBannerKind } from './BiddingRevisionBanner';
import { PIPELINE_SPECIALIZATIONS, VIRTUAL_ROLE_OPTIONS } from '../constants/biddingPipeline';
import { handleBiddingMutationPayload } from '../utils/biddingFork';

// Define virtual artists constant
const VIRTUAL_ARTISTS = [
  { id: 'virtual-junior', name: 'Junior Artist', specialization: 'Virtual', level: 'Junior', type: 'junior' },
  { id: 'virtual-mid', name: 'Mid Artist', specialization: 'Virtual', level: 'Mid', type: 'mid' },
  { id: 'virtual-senior', name: 'Senior Artist', specialization: 'Virtual', level: 'Senior', type: 'senior' },
  { id: 'virtual-lead', name: 'Lead Artist', specialization: 'Virtual', level: 'Lead', type: 'lead' }
];

type BiddingAggregationMode = 'specialization' | 'level';
type BiddingValueColumnMode = 'hours' | 'sum';
type AssignmentLevelKey = 'junior' | 'mid' | 'senior' | 'lead' | 'other';

const AGGREGATION_LEVEL_ORDER: AssignmentLevelKey[] = ['junior', 'mid', 'senior', 'lead', 'other'];

const LEADING_DATA_COLUMN_IDS = ['number', 'thumbnail', 'shotName', 'ai', 'timecodeIn', 'timecodeOut', 'duration'] as const;
const HIDEABLE_COLUMN_IDS = [...LEADING_DATA_COLUMN_IDS, 'total'] as const;

const specColumnId = (spec: string) => `spec:${spec}`;

const filterValidHiddenColumns = (stored: string[], specializations: string[]): string[] => {
  const validFixed = new Set<string>(HIDEABLE_COLUMN_IDS);
  const validSpecIds = new Set(specializations.map(specColumnId));
  return stored.filter((id) => validFixed.has(id) || validSpecIds.has(id));
};

interface ColumnHeaderProps {
  label: React.ReactNode;
  columnId: string;
  onHide: (columnId: string) => void;
  hideTitle: string;
  className?: string;
  onContextMenu?: (e: React.MouseEvent) => void;
}

const ColumnHeader: React.FC<ColumnHeaderProps> = ({ label, columnId, onHide, hideTitle, className = 'justify-between', onContextMenu }) => (
  <div
    className={`relative flex items-center gap-1 min-w-0 group ${className}`}
    onContextMenu={onContextMenu}
  >
    <span className="truncate">{label}</span>
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onHide(columnId);
      }}
      className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-0.5 rounded text-gray-500 hover:text-gray-300 transition-opacity flex-shrink-0 absolute right-0"
      title={hideTitle}
    >
      <ICONS.EyeOff className="w-3 h-3" />
    </button>
  </div>
);

const normalizeAssignmentLevel = (raw: string | null | undefined): AssignmentLevelKey => {
  const value = (raw || '').toLowerCase().trim();
  if (value === 'junior' || value === 'mid' || value === 'senior' || value === 'lead') {
    return value;
  }
  return 'other';
};

interface Artist {
  id: string;
  name: string;
  specialization?: string;
  hourlyRate?: number;
  assignedProjects?: string[];
}

// Use Shot type from types.ts (imported as ShotType)
type Shot = ShotType;

interface ShotAssignment {
  id?: string;
  shotId: string;
  artistId?: string | null; // Optional - NULL for virtual artists or unassigned
  realArtistId?: string | null; // Optional - For expense tracking (real artist)
  specialization: string;
  estimatedHours: number;
  hourlyRate: number;
  realEstimatedHours?: number; // Added for decoupling Expense View hours
  realHourlyRate?: number;     // Added for decoupling Expense View rates
  fixedAmount?: number | null; // Fixed payment amount (overrides hourly calculation) - used in expense sheets
  useFixedAmount?: boolean; // Toggle between hours-based calc and fixed amount in expense view
  isVirtualArtist?: boolean; // TRUE if this is a virtual artist (junior/mid/senior/lead)
  virtualArtistType?: 'junior' | 'mid' | 'senior' | 'lead' | null; // Type of virtual artist
  artistLevel?: string; // e.g. "Junior", "Mid", "Senior", "Lead"
  rowVersion?: number;
  status?: 'Pending' | 'Approved' | 'Paid'; // For expense assignments
  createdAt?: string;
  updatedAt?: string;
}

/** Reconstruct virtual-* artistId for UI selects after server returns artistId=null. */
const normalizeRevenueAssignment = (a: ShotAssignment): ShotAssignment => {
  if (a.isVirtualArtist && a.virtualArtistType && !a.artistId) {
    return { ...a, artistId: `virtual-${a.virtualArtistType}` };
  }
  return a;
};

interface BiddingSheet {
  id: string;
  name: string;
  projectId?: string;
  projectName?: string;
  currency?: string;
  projectCurrency?: string;
  videoStatus?: string;
  status?: string;
  videoUrl?: string;
  videoPath?: string; // Backend returns this field
  producerPercentage?: number;
  producerFixedFee?: number;
  useProducerFixedFee?: boolean;
  coordinatorFee?: number;
  coordinatorPercentage?: number;
  useCoordinatorFixedFee?: boolean;
  supervisorFee?: number;
  supervisorPercentage?: number;
  useSupervisorFixedFee?: boolean;
  finderFee?: number;
  finderPercentage?: number;
  useFinderFixedFee?: boolean;
  vatRate?: number;
  totalNet?: number;
  totalVat?: number;
  totalGross?: number;
  isRevenueSheet?: boolean; // TRUE for revenue (income) sheets, FALSE for expense sheets
  expenseSheetId?: string; // Reference to expense sheet (for revenue sheets)
  revenueSheetId?: string; // Reference to revenue sheet (for expense sheets)
  rowVersion?: number;
  projectBudget?: number;
  valueColumnMode?: 'hours' | 'sum';
  sourceSheetId?: string | null;
  revisionStatus?: 'pending' | 'rejected' | null;
  supersededBy?: string | null;
}

interface ShotTableProps {
  biddingSheet: BiddingSheet;
  onClose: () => void;
  onSave: () => void;
  currentRole?: string | null;
  onExportDataUpdate?: (data: {
    biddingSheet: BiddingSheet;
    shots: Shot[];
    assignments: ShotAssignment[];
    artists: Artist[];
    specializations: string[];
    producerPercentage: number;
    coordinatorFee: number;
  }) => void;
}

const ShotTable: React.FC<ShotTableProps> = ({
  biddingSheet,
  onClose,
  onSave,
  currentRole,
  onExportDataUpdate
}) => {
  const params = useParams<{ biddingSheetId?: string; shotId?: string }>();
  const navigate = useNavigate();
  // Use biddingSheetId from URL params if available, otherwise fall back to prop
  const biddingSheetId = params.biddingSheetId || biddingSheet.id;
  const projectId = biddingSheet.projectId;
  const { t, language } = useTranslation();
  
  // Revenue/Expense table switching
  const [activeTable, setActiveTable] = useState<'revenue' | 'expense'>(() => {
    const prefs = getUserPreferences();
    return prefs.biddingActiveTable === 'expense' ? 'expense' : 'revenue';
  });
  const [aggregationMode, setAggregationMode] = useState<BiddingAggregationMode>(() => {
    const prefs = getUserPreferences();
    return prefs.biddingAggregationMode === 'level' ? 'level' : 'specialization';
  });
  const [valueColumnMode, setValueColumnMode] = useState<BiddingValueColumnMode>(() =>
    biddingSheet.valueColumnMode === 'sum' ? 'sum' : 'hours'
  );
  
  const [shots, setShots] = useState<Shot[]>([]);
  const [revenueAssignments, setRevenueAssignments] = useState<ShotAssignment[]>([]);
  const [expenseAssignments, setExpenseAssignments] = useState<ExpenseAssignment[]>([]);
  
  // Computed property for compatibility with existing render logic
  const assignments = activeTable === 'revenue' 
    ? revenueAssignments 
    : expenseAssignments.map(ea => ({
        ...ea,
        artistId: ea.artistId, // Map to generic artistId for display
        realArtistId: ea.artistId, // Keep for legacy checks
        realEstimatedHours: ea.estimatedHours,
        realHourlyRate: ea.hourlyRate,
        useFixedAmount: ea.useFixedAmount || false,
        isVirtualArtist: false,
        virtualArtistType: null
      } as ShotAssignment));

  const [artists, setArtists] = useState<Artist[]>([]);
  const [specializations, setSpecializations] = useState<string[]>(['CG', 'Comp', 'Mattepainting']);
  const [hiddenColumnIds, setHiddenColumnIds] = useState<string[]>(() => {
    const prefs = getUserPreferences();
    const stored = prefs.biddingHiddenColumns?.[biddingSheetId] ?? [];
    return filterValidHiddenColumns(stored, ['CG', 'Comp', 'Mattepainting']);
  });
  const [loading, setLoading] = useState(true);
  const [isRatesModalOpen, setIsRatesModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isExportVideoOpen, setIsExportVideoOpen] = useState(false);
  const [isExportExcelOpen, setIsExportExcelOpen] = useState(false);
  const [isAiBidOpen, setIsAiBidOpen] = useState(false);
  const [aiAnalysesByShot, setAiAnalysesByShot] = useState<
    Record<string, { shotSize?: string; complexityScore?: number; vfxSignals?: string[]; rationale?: string }>
  >({});
  const [producerPercentage, setProducerPercentage] = useState<number>(0);
  const [producerFixedFee, setProducerFixedFee] = useState<number>(0);
  const [useProducerFixedFee, setUseProducerFixedFee] = useState<boolean>(false);
  const [coordinatorFee, setCoordinatorFee] = useState<number>(0);
  const [coordinatorPercentage, setCoordinatorPercentage] = useState<number>(0);
  const [useCoordinatorFixedFee, setUseCoordinatorFixedFee] = useState<boolean>(true);
  const [supervisorFee, setSupervisorFee] = useState<number>(0);
  const [supervisorPercentage, setSupervisorPercentage] = useState<number>(0);
  const [useSupervisorFixedFee, setUseSupervisorFixedFee] = useState<boolean>(true);
  const [finderFee, setFinderFee] = useState<number>(0);
  const [finderPercentage, setFinderPercentage] = useState<number>(0);
  const [useFinderFixedFee, setUseFinderFixedFee] = useState<boolean>(true);
  const [vatRate, setVatRate] = useState<number>(0);
  const [draggedShotId, setDraggedShotId] = useState<string | null>(null);
  const [dragOverShotId, setDragOverShotId] = useState<string | null>(null);
  const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const autoScrollSpeedRef = useRef<number>(0);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const [artistColors, setArtistColors] = useState<Record<string, { bg: string; text: string; border: string; class: string }>>({});
  const [draggedAssignment, setDraggedAssignment] = useState<{ shotId: string; spec: string; artistId: string } | null>(null);
  const [videoPlayerShot, setVideoPlayerShot] = useState<Shot | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; shotId: string } | null>(null);
  const [columnContextMenu, setColumnContextMenu] = useState<{ x: number; y: number; spec: string } | null>(null);
  const [rowValueClipboard, setRowValueClipboard] = useState<{
    mode: BiddingValueColumnMode;
    values: Record<string, number>;
  } | null>(null);
  const [fillColumnConfirm, setFillColumnConfirm] = useState<{
    spec: string;
    levelLabel: string;
    count: number;
    action: 'assign' | 'clear';
    virtualArtistId?: string;
  } | null>(null);
  const [deleteColumnConfirm, setDeleteColumnConfirm] = useState<{
    spec: string;
    count: number;
  } | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | undefined>(biddingSheet.videoPath || biddingSheet.videoUrl);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(biddingSheet.name);
  const [allBiddingSheets, setAllBiddingSheets] = useState<BiddingSheet[]>([]);
  const [selectedShots, setSelectedShots] = useState<Set<string>>(new Set());
  const [isBulkApproveModalOpen, setIsBulkApproveModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [shotsToDelete, setShotsToDelete] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [shotsPerPage, setShotsPerPage] = useState(() => readBiddingShotsPerPage());
  const [shotsPerPageInput, setShotsPerPageInput] = useState(() => String(readBiddingShotsPerPage()));
  const [producerName, setProducerName] = useState('');
  const [supervisorName, setSupervisorName] = useState('');
  const [participantDetailsModal, setParticipantDetailsModal] = useState<{
    isOpen: boolean;
    artistId: string;
    artistName: string;
    artistColor: string;
  } | null>(null);
  
  const [currentBiddingSheet, setCurrentBiddingSheet] = useState<BiddingSheet>(biddingSheet);
  const [revisionBannerKind, setRevisionBannerKind] = useState<BiddingRevisionBannerKind>(
    biddingSheet.revisionStatus === 'pending' ? 'pending' : null
  );
  const wasPendingRef = useRef(biddingSheet.revisionStatus === 'pending');
  const currency =
    currentBiddingSheet?.projectCurrency ||
    currentBiddingSheet?.currency ||
    biddingSheet.currency ||
    'USD';
  const [profitPool, setProfitPool] = useState<number | null>(null);
  const canViewExpense = currentRole !== 'Demo' && currentRole !== 'Project Manager';
  const canToggleValueColumnMode = isAdmin(currentRole ?? null);
  
  // Real-time sync: track pending changes and last update time
  const [isPendingUpdate, setIsPendingUpdate] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(Date.now());
  const pendingUpdatesRef = useRef<Set<string>>(new Set()); // Track assignment IDs being updated
  const isUpdatingRef = useRef(false); // Prevent concurrent updates
  
  // Track actively editing cells to prevent sync conflicts
  const activelyEditingRef = useRef<Set<string>>(new Set()); // Format: "shotId:spec:field" or "shotId:field"
  const estimatedHoursTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map()); // Track debounce timeouts for estimatedHours
  const timecodeTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map()); // Track debounce timeouts for timecode fields
  const durationTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map()); // Track debounce timeouts for duration
  const timecodeValuesRef = useRef<Map<string, number>>(new Map()); // Track current timecode values: "shotId:field" -> seconds
  const durationValuesRef = useRef<Map<string, number>>(new Map()); // Track current duration values: "shotId" -> frames
  const THUMBNAIL_CAPTURE_MIN_INTERVAL_MS = 5000;
  const thumbnailCaptureSeqRef = useRef<Map<string, number>>(new Map());
  const thumbnailLastCaptureAtRef = useRef<Map<string, number>>(new Map());
  const thumbnailPendingInRef = useRef<Map<string, number>>(new Map());
  const thumbnailThrottleTimerRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  
  // Track local shot order changes to preserve them during sync
  const localShotOrderRef = useRef<Map<string, number>>(new Map()); // shotId -> shotNumber
  
  // Track recently updated assignments/shots from other users (for visual highlighting)
  // Format: { shotId: { field: timestamp } } for shots, { assignmentId: timestamp } for assignments
  const [recentlyUpdated, setRecentlyUpdated] = useState<{
    shotFields: Map<string, Set<string>>; // shotId -> Set of fields (shotName, timecodeIn, timecodeOut, durationFrames, commentsCount)
    assignmentFields: Map<string, Set<string>>; // assignmentId -> Set of fields (artistId, estimatedHours)
  }>({ 
    shotFields: new Map(),
    assignmentFields: new Map()
  });
  
  // Track locally edited cells to avoid highlighting them on this machine
  const localEditsRef = useRef<Map<string, number>>(new Map()); // key: "shotId:field" or "assignmentId:field", value: timestamp
  
  // Track assignment versions to prevent overwriting newer data
  const assignmentVersionsRef = useRef<Map<string, number>>(new Map());
  const skipSpecializationsAutoSaveRef = useRef(false);
  const shotsRef = useRef<Shot[]>([]);
  const assignmentsRef = useRef<ShotAssignment[]>([]); // Keeps revenue assignments
  const expenseAssignmentsRef = useRef<ExpenseAssignment[]>([]); // Keeps expense assignments

  const rememberAssignmentVersion = (a: { id?: string; rowVersion?: number }) => {
    if (a.id && a.rowVersion !== undefined) {
      assignmentVersionsRef.current.set(a.id, a.rowVersion);
    }
  };
  
  // Keep refs in sync with state
  useEffect(() => {
    shotsRef.current = shots;
  }, [shots]);
  
  useEffect(() => {
    assignmentsRef.current = revenueAssignments;
  }, [revenueAssignments]);

  useEffect(() => {
    expenseAssignmentsRef.current = expenseAssignments;
  }, [expenseAssignments]);
  
  // Conflict resolution modal
  const [conflictModal, setConflictModal] = useState<{
    show: boolean;
    assignmentId: string;
    field: string;
    userValue: any;
    serverValue: any;
    currentData: ShotAssignment | null;
  }>({
    show: false,
    assignmentId: '',
    field: '',
    userValue: null,
    serverValue: null,
    currentData: null
  });
  
  // Annotations & Shot Names
  const [annotationModal, setAnnotationModal] = useState<{
    show: boolean;
    shot: Shot | null;
    commentId?: string; // Optional comment ID to highlight/scroll to
  }>({ show: false, shot: null });
  const [shotNameChanges, setShotNameChanges] = useState<Record<string, string>>({});
  const shotNameTimeouts = useRef<Record<string, NodeJS.Timeout>>({});
  
  // Track estimatedHours changes for debounced save (similar to shotNameChanges)
  const [estimatedHoursChanges, setEstimatedHoursChanges] = useState<Record<string, number>>({}); // key: "shotId:spec" -> value
  
  // Autofill Shot Names
  const [autofillModal, setAutofillModal] = useState<{
    show: boolean;
    pattern: ShotNamePattern | null;
    startIndex: number;
    isManual?: boolean;
  }>({ show: false, pattern: null, startIndex: 0, isManual: false });
  

  // Generate color for artist with RGB values
  const generateArtistColor = (artistId: string, index: number) => {
    const colors = [
      { bg: 'rgba(59, 130, 246, 0.2)', text: '#93C5FD', border: 'rgba(59, 130, 246, 0.5)', class: 'bg-blue-500/20 text-blue-300 border-blue-500/50' },
      { bg: 'rgba(34, 197, 94, 0.2)', text: '#86EFAC', border: 'rgba(34, 197, 94, 0.5)', class: 'bg-green-500/20 text-green-300 border-green-500/50' },
      { bg: 'rgba(168, 85, 247, 0.2)', text: '#D8B4FE', border: 'rgba(168, 85, 247, 0.5)', class: 'bg-purple-500/20 text-purple-300 border-purple-500/50' },
      { bg: 'rgba(236, 72, 153, 0.2)', text: '#F9A8D4', border: 'rgba(236, 72, 153, 0.5)', class: 'bg-pink-500/20 text-pink-300 border-pink-500/50' },
      { bg: 'rgba(234, 179, 8, 0.2)', text: '#FDE047', border: 'rgba(234, 179, 8, 0.5)', class: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50' },
      { bg: 'rgba(99, 102, 241, 0.2)', text: '#A5B4FC', border: 'rgba(99, 102, 241, 0.5)', class: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/50' },
      { bg: 'rgba(239, 68, 68, 0.2)', text: '#FCA5A5', border: 'rgba(239, 68, 68, 0.5)', class: 'bg-red-500/20 text-red-300 border-red-500/50' },
      { bg: 'rgba(6, 182, 212, 0.2)', text: '#67E8F9', border: 'rgba(6, 182, 212, 0.5)', class: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/50' },
      { bg: 'rgba(249, 115, 22, 0.2)', text: '#FDBA74', border: 'rgba(249, 115, 22, 0.5)', class: 'bg-orange-500/20 text-orange-300 border-orange-500/50' },
      { bg: 'rgba(20, 184, 166, 0.2)', text: '#5EEAD4', border: 'rgba(20, 184, 166, 0.5)', class: 'bg-teal-500/20 text-teal-300 border-teal-500/50' },
    ];
    return colors[index % colors.length];
  };

  // Assign colors to artists when they load
  useEffect(() => {
    if (artists.length > 0) {
      const colors: Record<string, { bg: string; text: string; border: string; class: string }> = {};
      artists.forEach((artist, index) => {
        colors[artist.id] = generateArtistColor(artist.id, index);
      });
      setArtistColors(colors);
    }
  }, [artists]);

  // Check if autofill was already dismissed for this bidding sheet
  const isAutofillDismissed = (biddingSheetId: string): boolean => {
    try {
      const key = `autofill_dismissed_${biddingSheetId}`;
      const dismissed = localStorage.getItem(key);
      return dismissed === 'true';
    } catch (error) {
      return false;
    }
  };

  // Mark autofill as dismissed for this bidding sheet
  const markAutofillDismissed = (biddingSheetId: string) => {
    try {
      const key = `autofill_dismissed_${biddingSheetId}`;
      localStorage.setItem(key, 'true');
    } catch (error) {
      console.error('Failed to save autofill dismissal:', error);
    }
  };

  const handleActiveTableChange = (table: 'revenue' | 'expense') => {
    setActiveTable(table);
    updateUserPreferences({ biddingActiveTable: table });
  };

  const handleAggregationModeChange = (mode: BiddingAggregationMode) => {
    setAggregationMode(mode);
    updateUserPreferences({ biddingAggregationMode: mode });
  };

  const handleValueColumnModeChange = (mode: BiddingValueColumnMode) => {
    if (!canToggleValueColumnMode) return;

    setValueColumnMode(mode);
    setCurrentBiddingSheet((prev) => ({ ...prev, valueColumnMode: mode }));

    const token = localStorage.getItem('spellbook_token');
    if (!token || !biddingSheetId) return;

    fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ valueColumnMode: mode }),
    }).catch((error) => {
      console.error('Failed to persist value column mode:', error);
    });
  };

  const handleFixedAmountInput = async (
    shotId: string,
    spec: string,
    assignment: ShotAssignment | undefined,
    value: number
  ) => {
    const roundedValue = Math.round(value);

    if (!assignment?.id) {
      if (roundedValue === 0) return;

      try {
        const token = localStorage.getItem('spellbook_token');
        const isRevenue = activeTable === 'revenue';
        const assignmentData = {
          shotId,
          specialization: spec,
          estimatedHours: 0,
          hourlyRate: 0,
          fixedAmount: roundedValue,
          useFixedAmount: true,
          artistId: null,
        };

        const tempId = `temp-${Date.now()}`;
        const tempAssignment = { ...assignmentData, id: tempId };

        if (isRevenue) {
          setRevenueAssignments(prev => [...prev, tempAssignment as ShotAssignment]);
        } else {
          setExpenseAssignments(prev => [...prev, tempAssignment as ExpenseAssignment]);
        }
        pendingUpdatesRef.current.add(tempId);
        setIsPendingUpdate(true);

        const endpoint = isRevenue
          ? `${API_CONFIG.API_URL}/bidding/shots/${shotId}/assignments`
          : `${API_CONFIG.API_URL}/bidding/shots/${shotId}/expense-assignments`;

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(assignmentData),
        });

        if (response.ok) {
          const newAssignment = await response.json();
          pendingUpdatesRef.current.delete(tempId);
          if (newAssignment.id) pendingUpdatesRef.current.add(newAssignment.id);

          const normalized = isRevenue
            ? normalizeRevenueAssignment(newAssignment)
            : newAssignment;
          rememberAssignmentVersion(normalized);

          const updateState = isRevenue ? setRevenueAssignments : setExpenseAssignments;
          updateState((prev: any[]) => prev.map(a => (a.id === tempId ? normalized : a)));

          setTimeout(() => {
            if (newAssignment.id) pendingUpdatesRef.current.delete(newAssignment.id);
            setIsPendingUpdate(false);
          }, 1000);
        } else {
          const updateState = isRevenue ? setRevenueAssignments : setExpenseAssignments;
          updateState((prev: any[]) => prev.filter(a => a.id !== tempId));
          pendingUpdatesRef.current.delete(tempId);
          setIsPendingUpdate(false);
        }
      } catch (error) {
        console.error('Error creating assignment with fixed amount:', error);
      }
      return;
    }

    await performAssignmentFieldsUpdate(
      assignment,
      { fixedAmount: roundedValue, useFixedAmount: true },
      shotId,
      spec
    );
  };

  useEffect(() => {
    return subscribeUserPreferences((prefs) => {
      if (prefs.biddingShotsPerPage !== undefined) {
        const value = readBiddingShotsPerPage();
        setShotsPerPage((prev) => (prev === value ? prev : value));
        setShotsPerPageInput((prev) => (prev === String(value) ? prev : String(value)));
      }
      if (prefs.biddingActiveTable) {
        setActiveTable((prev) => (prev === prefs.biddingActiveTable ? prev : prefs.biddingActiveTable!));
      }
      if (prefs.biddingAggregationMode) {
        setAggregationMode((prev) =>
          prev === prefs.biddingAggregationMode ? prev : prefs.biddingAggregationMode!
        );
      }
    });
  }, []);

  // Auto-detect shot name pattern when shot names change
  // Only show automatically if data is loaded and user is actively editing (not on initial load)
  useEffect(() => {
    // Don't show autofill on initial load - wait for user interaction
    if (loading) return;
    if (shots.length < 2) return;
    
    // Check if autofill was already dismissed for this bidding sheet
    if (isAutofillDismissed(biddingSheetId)) return;
    
    // Check if there are unfilled shots after filled ones
    if (!hasUnfilledShotsAfterFilled(shots)) return;
    
    // Detect pattern
    const pattern = detectShotNamePattern(shots);
    if (!pattern) return;
    
    // Find first unfilled index after last filled
    const lastFilledIndex = shots.map((s, i) => ({ shot: s, index: i }))
      .filter(({ shot }) => shot.shotName && shot.shotName.trim())
      .pop()?.index ?? -1;
    
    const startIndex = lastFilledIndex + 1;
    
    // Show autofill suggestion only if:
    // 1. Not already shown
    // 2. Annotation modal is NOT open
    // 3. Data has been loaded (not initial load)
    // 4. Current user is the one who made the changes (check userId)
    const currentUserId = localStorage.getItem('userId');
    const autofillUserIdKey = `autofill_user_${biddingSheetId}`;
    const lastAutofillUserId = localStorage.getItem(autofillUserIdKey);
    
    // Only show if current user made the changes or no user has triggered it yet
    // AND only if we're not in initial loading state
    if (!autofillModal.show && !annotationModal.show && startIndex < shots.length && !loading) {
      if (!lastAutofillUserId || lastAutofillUserId === currentUserId) {
      setAutofillModal({ show: true, pattern, startIndex });
        // Store current user as the one who triggered autofill
        if (currentUserId) {
          localStorage.setItem(autofillUserIdKey, currentUserId);
    }
      }
    }
  }, [shots, biddingSheetId, loading]);

  // Close context menus on click outside
  useEffect(() => {
    const handleClick = () => {
      setContextMenu(null);
      setColumnContextMenu(null);
    };
    if (contextMenu || columnContextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu, columnContextMenu]);

  // Cleanup auto-scroll on unmount
  useEffect(() => {
    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
      autoScrollSpeedRef.current = 0;
    };
  }, []);

  // Fetch data on mount and when biddingSheetId changes
  useEffect(() => {
    // Ensure biddingSheetId is available before fetching
    if (biddingSheetId || biddingSheet.id) {
    fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biddingSheetId]);

  // Real-time polling: sync data every 2 seconds when component is visible
  useEffect(() => {
    if (!biddingSheetId || loading) return;

    const intervalId = setInterval(() => {
      // Skip if component is not visible or user is actively editing
      if (document.hidden || isPendingUpdate || pendingUpdatesRef.current.size > 0 || isUpdatingRef.current) {
        return;
      }
      
      // Skip if any cell is actively being edited
      if (activelyEditingRef.current.size > 0) {
        return;
      }

      // Skip if recently updated (within last 1 second)
      const timeSinceLastUpdate = Date.now() - lastUpdateTime;
      if (timeSinceLastUpdate < 1000) {
        return;
      }

      // Silent background sync without showing loading state
      syncDataSilently();
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(intervalId);
  }, [biddingSheetId, loading]); // Removed shots and assignments from dependencies to prevent infinite loop

  // Listen for visibility changes to pause/resume polling
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !loading && !isPendingUpdate) {
        // Sync immediately when tab becomes visible
        syncDataSilently();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loading, isPendingUpdate]);

  // Smart sync function that merges data without losing user's changes
  const syncDataSilently = async () => {
    if (isUpdatingRef.current) return; // Prevent concurrent syncs
    if (pendingUpdatesRef.current.size > 0) return; // Skip if user is editing
    if (activelyEditingRef.current.size > 0) return; // Skip if any cell is actively being edited

    try {
      isUpdatingRef.current = true;
      const token = localStorage.getItem('spellbook_token');

      // Revision lifecycle: promote / reject while editor is still on the sheet
      const metaRes = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (metaRes.status === 404 && wasPendingRef.current) {
        const sourceId = currentBiddingSheet.sourceSheetId;
        setRevisionBannerKind('rejected');
        if (sourceId) navigate(`/bidding/${sourceId}`);
        return;
      }
      if (metaRes.ok) {
        const meta = await metaRes.json();
        setCurrentBiddingSheet((prev) => ({ ...prev, ...meta }));
        if (wasPendingRef.current && meta.revisionStatus !== 'pending') {
          wasPendingRef.current = false;
          setRevisionBannerKind('approved');
        } else if (meta.revisionStatus === 'pending') {
          wasPendingRef.current = true;
          setRevisionBannerKind('pending');
        }
      }
      
      // Get current state from refs
      const currentShots = shotsRef.current;
      const currentAssignments = assignmentsRef.current;
      
      // Fetch shots with comments count and rowVersion
      const shotsResponse = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/shots`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      let fetchedShots: Shot[] = [];
      const updatedShotFields = new Map<string, Set<string>>(); // shotId -> Set of changed fields
      if (shotsResponse.ok) {
        const shotsData = await shotsResponse.json();
        fetchedShots = shotsData.shots || [];
        
        // Update shots with server data, preserving local changes, but only if data actually changed
        setShots(prevShots => {
          // Check if anything actually changed
          let hasChanges = false;
          const newShots = prevShots.map(localShot => {
            const serverShot = fetchedShots.find((s: Shot) => s.id === localShot.id);
            if (!serverShot) return localShot;
            
            // Preserve local shot order if it was changed locally
            const localOrder = localShotOrderRef.current.get(localShot.id);
            const shotNumber = localOrder !== undefined ? localOrder : serverShot.shotNumber;
            
            // Check if server has newer version
            const serverVersion = serverShot.rowVersion || 0;
            const localVersion = localShot.rowVersion || 0;
            
            // NEVER update shots that are in pendingUpdatesRef, even if server has newer version
            // This prevents race conditions during drag&drop reordering
            if (pendingUpdatesRef.current.has(localShot.id)) {
              return localShot; // Keep local version unchanged
            }
            
            // Use server data if it's newer
            if (serverVersion > localVersion) {
              const changedFields = new Set<string>();
              
              // Check if fields are actively being edited - if so, preserve local values
              const isEditingShotName = activelyEditingRef.current.has(`${localShot.id}:shotName`);
              const isEditingTimecodeIn = activelyEditingRef.current.has(`${localShot.id}:timecodeIn`);
              const isEditingTimecodeOut = activelyEditingRef.current.has(`${localShot.id}:timecodeOut`);
              const isEditingDuration = activelyEditingRef.current.has(`${localShot.id}:durationFrames`);
              
              // Check specific fields for changes, but skip if actively being edited
              if (!isEditingShotName && serverShot.shotName !== localShot.shotName) {
                changedFields.add('shotName');
              }
              if (!isEditingTimecodeIn && serverShot.timecodeInSeconds !== localShot.timecodeInSeconds) {
                changedFields.add('timecodeIn');
              }
              if (!isEditingTimecodeOut && serverShot.timecodeOutSeconds !== localShot.timecodeOutSeconds) {
                changedFields.add('timecodeOut');
              }
              if (!isEditingDuration && serverShot.durationFrames !== localShot.durationFrames) {
                changedFields.add('durationFrames');
              }
              if (serverShot.commentsCount !== localShot.commentsCount) {
                changedFields.add('commentsCount');
              }
              
              if (changedFields.size > 0) {
                hasChanges = true;
                updatedShotFields.set(localShot.id, changedFields);
              }
              
              return {
                ...serverShot,
                shotNumber, // Use preserved local order
                // Preserve local values if actively being edited
                shotName: isEditingShotName ? localShot.shotName : (shotNameChanges[localShot.id] || serverShot.shotName || serverShot.shotName),
                // For timecode, also check if there's a pending timeout (active editing via arrows)
                timecodeInSeconds: isEditingTimecodeIn || timecodeTimeoutsRef.current.has(`${localShot.id}:timecodeIn`) 
                  ? localShot.timecodeInSeconds 
                  : serverShot.timecodeInSeconds,
                timecodeOutSeconds: isEditingTimecodeOut || timecodeTimeoutsRef.current.has(`${localShot.id}:timecodeOut`) 
                  ? localShot.timecodeOutSeconds 
                  : serverShot.timecodeOutSeconds,
                durationFrames: isEditingDuration ? localShot.durationFrames : serverShot.durationFrames,
              };
            }
            
            return {
              ...localShot,
              shotNumber // Use preserved local order
            };
          });
          
          // Sort by shotNumber to maintain order
          newShots.sort((a, b) => a.shotNumber - b.shotNumber);
          
          // Add new shots from server
          const newShotsFromServer = fetchedShots.filter((serverShot: Shot) => 
            !prevShots.find(localShot => localShot.id === serverShot.id)
          );
          
          if (newShotsFromServer.length > 0) {
            hasChanges = true;
          }
          
          // Only update if something changed
          if (!hasChanges && newShotsFromServer.length === 0) {
            return prevShots; // Return same reference to prevent re-render
          }
          
          return newShots.concat(newShotsFromServer);
        });
      }
      
      // Fetch assignments silently - use refs to get current state
      // Separate logic for Revenue and Expense
      
      const allRevenueAssignments: ShotAssignment[] = [];
      const allExpenseAssignments: ExpenseAssignment[] = [];
      
      const updatedAssignmentFields = new Map<string, Set<string>>(); // assignmentId -> Set of changed fields
      
      await Promise.all(
        currentShots.map(async shot => {
            // Check pending updates for this shot
            // Simplified: If any assignment for this shot is pending, skip fetch for this shot to avoid race conditions
            // Ideally we check per assignment ID, but IDs might not exist yet for new ones.
            // For now, check if we have pending updates for any assignment linked to this shot
            const pendingRevenue = assignmentsRef.current.filter(
              (a) => a.shotId === shot.id && a.id && pendingUpdatesRef.current.has(a.id)
            );
            const pendingExpense = expenseAssignmentsRef.current.filter(
              (a) => a.shotId === shot.id && a.id && pendingUpdatesRef.current.has(a.id)
            );
            
            if (pendingRevenue.length === 0 && pendingExpense.length === 0) {
            try {
              const assignResponse = await fetch(`${API_CONFIG.API_URL}/bidding/shots/${shot.id}/assignments`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              if (assignResponse.ok) {
                const assignData = await assignResponse.json();
                        const rAssignments = assignData.revenue || assignData.assignments || [];
                        const eAssignments = assignData.expense || [];
                        
                        return { rAssignments, eAssignments };
                    }
                 } catch (e) { console.error(e); }
            }
            return null;
        })
      ).then(results => {
          results.forEach(res => {
              if (res) {
                  allRevenueAssignments.push(...res.rAssignments);
                  allExpenseAssignments.push(...res.eAssignments);
              }
          });
      });
      
      // Update Revenue Assignments State
      setRevenueAssignments(prev => {
          // Process server assignments to reconstruct virtual-* IDs
          const processedRevenue = allRevenueAssignments.map((a: ShotAssignment) => {
            const normalized = normalizeRevenueAssignment(a);
            rememberAssignmentVersion(normalized);
            return normalized;
          });
          
          // Merge logic - keep pending ones and recently edited ones from prev
          const merged = [...processedRevenue];
          const now = Date.now();
          const RECENT_EDIT_THRESHOLD = 3000; // 3 seconds
          
          prev.forEach(p => {
              if (!p.id) return;
              
              // Check if this assignment is pending update
              const isPending = pendingUpdatesRef.current.has(p.id);
              
              // Check if any field was recently edited (within last 3 seconds)
              const wasRecentlyEdited = Array.from(localEditsRef.current.entries()).some(([key, timestamp]) => {
                return key.startsWith(`${p.id}:`) && (now - timestamp) < RECENT_EDIT_THRESHOLD;
              });
              
              if (isPending || wasRecentlyEdited) {
                  const idx = merged.findIndex(m => m.id === p.id);
                  if (idx >= 0) merged[idx] = p;
                  else merged.push(p);
              }
          });
          return merged;
      });

      // Update Expense Assignments State
      setExpenseAssignments(prev => {
          const merged = [...allExpenseAssignments];
          allExpenseAssignments.forEach((a) => rememberAssignmentVersion(a));
          const now = Date.now();
          const RECENT_EDIT_THRESHOLD = 3000; // 3 seconds
          
          prev.forEach(p => {
              if (!p.id) return;
              
              // Check if this assignment is pending update
              const isPending = pendingUpdatesRef.current.has(p.id);
              
              // Check if any field was recently edited (within last 3 seconds)
              const wasRecentlyEdited = Array.from(localEditsRef.current.entries()).some(([key, timestamp]) => {
                return key.startsWith(`${p.id}:`) && (now - timestamp) < RECENT_EDIT_THRESHOLD;
              });
              
              if (isPending || wasRecentlyEdited) {
                  const idx = merged.findIndex(m => m.id === p.id);
                  if (idx >= 0) merged[idx] = p;
                  else merged.push(p);
              }
          });
          return merged;
      });

      setLastUpdateTime(Date.now());
    } catch (error) {
      console.error('Error syncing data:', error);
    } finally {
      isUpdatingRef.current = false;
    }
  };

  // Update export data when data changes
  useEffect(() => {
    if (onExportDataUpdate && shots.length > 0) {
      onExportDataUpdate({
        biddingSheet: {
          ...currentBiddingSheet,
          valueColumnMode,
          producerPercentage,
          producerFixedFee,
          useProducerFixedFee,
          coordinatorFee,
          coordinatorPercentage,
          useCoordinatorFixedFee,
          supervisorFee,
          supervisorPercentage,
          useSupervisorFixedFee,
          finderFee,
          finderPercentage,
          useFinderFixedFee,
          vatRate,
        },
        shots,
        assignments,
        artists,
        specializations,
        producerPercentage,
        coordinatorFee,
      });
    }
  }, [
    shots,
    assignments,
    artists,
    specializations,
    currentBiddingSheet,
    valueColumnMode,
    producerPercentage,
    producerFixedFee,
    useProducerFixedFee,
    coordinatorFee,
    coordinatorPercentage,
    useCoordinatorFixedFee,
    supervisorFee,
    supervisorPercentage,
    useSupervisorFixedFee,
    finderFee,
    finderPercentage,
    useFinderFixedFee,
    vatRate,
    onExportDataUpdate,
  ]);

  // Auto-save specializations when they change (debounced)
  useEffect(() => {
    // Skip on initial render (when specializations are default or just loaded)
    if (loading) return;
    
    // Debounce: save after 1 second of no changes
    const timeoutId = setTimeout(async () => {
      if (skipSpecializationsAutoSaveRef.current) {
        skipSpecializationsAutoSaveRef.current = false;
        return;
      }

      try {
        const token = localStorage.getItem('spellbook_token');
        
        const response = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/specializations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ specializations })
        });
        
        if (response.ok) {
          // Specializations auto-saved successfully
        } else {
          console.error('❌ Failed to auto-save specializations:', response.status);
        }
      } catch (error) {
        console.error('❌ Error auto-saving specializations:', error);
      }
    }, 1000);
    
    // Cleanup timeout on unmount or when specializations change again
    return () => clearTimeout(timeoutId);
  }, [specializations, biddingSheetId, loading]);

  useEffect(() => {
    const prefs = getUserPreferences();
    const stored = prefs.biddingHiddenColumns?.[biddingSheetId] ?? [];
    setHiddenColumnIds(filterValidHiddenColumns(stored, specializations));
  }, [biddingSheetId, specializations]);

  const hiddenColumnSet = useMemo(() => new Set(hiddenColumnIds), [hiddenColumnIds]);
  const isColumnVisible = useCallback(
    (columnId: string) => !hiddenColumnSet.has(columnId),
    [hiddenColumnSet]
  );
  const visibleSpecs = useMemo(
    () => specializations.filter((spec) => isColumnVisible(specColumnId(spec))),
    [specializations, isColumnVisible]
  );
  const visibleLeadingCount = useMemo(
    () => LEADING_DATA_COLUMN_IDS.filter((id) => isColumnVisible(id)).length,
    [isColumnVisible]
  );
  const columnHideTitle = t('bidding.columns.hide') || 'Hide column';

  const hideColumn = useCallback(
    (columnId: string) => {
      setHiddenColumnIds((prev) => {
        if (prev.includes(columnId)) return prev;
        const next = [...prev, columnId];
        const prefs = getUserPreferences();
        updateUserPreferences({
          biddingHiddenColumns: {
            ...(prefs.biddingHiddenColumns ?? {}),
            [biddingSheetId]: next,
          },
        });
        return next;
      });
    },
    [biddingSheetId]
  );

  const handleUnhideAllColumns = useCallback(() => {
    setHiddenColumnIds([]);
    const prefs = getUserPreferences();
    updateUserPreferences({
      biddingHiddenColumns: {
        ...(prefs.biddingHiddenColumns ?? {}),
        [biddingSheetId]: [],
      },
    });
  }, [biddingSheetId]);

  const countUnassignedInColumn = useCallback(
    (spec: string) =>
      shots.filter((shot) => {
        const assignment = revenueAssignments.find(
          (a) => a.shotId === shot.id && a.specialization === spec
        );
        return !assignment || (!assignment.artistId && !assignment.isVirtualArtist);
      }).length,
    [shots, revenueAssignments]
  );

  const countAssignedInColumn = useCallback(
    (spec: string) =>
      shots.filter((shot) => {
        const assignment = revenueAssignments.find(
          (a) => a.shotId === shot.id && a.specialization === spec
        );
        return !!(assignment?.artistId || assignment?.isVirtualArtist);
      }).length,
    [shots, revenueAssignments]
  );

  const refreshAssignmentsFromServer = useCallback(async () => {
    const token = localStorage.getItem('spellbook_token');
    const activeSheetId = biddingSheetId || currentBiddingSheet?.id || biddingSheet.id;
    if (!token || !activeSheetId) return;

    const allRevenueAssignments: ShotAssignment[] = [];
    const allExpenseAssignments: ExpenseAssignment[] = [];

    await Promise.all(
      shotsRef.current.map(async (shot) => {
        try {
          const assignResponse = await fetch(
            `${API_CONFIG.API_URL}/bidding/shots/${shot.id}/assignments`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!assignResponse.ok) return;

          const assignData = await assignResponse.json();
          const rAssignments = assignData.revenue || assignData.assignments || [];
          const eAssignments = assignData.expense || [];

          rAssignments.forEach((a: ShotAssignment) => {
            const processed = normalizeRevenueAssignment(a);
            allRevenueAssignments.push(processed);
            rememberAssignmentVersion(processed);
          });

          eAssignments.forEach((a: ExpenseAssignment) => {
            allExpenseAssignments.push(a);
            rememberAssignmentVersion(a);
          });
        } catch (error) {
          console.error('Failed to refresh assignments for shot', shot.id, error);
        }
      })
    );

    setRevenueAssignments(allRevenueAssignments);
    setExpenseAssignments(allExpenseAssignments);
  }, [biddingSheetId, currentBiddingSheet?.id, biddingSheet.id]);

  const handleRenameRole = async (from: string, to: string) => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(
        `${API_CONFIG.API_URL}/bidding/${biddingSheetId}/specializations/rename`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ from, to }),
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(data.error || t('bidding.renameSpecializationFailed'));
        return;
      }

      const data = await response.json();
      skipSpecializationsAutoSaveRef.current = true;
      setSpecializations(data.specializations ?? specializations.map((s) => (s === from ? to : s)));
      setHiddenColumnIds((prev) => {
        const fromId = specColumnId(from);
        const toId = specColumnId(to);
        if (!prev.includes(fromId)) return prev;
        const next = prev.map((id) => (id === fromId ? toId : id));
        const prefs = getUserPreferences();
        updateUserPreferences({
          biddingHiddenColumns: {
            ...(prefs.biddingHiddenColumns ?? {}),
            [biddingSheetId]: next,
          },
        });
        return next;
      });
      await refreshAssignmentsFromServer();
      setLastUpdateTime(Date.now());
    } catch (error) {
      console.error('Error renaming specialization:', error);
      alert(t('bidding.renameSpecializationFailed'));
    }
  };

  const handleDeleteColumn = async (spec: string) => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(
        `${API_CONFIG.API_URL}/bidding/${biddingSheetId}/specializations/remove`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ specialization: spec }),
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(data.error || t('bidding.deleteColumnFailed'));
        return;
      }

      const data = await response.json();
      skipSpecializationsAutoSaveRef.current = true;
      setSpecializations(
        data.specializations ?? specializations.filter((s) => s !== spec)
      );
      setHiddenColumnIds((prev) => {
        const removedId = specColumnId(spec);
        if (!prev.includes(removedId)) return prev;
        const next = prev.filter((id) => id !== removedId);
        const prefs = getUserPreferences();
        updateUserPreferences({
          biddingHiddenColumns: {
            ...(prefs.biddingHiddenColumns ?? {}),
            [biddingSheetId]: next,
          },
        });
        return next;
      });
      await refreshAssignmentsFromServer();
      setLastUpdateTime(Date.now());
    } catch (error) {
      console.error('Error deleting column:', error);
      alert(t('bidding.deleteColumnFailed'));
    }
  };

  const handleFillColumnWithLevel = async (spec: string, virtualArtistId: string) => {
    for (const shot of shots) {
      const assignment = assignmentsRef.current.find(
        (a) => a.shotId === shot.id && a.specialization === spec
      );
      if (assignment?.artistId || assignment?.isVirtualArtist) continue;
      await handleUpdateAssignment(shot.id, spec, 'artistId', virtualArtistId);
    }
  };

  const handleClearColumnWithLevel = async (spec: string) => {
    for (const shot of shots) {
      const assignment = assignmentsRef.current.find(
        (a) => a.shotId === shot.id && a.specialization === spec
      );
      if (!assignment?.artistId && !assignment?.isVirtualArtist) continue;
      await handleUpdateAssignment(shot.id, spec, 'artistId', null);
    }
  };

  const countAssignmentsInColumn = useCallback(
    (spec: string) => {
      const revenueCount = revenueAssignments.filter((a) => a.specialization === spec).length;
      const expenseCount = expenseAssignments.filter((a) => a.specialization === spec).length;
      return revenueCount + expenseCount;
    },
    [revenueAssignments, expenseAssignments]
  );

  const getColumnContextMenuItems = (spec: string): ContextMenuItem[] => {
    const renameTargets = PIPELINE_SPECIALIZATIONS.filter(
      (pipelineSpec) => pipelineSpec !== spec && !specializations.includes(pipelineSpec)
    );
    const isLastColumn = specializations.length <= 1;

    return [
      {
        label: t('bidding.renameSpecialization'),
        subMenu:
          renameTargets.length > 0
            ? renameTargets.map((target) => ({
                label: target,
                onClick: () => {
                  void handleRenameRole(spec, target);
                },
              }))
            : [
                {
                  label: t('bidding.noRenameTargets'),
                  onClick: () => {},
                  disabled: true,
                },
              ],
      },
      {
        label: t('bidding.fillColumnWithLevel'),
        subMenu: [
          {
            label: t('common.notAssigned'),
            onClick: () => {
              const count = countAssignedInColumn(spec);
              if (count === 0) return;
              setFillColumnConfirm({
                spec,
                levelLabel: t('common.notAssigned'),
                count,
                action: 'clear',
              });
            },
            disabled: countAssignedInColumn(spec) === 0,
          },
          ...VIRTUAL_ROLE_OPTIONS.map((role) => ({
            label: t(role.labelKey),
            onClick: () => {
              const count = countUnassignedInColumn(spec);
              if (count === 0) return;
              setFillColumnConfirm({
                spec,
                virtualArtistId: role.id,
                levelLabel: t(role.labelKey),
                count,
                action: 'assign',
              });
            },
            disabled: countUnassignedInColumn(spec) === 0,
          })),
        ],
      },
      { separator: true, label: '' },
      {
        label: t('bidding.deleteColumn'),
        danger: true,
        disabled: isLastColumn,
        onClick: () => {
          if (isLastColumn) {
            alert(t('bidding.cannotDeleteLastColumn'));
            return;
          }
          setDeleteColumnConfirm({
            spec,
            count: countAssignmentsInColumn(spec),
          });
        },
      },
    ];
  };

  // Determine which bidding sheet to load - always use the same sheet ID (revenue sheet)
  // The expense table is just a virtual representation of the same data
  const getActiveBiddingSheetId = (): string => {
    // Always use the revenue sheet ID - expense is just a different view
    return biddingSheetId || currentBiddingSheet?.id || biddingSheet.id || '';
  };

  // Initialize current bidding sheet
  useEffect(() => {
    // Ensure biddingSheet has an id before setting it
    if (biddingSheet.id) {
      setCurrentBiddingSheet(biddingSheet);
    }
  }, [biddingSheet]);

  // Switch table when activeTable changes - just update view, no data reload needed
  // The expense table is a virtual representation of the same data
  useEffect(() => {
    // No need to fetch data - we're just changing the view
    // The same assignments are used, just displayed differently
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTable]);

  // Update profit pool when data changes
  // Profit pool is calculated as the difference between revenue (virtual artists) and expense (real artists)
  useEffect(() => {
    if (canViewExpense) {
      calculateProfitPool().then(setProfitPool);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shots, assignments]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('spellbook_token');
      const activeSheetId = getActiveBiddingSheetId();
      
      // Validate activeSheetId
      if (!activeSheetId) {
        console.error('No active sheet ID found!', { biddingSheetId, currentBiddingSheet, activeTable });
        setLoading(false);
        return;
      }
      
      // Fetch bidding sheet to get producer percentage and video URL
      const biddingResponse = await fetch(`${API_CONFIG.API_URL}/bidding/${activeSheetId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (biddingResponse.ok) {
        const biddingData = await biddingResponse.json();
        // Backend returns 'videoPath', not 'videoUrl'
        const fetchedVideoUrl = biddingData.videoPath || biddingData.videoUrl || biddingData.video_url;
        setProducerPercentage(Number(biddingData.producerPercentage) || 0);
        setProducerFixedFee(Number(biddingData.producerFixedFee) || 0);
        setUseProducerFixedFee(biddingData.useProducerFixedFee || false);
        setCoordinatorFee(Number(biddingData.coordinatorFee) || 0);
        setCoordinatorPercentage(Number(biddingData.coordinatorPercentage) || 0);
        setUseCoordinatorFixedFee(biddingData.useCoordinatorFixedFee !== false);
        setSupervisorFee(Number(biddingData.supervisorFee) || 0);
        setSupervisorPercentage(Number(biddingData.supervisorPercentage) || 0);
        setUseSupervisorFixedFee(biddingData.useSupervisorFixedFee !== false);
        setFinderFee(Number(biddingData.finderFee) || 0);
        setFinderPercentage(Number(biddingData.finderPercentage) || 0);
        setUseFinderFixedFee(biddingData.useFinderFixedFee !== false);
        setVatRate(Number(biddingData.vatRate) || 0);
        setVideoUrl(fetchedVideoUrl);
        
        // Backward compatibility: ensure isRevenueSheet is set
        if (biddingData.isRevenueSheet === undefined) {
          biddingData.isRevenueSheet = true;
        }
        
        setCurrentBiddingSheet(biddingData);
        wasPendingRef.current = biddingData.revisionStatus === 'pending';
        setRevisionBannerKind(biddingData.revisionStatus === 'pending' ? 'pending' : revisionBannerKind === 'approved' ? 'approved' : null);

        setValueColumnMode(biddingData.valueColumnMode === 'sum' ? 'sum' : 'hours');
        
        // If switching to expense sheet, update the active sheet ID
        if (activeTable === 'expense' && biddingData.id) {
          // Expense sheet loaded
        }
      }

      // Fetch all bidding sheets for project to check for duplicates
      if (projectId) {
        const allBiddingsResponse = await fetch(`${API_CONFIG.API_URL}/bidding/project/${projectId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (allBiddingsResponse.ok) {
          const data = await allBiddingsResponse.json();
          setAllBiddingSheets(data.biddingSheets || []);
        }
      }
      
      // Fetch shots
      const shotsResponse = await fetch(`${API_CONFIG.API_URL}/bidding/${activeSheetId}/shots`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      let fetchedShots: Shot[] = [];
      if (shotsResponse.ok) {
        const shotsData = await shotsResponse.json();
        fetchedShots = shotsData.shots || [];
        setShots(fetchedShots);
        console.log(`Loaded ${fetchedShots.length} shots for sheet ${activeSheetId}`);
      } else {
        const errorText = await shotsResponse.text();
        console.error(`Failed to fetch shots for sheet ${activeSheetId}:`, shotsResponse.status, errorText);
        setShots([]); // Set empty array on error
      }

      // Fetch ALL artists to ensure we have both Project Team and Global Roles (Virtual Artists)
      const artistsResponse = await fetch(`${API_CONFIG.API_URL}/artists`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (artistsResponse.ok) {
        const artistsData = await artistsResponse.json();
        // Handle both array and object response formats
        const allArtists = Array.isArray(artistsData) ? artistsData : (artistsData.artists || []);
        
        // Merge virtual artists (ensure no duplicates if they somehow exist)
        const virtualIds = new Set(VIRTUAL_ARTISTS.map(v => v.id));
        const mergedArtists = [
          ...allArtists.filter((a: any) => !virtualIds.has(a.id)), 
          ...VIRTUAL_ARTISTS
        ];
        setArtists(mergedArtists);
      }

      // Fetch assignments and initialize versions
      const allRevenueAssignments: ShotAssignment[] = [];
      const allExpenseAssignments: ExpenseAssignment[] = [];

      for (const shot of fetchedShots) {
        const assignResponse = await fetch(`${API_CONFIG.API_URL}/bidding/shots/${shot.id}/assignments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (assignResponse.ok) {
          const assignData = await assignResponse.json();
          const rAssignments = assignData.revenue || assignData.assignments || [];
          const eAssignments = assignData.expense || [];
          
          // Process revenue assignments to reconstruct virtual-* IDs
          const processedRevenue = rAssignments.map((a: ShotAssignment) =>
            normalizeRevenueAssignment(a)
          );
          
          allRevenueAssignments.push(...processedRevenue);
          allExpenseAssignments.push(...eAssignments);
          
          // Initialize assignment versions (Revenue)
          processedRevenue.forEach((a: ShotAssignment) => {
            rememberAssignmentVersion(a);
          });
          // Initialize assignment versions (Expense) - using same ref map, assuming UUIDs no collision
          eAssignments.forEach((a: ExpenseAssignment) => {
            rememberAssignmentVersion(a);
          });
        }
      }
      setRevenueAssignments(allRevenueAssignments);
      setExpenseAssignments(allExpenseAssignments);

      // Zero orphan hours/amounts (no executor) so user doesn't have to click each cell.
      // SAFETY: never touch rows that have a real artistId, custom participant (via artistId),
      // or any virtual-artist flag/type — those keep estimatedHours / fixedAmount as entered.
      const revenueNeedsClear = (a: ShotAssignment) => {
        const hasExec = !!(
          a.artistId ||
          a.isVirtualArtist ||
          a.virtualArtistType
        );
        if (hasExec) return false;
        return (
          (Number(a.estimatedHours) || 0) !== 0 ||
          (Number(a.fixedAmount) || 0) !== 0 ||
          !!a.useFixedAmount
        );
      };
      const expenseNeedsClear = (a: ExpenseAssignment) => {
        // Expense: only real artistId counts as executor
        if (a.artistId) return false;
        return (
          (Number(a.estimatedHours) || 0) !== 0 ||
          (Number(a.fixedAmount) || 0) !== 0 ||
          !!a.useFixedAmount
        );
      };

      const orphanRevenue = allRevenueAssignments.filter(revenueNeedsClear);
      const orphanExpense = allExpenseAssignments.filter(expenseNeedsClear);

      if (orphanRevenue.length > 0 || orphanExpense.length > 0) {
        const clearedRevenue = allRevenueAssignments.map((a) =>
          revenueNeedsClear(a)
            ? { ...a, estimatedHours: 0, fixedAmount: 0, useFixedAmount: false }
            : a
        );
        const clearedExpense = allExpenseAssignments.map((a) =>
          expenseNeedsClear(a)
            ? { ...a, estimatedHours: 0, fixedAmount: 0, useFixedAmount: false }
            : a
        );
        setRevenueAssignments(clearedRevenue);
        setExpenseAssignments(clearedExpense);

        void (async () => {
          for (const a of orphanRevenue) {
            if (!a.id || String(a.id).startsWith('temp-')) continue;
            try {
              const res = await fetch(`${API_CONFIG.API_URL}/bidding/assignments/${a.id}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                  estimatedHours: 0,
                  fixedAmount: 0,
                  useFixedAmount: false,
                  rowVersion: a.rowVersion,
                }),
              });
              if (res.ok) {
                const updated = await res.json();
                if (updated.id && updated.rowVersion !== undefined) {
                  assignmentVersionsRef.current.set(updated.id, updated.rowVersion);
                }
                if (updated.isVirtualArtist && updated.virtualArtistType && !updated.artistId) {
                  updated.artistId = `virtual-${updated.virtualArtistType}`;
                }
                setRevenueAssignments((prev) =>
                  prev.map((x) => (x.id === a.id ? { ...x, ...updated, estimatedHours: 0, fixedAmount: 0, useFixedAmount: false } : x))
                );
              }
            } catch (e) {
              console.warn('Failed to clear orphan revenue assignment', a.id, e);
            }
          }
          for (const a of orphanExpense) {
            if (!a.id || String(a.id).startsWith('temp-')) continue;
            try {
              const res = await fetch(`${API_CONFIG.API_URL}/bidding/expense-assignments/${a.id}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                  estimatedHours: 0,
                  fixedAmount: 0,
                  useFixedAmount: false,
                  rowVersion: a.rowVersion,
                }),
              });
              if (res.ok) {
                const updated = await res.json();
                if (updated.id && updated.rowVersion !== undefined) {
                  assignmentVersionsRef.current.set(updated.id, updated.rowVersion);
                }
                setExpenseAssignments((prev) =>
                  prev.map((x) => (x.id === a.id ? { ...x, ...updated, estimatedHours: 0, fixedAmount: 0, useFixedAmount: false } : x))
                );
              }
            } catch (e) {
              console.warn('Failed to clear orphan expense assignment', a.id, e);
            }
          }
        })();
      }

      // Fetch specializations for this bidding sheet
      try {
        const specsResponse = await fetch(`${API_CONFIG.API_URL}/bidding/${activeSheetId}/specializations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (specsResponse.ok) {
          const specsData = await specsResponse.json();
          const loadedSpecs = specsData.specializations || [];
          // Always apply saved list (including empty — user may have created bid without columns)
          setSpecializations(loadedSpecs);
        }
      } catch (error) {
        console.warn('Failed to load specializations, using defaults:', error);
        // Keep default specializations ['CG', 'Comp', 'Mattepainting']
      }

      try {
        const aiRes = await fetch(`${API_CONFIG.API_URL}/bidding/${activeSheetId}/ai/analyses`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (aiRes.ok) {
          const aiData = await aiRes.json();
          const map: Record<string, any> = {};
          for (const a of aiData.analyses || []) {
            map[a.shotId] = a.tags || {};
          }
          setAiAnalysesByShot(map);
        }
      } catch (error) {
        console.warn('Failed to load AI analyses:', error);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      // Set empty arrays on error to prevent showing stale data
      setShots([]);
      setRevenueAssignments([]);
      setExpenseAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInsertRow = async (targetShotNumber: number, position: 'above' | 'below') => {
    try {
      const token = localStorage.getItem('spellbook_token');
      
      // Calculate new shot number
      const insertPosition = position === 'above' ? targetShotNumber : targetShotNumber + 1;
      
      // Find shots that need to be renumbered (BEFORE they are changed)
      const shotsToRenumber = shots.filter(shot => shot.shotNumber >= insertPosition);
      
      if (shotsToRenumber.length > 0) {
        // Use atomic reorder endpoint to shift shots
        const reorderPayload = shotsToRenumber.map(shot => ({
          id: shot.id,
          shotNumber: shot.shotNumber + 1
        }));
        
        const reorderResponse = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/shots/reorder`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ shots: reorderPayload })
        });
        
        if (!reorderResponse.ok) {
          const errorText = await reorderResponse.text();
          console.error('❌ Reorder failed during insert:', errorText);
          throw new Error('Failed to renumber shots for insert');
        }
      }
      
      // Create new shot
      const response = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/shots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          shotNumber: insertPosition,
          frameIn: 1001,
          frameOut: 1001,
          durationFrames: 0,
          timecodeInSeconds: 0,
          timecodeOutSeconds: 0
        })
      });

      if (response.ok) {
        const newShot = await response.json();
        
        // Refresh all shots from backend to get the correct state
        await fetchData();
      } else {
        const errorData = await response.text();
        console.error('❌ Failed to create shot:', response.status, errorData);
        // Revert on error
        fetchData();
      }
    } catch (error) {
      console.error('❌ Error inserting row:', error);
      fetchData();
    }
  };

  const handleAddRow = async () => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const newShotNumber = shots.length > 0 ? Math.max(...shots.map(s => s.shotNumber)) + 1 : 1;
      
      const response = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/shots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          shotNumber: newShotNumber,
          frameIn: 1001,
          frameOut: 1001
        })
      });

      if (response.ok) {
        const newShot = await response.json();
        setShots([...shots, newShot]);
      }
    } catch (error) {
      console.error('Error adding shot:', error);
    }
  };

  const handleToggleSelectShot = (shotId: string) => {
    const newSelected = new Set(selectedShots);
    if (newSelected.has(shotId)) {
      newSelected.delete(shotId);
    } else {
      newSelected.add(shotId);
    }
    setSelectedShots(newSelected);
  };

  const handleToggleSelectAll = () => {
    if (selectedShots.size === shots.length) {
      setSelectedShots(new Set());
    } else {
      setSelectedShots(new Set(shots.map(s => s.id)));
    }
  };

  const handleBulkApprove = async (specializations: string[], options?: { skipConflicts?: boolean; updateFees?: boolean; updateLiabilities?: boolean; deleteOrphaned?: boolean }) => {
    const token = localStorage.getItem('spellbook_token');
    const response = await fetch(`${API_CONFIG.API_URL}/bidding/expense-assignments/bulk-approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        shotIds: Array.from(selectedShots),
        specializations: specializations.length > 0 ? specializations : undefined,
        skipConflicts: options?.skipConflicts ?? true,
        updateFees: options?.updateFees ?? false,
        updateLiabilities: options?.updateLiabilities ?? options?.updateFees ?? false,
        deleteOrphaned: options?.deleteOrphaned ?? false
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to approve expenses');
    }

    // Refresh assignments for selected shots
    const token2 = localStorage.getItem('spellbook_token');
    for (const shotId of selectedShots) {
      const assignResponse = await fetch(`${API_CONFIG.API_URL}/bidding/shots/${shotId}/assignments`, {
        headers: { 'Authorization': `Bearer ${token2}` }
      });
      
      if (assignResponse.ok) {
        const assignData = await assignResponse.json();
        const eAssignments = assignData.expense || [];
        // Update existing assignments
        setExpenseAssignments(prev => {
          const filtered = prev.filter(a => a.shotId !== shotId);
          return [...filtered, ...eAssignments];
        });
      }
    }
    
    setSelectedShots(new Set());
  };

  const handleDeleteButtonClick = () => {
    if (!isDeleteMode) {
      // Entering delete mode - show checkboxes
      setIsDeleteMode(true);
      setSelectedShots(new Set());
    } else {
      // Confirming deletion
      if (selectedShots.size > 0) {
        setShotsToDelete(Array.from(selectedShots));
        setIsDeleteModalOpen(true);
      }
    }
  };

  const handleDeleteSingleShot = (shotId: string) => {
    setShotsToDelete([shotId]);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('spellbook_token');

      // Delete shots
      await Promise.all(
        shotsToDelete.map(shotId =>
          fetch(`${API_CONFIG.API_URL}/bidding/shots/${shotId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          })
        )
      );

      // Filter out deleted shots
      const remainingShots = shots.filter(s => !shotsToDelete.includes(s.id));
      
      // Renumber shots
      const renumberedShots = remainingShots.map((shot, index) => ({
        ...shot,
        shotNumber: index + 1
      }));
      
      // Update local state
      setShots(renumberedShots);
      setRevenueAssignments(revenueAssignments.filter(a => !shotsToDelete.includes(a.shotId)));
      setExpenseAssignments(expenseAssignments.filter(a => !shotsToDelete.includes(a.shotId)));
      setSelectedShots(new Set());
      setIsDeleteMode(false);
      
      // Add all shots to pendingUpdatesRef to prevent syncDataSilently from interfering
      renumberedShots.forEach(shot => {
        pendingUpdatesRef.current.add(shot.id);
      });
      
      // Two-phase update to avoid unique constraint conflicts:
      // Phase 1: Set all shots to temporary negative numbers
      // Phase 2: Set all shots to their final numbers
      
      const savedShots: Shot[] = [];
      const failedShotIds: string[] = [];
      
      // Phase 1: Set all to temporary negative values
      for (const shot of renumberedShots) {
        try {
          const tempPayload = { shotNumber: -(shot.shotNumber + 1000) }; // Negative temp value
          const response = await fetch(`${API_CONFIG.API_URL}/bidding/shots/${shot.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(tempPayload)
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error(`❌ Phase 1 failed for shot ${shot.id} order:`, response.status, response.statusText, errorText);
            failedShotIds.push(shot.id);
          }
        } catch (err) {
          console.error(`❌ Phase 1 error for shot ${shot.id}:`, err);
          failedShotIds.push(shot.id);
        }
      }
      
      // If Phase 1 failed completely, abort
      if (failedShotIds.length === renumberedShots.length) {
        alert('Ошибка при сохранении порядка. Попробуйте удалить строки снова.');
        // Clear pending updates flags
        renumberedShots.forEach(shot => {
          pendingUpdatesRef.current.delete(shot.id);
        });
        fetchData();
        return;
      }
      
      // Phase 2: Set all to final values
      for (const shot of renumberedShots) {
        if (failedShotIds.includes(shot.id)) continue; // Skip shots that failed in Phase 1
        
        try {
          const payload = { shotNumber: shot.shotNumber };
          const response = await fetch(`${API_CONFIG.API_URL}/bidding/shots/${shot.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          });
          
          if (response.ok) {
            const savedShot = await response.json();
            savedShots.push(savedShot);
          } else {
            const errorText = await response.text();
            console.error(`❌ Phase 2 failed for shot ${shot.id} order:`, response.status, response.statusText, errorText);
            failedShotIds.push(shot.id);
          }
        } catch (err) {
          console.error(`❌ Phase 2 error for shot ${shot.id}:`, err);
          failedShotIds.push(shot.id);
        }
      }
      
      // Clear pending updates flags for ALL shots (both successful and failed)
      renumberedShots.forEach(shot => {
        pendingUpdatesRef.current.delete(shot.id);
      });
      
      if (failedShotIds.length === 0 && savedShots.length === renumberedShots.length) {
        // Update local shots with returned data (including updated rowVersion)
        if (savedShots.length > 0) {
          setShots(prevShots => {
            const updatedShotsMap = new Map(savedShots.map(s => [s.id, s]));
            return prevShots.map(shot => updatedShotsMap.get(shot.id) || shot);
          });
        }
        setLastUpdateTime(Date.now());
      } else if (failedShotIds.length > 0) {
        // Partial failure - refresh data to get correct state
        alert(`⚠️ Частичный успех: ${savedShots.length}/${renumberedShots.length} строк обновлено. Обновляю данные...`);
        fetchData();
      }

      setIsDeleteModalOpen(false);
      setShotsToDelete([]);
    } catch (error) {
      console.error('Error deleting shots:', error);
      fetchData();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDeleteMode = () => {
    setIsDeleteMode(false);
    setSelectedShots(new Set());
  };

  // Drag & Drop handlers - now using dedicated drag handle
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, shotId: string) => {
    setDraggedShotId(shotId);
    e.dataTransfer.effectAllowed = 'move';
    
    // Create a ghost image of the entire row
    const handle = e.currentTarget;
    const row = handle.closest('tr');
    if (row) {
      const ghost = row.cloneNode(true) as HTMLElement;
      ghost.style.position = 'absolute';
      ghost.style.top = '-9999px';
      ghost.style.width = row.offsetWidth + 'px';
      ghost.style.opacity = '0.8';
      ghost.style.backgroundColor = 'rgb(99, 102, 241)';
      document.body.appendChild(ghost);
      
      // Position ghost so it "sticks" to the handle (left side) instead of centering
      const handleRect = handle.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      const offsetX = handleRect.left - rowRect.left + handleRect.width / 2;
      
      e.dataTransfer.setDragImage(ghost, offsetX, 20);
      setTimeout(() => document.body.removeChild(ghost), 0);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>, shotId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverShotId(shotId);

    // Auto-scroll the table container (sticky thead lives inside it)
    const scrollEl = tableScrollRef.current;
    const cursorY = e.clientY;
    let scrollSpeed = 0;

    if (scrollEl) {
      const rect = scrollEl.getBoundingClientRect();
      const scrollThreshold = Math.max(40, rect.height * 0.08);
      if (cursorY < rect.top + scrollThreshold) {
        const distanceFromTop = cursorY - rect.top;
        scrollSpeed = -Math.max(5, ((scrollThreshold - distanceFromTop) / scrollThreshold) * 20);
      } else if (cursorY > rect.bottom - scrollThreshold) {
        const distanceFromBottom = rect.bottom - cursorY;
        scrollSpeed = Math.max(5, ((scrollThreshold - distanceFromBottom) / scrollThreshold) * 20);
      }
    } else {
      const viewportHeight = window.innerHeight;
      const scrollThreshold = viewportHeight * 0.05;
      if (cursorY < scrollThreshold) {
        scrollSpeed = -Math.max(5, ((scrollThreshold - cursorY) / scrollThreshold) * 20);
      } else if (cursorY > viewportHeight - scrollThreshold) {
        const distanceFromBottom = viewportHeight - cursorY;
        scrollSpeed = Math.max(5, ((scrollThreshold - distanceFromBottom) / scrollThreshold) * 20);
      }
    }

    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }

    autoScrollSpeedRef.current = scrollSpeed;

    if (scrollSpeed !== 0) {
      autoScrollIntervalRef.current = setInterval(() => {
        if (autoScrollSpeedRef.current === 0) return;
        const el = tableScrollRef.current;
        if (el) {
          el.scrollTop += autoScrollSpeedRef.current;
        } else {
          window.scrollBy({ top: autoScrollSpeedRef.current, behavior: 'auto' });
        }
      }, 16);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLTableRowElement>, targetShotId: string) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    setDragOverShotId(null);
    
    if (!draggedShotId || draggedShotId === targetShotId) {
      setDraggedShotId(null);
      return;
    }

    const draggedIndex = shots.findIndex(s => s.id === draggedShotId);
    const targetIndex = shots.findIndex(s => s.id === targetShotId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedShotId(null);
      return;
    }

    // Reorder shots array
    const newShots = [...shots];
    const [removed] = newShots.splice(draggedIndex, 1);
    newShots.splice(targetIndex, 0, removed);

    // Update shot numbers
    const updatedShots = newShots.map((shot, index) => ({
      ...shot,
      shotNumber: index + 1
    }));

    // Store local order changes and mark as pending to prevent sync overwrite
    updatedShots.forEach(shot => {
      localShotOrderRef.current.set(shot.id, shot.shotNumber);
      pendingUpdatesRef.current.add(shot.id); // Prevent sync from overwriting during save
    });

    setShots(updatedShots);
    setDraggedShotId(null);

    // Save new order to backend using atomic reorder endpoint
    try {
      const token = localStorage.getItem('spellbook_token');
      
      const response = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/shots/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          shots: updatedShots.map(s => ({ id: s.id, shotNumber: s.shotNumber }))
        })
      });

      // Clear pending updates flag for ALL shots
      updatedShots.forEach(shot => {
        pendingUpdatesRef.current.delete(shot.id);
      });

      if (response.ok) {
        const data = await response.json();
        if (data.shots) {
          setShots(data.shots);
        }
        setLastUpdateTime(Date.now());
      } else {
        const errorText = await response.text();
        console.error('❌ Reorder failed:', response.status, errorText);
        alert('Ошибка при сохранении порядка. Попробуйте снова.');
        fetchData(); // Revert to server state
      }
    } catch (error) {
      console.error('❌ Error updating shot order:', error);
      updatedShots.forEach(shot => {
        pendingUpdatesRef.current.delete(shot.id);
      });
      alert('Ошибка при сохранении порядка. Попробуйте снова.');
      fetchData();
    }
  };

  const handleDragEnd = () => {
    setDraggedShotId(null);
    setDragOverShotId(null);
    
    // Stop auto-scroll
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
    autoScrollSpeedRef.current = 0;
  };

  // Drag & Drop handlers for artist assignment badges
  const handleBadgeDragStart = (e: React.DragEvent<HTMLDivElement>, shotId: string, spec: string, artistId: string) => {
    e.stopPropagation(); // Prevent row drag
    setDraggedAssignment({ shotId, spec, artistId });
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleBadgeDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleBadgeDrop = async (e: React.DragEvent<HTMLDivElement>, targetShotId: string, targetSpec: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedAssignment) return;

    const { artistId } = draggedAssignment;

    // Don't do anything if dropping on the same cell
    if (draggedAssignment.shotId === targetShotId && draggedAssignment.spec === targetSpec) {
      setDraggedAssignment(null);
      return;
    }

    // Copy the assignment to the target cell
    await handleUpdateAssignment(targetShotId, targetSpec, 'artistId', artistId);
    
    setDraggedAssignment(null);
  };

  const handleBadgeDragEnd = () => {
    setDraggedAssignment(null);
  };

  const handleNameDoubleClick = () => {
    setIsEditingName(true);
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      const updatedSheet = await response.json().catch(() => ({}));
      if (handleBiddingMutationPayload(updatedSheet, navigate, biddingSheetId)) return;
      
      if (response.ok) {
        setCurrentBiddingSheet(prev => ({ ...prev, status: updatedSheet.status, approvedBy: updatedSheet.approvedBy, approvedAt: updatedSheet.approvedAt }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleNameSave = async () => {
    const trimmedName = editedName.trim();
    
    if (!trimmedName) {
      setIsEditingName(false);
      setEditedName(biddingSheet.name);
      return;
    }

    // Check for duplicate name
    const duplicate = allBiddingSheets.find(
      b => b.id !== biddingSheetId && b.name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (duplicate) {
      alert(t('bidding.duplicateNameError') || 'A bidding sheet with this name already exists');
      setEditedName(biddingSheet.name);
      setIsEditingName(false);
      return;
    }

    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: trimmedName })
      });

      if (response.ok) {
        biddingSheet.name = trimmedName;
      } else {
        setEditedName(biddingSheet.name);
      }
    } catch (error) {
      console.error('Error updating bidding name:', error);
      setEditedName(biddingSheet.name);
    }

    setIsEditingName(false);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      setEditedName(biddingSheet.name);
      setIsEditingName(false);
    }
  };

  const handleUpdateShot = async (shotId: string, field: string, value: any) => {
    try {
      const token = localStorage.getItem('spellbook_token');
      
      // Calculate duration if frameIn or frameOut changes
      const shot = shots.find(s => s.id === shotId);
      let updateData: any = { [field]: value };
      
      if (shot && (field === 'frameIn' || field === 'frameOut')) {
        const frameIn = field === 'frameIn' ? Number(value) : (shot.frameIn || 1001);
        const frameOut = field === 'frameOut' ? Number(value) : (shot.frameOut || 1001);
        const durationFrames = Math.max(0, frameOut - frameIn + 1);
        updateData.durationFrames = durationFrames;
      }
      
      const response = await fetch(`${API_CONFIG.API_URL}/bidding/shots/${shotId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      const payload = await response.json().catch(() => ({}));
      if (handleBiddingMutationPayload(payload, navigate, biddingSheetId)) return;

      if (response.ok) {
        setShots(shots.map(s => s.id === shotId ? { ...s, ...updateData } : s));
      }
    } catch (error) {
      console.error('Error updating shot:', error);
    }
  };

  // Helper function to get rate for artist-specialization combination
  const getRateForArtistSpec = async (artistId: string, specialization: string): Promise<number> => {
    if (!projectId) return 0;
    
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/projects/${projectId}/rates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const rates = data.rates || [];
        const rate = rates.find((r: any) => r.artistId === artistId && r.specialization === specialization);
        if (rate) return rate.hourlyRate;
      }
    } catch (error) {
      console.error('Error fetching rate:', error);
    }
    
    // Fallback to artist's base rate
    const artist = artists.find(a => a.id === artistId);
    if (artist) return artist.hourlyRate || 0;
    
    return 0;
  };

  // Update all assignments with new hourlyRate after rates are saved
  const handleRatesSaved = async () => {
    if (!projectId) return;
    
    try {
      const token = localStorage.getItem('spellbook_token');
      
      // Fetch updated rates
      const response = await fetch(`${API_CONFIG.API_URL}/projects/${projectId}/rates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const rates = data.rates || [];
        
        // Create a map for quick lookup: ratesMap[artistId][specialization] = hourlyRate
        const ratesMap: Record<string, Record<string, number>> = {};
        rates.forEach((r: any) => {
          if (!ratesMap[r.artistId]) {
            ratesMap[r.artistId] = {};
          }
          ratesMap[r.artistId][r.specialization] = r.hourlyRate;
        });
        
        // Update Revenue Assignments
        const updatedRevenue = await Promise.all(
          revenueAssignments.map(async (assignment) => {
            let updated = false;
            const version =
              (assignment.id ? assignmentVersionsRef.current.get(assignment.id) : undefined) ??
              assignment.rowVersion;
            const updatePayload: any = { rowVersion: version };

            // Virtual rows often have artistId null on server — still update their rate
            const isVirtual =
              !!(assignment.isVirtualArtist && assignment.virtualArtistType) ||
              !!(assignment.artistId && String(assignment.artistId).startsWith('virtual-'));

            if (assignment.specialization && (isVirtual || assignment.artistId)) {
              let newRate = 0;
              if (isVirtual) {
                const virtualId = assignment.virtualArtistType
                  ? `virtual-${assignment.virtualArtistType}`
                  : String(assignment.artistId);
                newRate = await getRateForArtistSpec(virtualId, assignment.specialization);
              } else if (assignment.artistId) {
                newRate =
                  ratesMap[assignment.artistId]?.[assignment.specialization] ||
                  artists.find((a) => a.id === assignment.artistId)?.hourlyRate ||
                  0;
              }

              if (assignment.hourlyRate !== newRate) {
                updatePayload.hourlyRate = newRate;
                updated = true;
              }
            }

            if (updated && assignment.id) {
              try {
                const putOnce = async (payload: any) =>
                  fetch(`${API_CONFIG.API_URL}/bidding/assignments/${assignment.id}`, {
                    method: 'PUT',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                  });

                let updateResponse = await putOnce(updatePayload);

                if (updateResponse.status === 409) {
                  const conflict = await updateResponse.json().catch(() => ({}));
                  const serverVersion = conflict.currentData?.rowVersion;
                  if (serverVersion !== undefined) {
                    rememberAssignmentVersion({ id: assignment.id, rowVersion: serverVersion });
                    updateResponse = await putOnce({ ...updatePayload, rowVersion: serverVersion });
                  }
                }

                if (updateResponse.ok) {
                  const updatedAssignment = normalizeRevenueAssignment(await updateResponse.json());
                  rememberAssignmentVersion(updatedAssignment);
                  return updatedAssignment;
                }
              } catch (error) {
                console.error(`Error updating assignment ${assignment.id}:`, error);
              }
            }
            return normalizeRevenueAssignment(assignment);
          })
        );
        setRevenueAssignments(updatedRevenue.map(normalizeRevenueAssignment));

        // Update Expense Assignments
        const updatedExpense = await Promise.all(
          expenseAssignments.map(async (assignment) => {
            let updated = false;
            const version =
              (assignment.id ? assignmentVersionsRef.current.get(assignment.id) : undefined) ??
              assignment.rowVersion;
            const updatePayload: any = { rowVersion: version };

            // Check and update expense rate (artistId is REAL artist here)
            if (assignment.artistId && assignment.specialization) {
              const newRate =
                ratesMap[assignment.artistId]?.[assignment.specialization] ||
                artists.find((a) => a.id === assignment.artistId)?.hourlyRate ||
                0;

              if (assignment.hourlyRate !== newRate) {
                updatePayload.hourlyRate = newRate;
                updated = true;
              }
            }

            if (updated && assignment.id) {
              try {
                const putOnce = async (payload: any) =>
                  fetch(`${API_CONFIG.API_URL}/bidding/expense-assignments/${assignment.id}`, {
                    method: 'PUT',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                  });

                let updateResponse = await putOnce(updatePayload);

                if (updateResponse.status === 409) {
                  const conflict = await updateResponse.json().catch(() => ({}));
                  const serverVersion = conflict.currentData?.rowVersion;
                  if (serverVersion !== undefined) {
                    rememberAssignmentVersion({ id: assignment.id, rowVersion: serverVersion });
                    updateResponse = await putOnce({ ...updatePayload, rowVersion: serverVersion });
                  }
                }

                if (updateResponse.ok) {
                  const updatedAssignment = await updateResponse.json();
                  rememberAssignmentVersion(updatedAssignment);
                  return updatedAssignment;
                }
              } catch (error) {
                console.error(`Error updating expense assignment ${assignment.id}:`, error);
              }
            }
            return assignment;
          })
        );
        setExpenseAssignments(updatedExpense);

        setLastUpdateTime(Date.now());
      }
    } catch (error) {
      console.error('Error refreshing assignments after rates save:', error);
    }
  };

  // TODO: Task/Shot approval status will be fetched from MagicBox API in future
  // const handleApproveAssignment - removed, approval handled via MagicBox integration

  const handleUpdateAssignment = async (
    shotId: string,
    specialization: string,
    field: 'artistId' | 'realArtistId' | 'estimatedHours' | 'realEstimatedHours' | 'hourlyRate' | 'realHourlyRate' | 'fixedAmount' | 'useFixedAmount' | 'isVirtualArtist' | 'virtualArtistType',
    value: string | number | boolean | null,
    options?: { immediate?: boolean }
  ) => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const isRevenue = activeTable === 'revenue';
      
      // Normalize field names based on active table
      let targetField: string = field;
      if (!isRevenue) {
          if (field === 'realArtistId') targetField = 'artistId';
          if (field === 'realEstimatedHours') targetField = 'estimatedHours';
          if (field === 'realHourlyRate') targetField = 'hourlyRate';
      }

      // Find existing assignment in correct state
      const currentList = (isRevenue ? revenueAssignments : expenseAssignments) as (ShotAssignment | ExpenseAssignment)[];
      const existingAssignment = currentList.find(
        (a: ShotAssignment | ExpenseAssignment) => a.shotId === shotId && a.specialization === specialization
      );
      
      // Track local edit
      if (existingAssignment?.id) {
        const editKey = `${existingAssignment.id}:${field}`; // Keep original field name for UI key compatibility if needed
        localEditsRef.current.set(editKey, Date.now());
      }

      if (existingAssignment) {
        // For artistId - keep assignment even if unassigned (set to null)
        if (targetField === 'artistId' && !value) {
          if (isRevenue) {
            setRevenueAssignments(prev => prev.map(a =>
              a.id === existingAssignment.id ? { 
                ...a, 
                artistId: null, 
                hourlyRate: 0,
                estimatedHours: 0,
                fixedAmount: 0,
                useFixedAmount: false,
                isVirtualArtist: false,
                virtualArtistType: null,
                artistLevel: undefined
              } : a
            ));
          } else {
            setExpenseAssignments(prev => prev.map(a =>
              a.id === existingAssignment.id ? {
                ...a,
                artistId: null,
                hourlyRate: 0,
                estimatedHours: 0,
                fixedAmount: 0,
                useFixedAmount: false,
              } : a
            ));
          }
          
          // Send update to server with all virtual fields cleared
          const clearData: any = { artistId: null, hourlyRate: 0 };
          if (isRevenue) {
            clearData.isVirtualArtist = false;
            clearData.virtualArtistType = null;
          }
          
          await performAssignmentUpdate(existingAssignment, targetField, null, shotId, specialization);
          return;
        }

        // For estimatedHours - debounce (unless immediate paste/bulk)
        if (targetField === 'estimatedHours') {
           // ... logic similar to original but specific to table
          const changeKey = `${shotId}:${specialization}`;
           // Update local state immediately
           if (isRevenue) {
               setRevenueAssignments(prev => prev.map(a => a.id === existingAssignment.id ? { ...a, estimatedHours: value as number } : a));
           } else {
               setExpenseAssignments(prev => prev.map(a => a.id === existingAssignment.id ? { ...a, estimatedHours: value as number } : a));
           }

           const timeoutKey = `${shotId}:${specialization}:${isRevenue ? 'rev' : 'exp'}`;
          if (estimatedHoursTimeoutsRef.current.has(timeoutKey)) {
            clearTimeout(estimatedHoursTimeoutsRef.current.get(timeoutKey)!);
            estimatedHoursTimeoutsRef.current.delete(timeoutKey);
          }

          if (options?.immediate) {
            const roundedValue = Math.round((value as number) * 10) / 10;
            await performAssignmentUpdate(existingAssignment, targetField, roundedValue, shotId, specialization);
            return;
          }

          const timeoutId = setTimeout(async () => {
            const roundedValue = Math.round((value as number) * 10) / 10;
               await performAssignmentUpdate(existingAssignment, targetField, roundedValue, shotId, specialization);
            estimatedHoursTimeoutsRef.current.delete(timeoutKey);
           }, 1000);
          estimatedHoursTimeoutsRef.current.set(timeoutKey, timeoutId);
          return;
        }

        // For other fields (artistId, fixedAmount, virtual flags) - update immediately
        // Pre-fetch rate
        let rateToSet = 0;
        if ((targetField === 'artistId' || targetField === 'virtualArtistType') && value) {
             const artistKey = targetField === 'virtualArtistType' ? `virtual-${value}` : String(value);
             rateToSet = await getRateForArtistSpec(artistKey, specialization);
        }

        // Optimistic Update
        if (isRevenue) {
            setRevenueAssignments(prev => prev.map(a => {
            if (a.id === existingAssignment.id) {
              const updated: any = { ...a };
                    updated[targetField] = value;
                    
                    // Handle virtual/real logic for Revenue
                    if (targetField === 'artistId') {
                        // Check if it's a virtual artist selection
                        if (typeof value === 'string' && value.startsWith('virtual-')) {
                          const vType = value.replace('virtual-', '');
                          updated.artistId = value; // Keep virtual-* ID for UI
                          updated.isVirtualArtist = true;
                          updated.virtualArtistType = vType;
                          updated.artistLevel = vType.charAt(0).toUpperCase() + vType.slice(1);
                        } else if (value) {
                          // Real artist selected
                          updated.isVirtualArtist = false;
                          updated.virtualArtistType = null;
                          updated.artistLevel = null;
                        } else {
                          // Unassigned
                          updated.isVirtualArtist = false;
                          updated.virtualArtistType = null;
                          updated.artistLevel = null;
                        }
                        updated.hourlyRate = rateToSet;
                        // Reset value when executor changes (mid/junior/lead or clear)
                        updated.estimatedHours = 0;
                        updated.fixedAmount = 0;
                        updated.useFixedAmount = false;
                    } else if (targetField === 'virtualArtistType') {
                if (value) {
                            updated.isVirtualArtist = true;
                            updated.hourlyRate = rateToSet;
                        } else {
                  updated.isVirtualArtist = false;
                            updated.hourlyRate = 0;
                        }
                        updated.estimatedHours = 0;
                        updated.fixedAmount = 0;
                        updated.useFixedAmount = false;
                    }
                    return updated;
                }
                return a;
            }));
                } else {
            // Expense
            setExpenseAssignments(prev => prev.map(a => {
                if (a.id === existingAssignment.id) {
                    const updated: any = { ...a };
                    updated[targetField] = value;
                    if (targetField === 'artistId') {
                        updated.hourlyRate = rateToSet;
                        updated.estimatedHours = 0;
                        updated.fixedAmount = 0;
                        updated.useFixedAmount = false;
              }
              return updated;
            }
            return a;
          }));
        }
        
        await performAssignmentUpdate(existingAssignment, targetField, value, shotId, specialization);
        return;
      }
      
      // CREATE NEW ASSIGNMENT
      if (!existingAssignment && value) {
          const assignmentData: any = {
              shotId,
          specialization,
              estimatedHours: 0,
          hourlyRate: 0
        };
        
          // Rate fetch
          let rate = 0;
          if (targetField === 'virtualArtistType') {
              rate = await getRateForArtistSpec(`virtual-${value}`, specialization);
          assignmentData.virtualArtistType = value;
              assignmentData.isVirtualArtist = true;
          } else if (targetField === 'artistId' || field === 'realArtistId') {
              // Check if it's a virtual artist selection
              if (typeof value === 'string' && value.startsWith('virtual-')) {
                const vType = value.replace('virtual-', '');
                rate = await getRateForArtistSpec(String(value), specialization);
                assignmentData.artistId = null; // Backend expects null for virtual
                assignmentData.isVirtualArtist = true;
                assignmentData.virtualArtistType = vType;
                assignmentData.artistLevel = vType.charAt(0).toUpperCase() + vType.slice(1);
              } else {
                rate = await getRateForArtistSpec(String(value), specialization);
                assignmentData.artistId = value;
                if (isRevenue) assignmentData.isVirtualArtist = false;
              }
          }
          assignmentData.hourlyRate = rate;
        
        const tempId = `temp-${Date.now()}`;
          // For UI, keep virtual-* ID so dropdown shows correct selection
          const tempAssignment = { 
            ...assignmentData, 
            id: tempId,
            artistId: (typeof value === 'string' && value.startsWith('virtual-')) ? value : assignmentData.artistId
          };
          
          // Optimistic Add
          if (isRevenue) {
              setRevenueAssignments(prev => [...prev, tempAssignment]);
          } else {
              setExpenseAssignments(prev => [...prev, tempAssignment]);
          }
        pendingUpdatesRef.current.add(tempId);
        setIsPendingUpdate(true);
        
          // API Call
          const endpoint = isRevenue 
            ? `${API_CONFIG.API_URL}/bidding/shots/${shotId}/assignments`
            : `${API_CONFIG.API_URL}/bidding/shots/${shotId}/expense-assignments`;
            
          const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(assignmentData)
        });

        if (response.ok) {
          const newAssignment = await response.json();
          pendingUpdatesRef.current.delete(tempId);
          if (newAssignment.id) pendingUpdatesRef.current.add(newAssignment.id);

          const normalized = isRevenue
            ? normalizeRevenueAssignment(newAssignment)
            : newAssignment;
          rememberAssignmentVersion(normalized);

          const updateState = isRevenue ? setRevenueAssignments : setExpenseAssignments;
          updateState((prev: any[]) => prev.map(a => (a.id === tempId ? normalized : a)));

          setTimeout(() => {
            if (newAssignment.id) pendingUpdatesRef.current.delete(newAssignment.id);
            setIsPendingUpdate(false);
          }, 1000);
        } else {
              // Revert
              const updateState = isRevenue ? setRevenueAssignments : setExpenseAssignments;
              updateState((prev: any[]) => prev.filter(a => a.id !== tempId));
          pendingUpdatesRef.current.delete(tempId);
            setIsPendingUpdate(false);
        }
      }
    } catch (error) {
      console.error('Error updating assignment:', error);
    }
  };
  
  const performAssignmentUpdate = async (
    existingAssignment: ShotAssignment | ExpenseAssignment,
    field: string,
    value: any,
    shotId: string,
    specialization: string
  ) => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const isRevenue = activeTable === 'revenue';
      
      // Map field name for Expense API if needed (UI uses 'realArtistId' sometimes)
      let apiField = field;
      if (!isRevenue) {
          if (field === 'realArtistId') apiField = 'artistId';
          if (field === 'realEstimatedHours') apiField = 'estimatedHours';
          if (field === 'realHourlyRate') apiField = 'hourlyRate';
      }
      
      const updateData: any = { [apiField]: value };
      
      // Handle Virtual Artist Selection (Update)
      if (apiField === 'artistId' && typeof value === 'string' && value.startsWith('virtual-')) {
         const vArtist = VIRTUAL_ARTISTS.find(v => v.id === value);
         if (vArtist) {
           updateData.artistId = null;
           updateData.isVirtualArtist = true;
           updateData.virtualArtistType = vArtist.type;
           updateData.artistLevel = vArtist.level;
         }
      }
      
      // Special logic for rates/virtual flags
      if (apiField === 'artistId' || apiField === 'virtualArtistType') {
          if (value) {
              const lookupId = (typeof value === 'string' && value.startsWith('virtual-')) ? value : 
                               (apiField === 'virtualArtistType' ? `virtual-${value}` : String(value));
              
              const rate = await getRateForArtistSpec(lookupId, specialization);
              updateData.hourlyRate = rate;
            
              if (isRevenue && apiField === 'artistId' && !String(value).startsWith('virtual-')) {
                  updateData.isVirtualArtist = true; // Legacy fallback? Or maybe if selecting real artist in revenue?
                  // Actually if selecting real artist in revenue, we might treat it as virtual override or just assigned?
                  // For now, if it's real artist ID, we keep isVirtualArtist=true as per previous logic?
                  // Previous logic: if (isRevenue && apiField === 'artistId') updateData.isVirtualArtist = true;
                  // We should probably respect if it's a real artist or not.
                  // But let's keep it simple: if it's NOT virtual-*, we assume it's a real artist ID, 
                  // but if we are in Revenue, we might still flag isVirtualArtist if that was the intention.
                  // However, with new Virtual options, we are explicit.
              }
          } else {
              // Unassigning - clear all virtual fields
              updateData.hourlyRate = 0;
              if (isRevenue) {
                updateData.isVirtualArtist = false;
                updateData.virtualArtistType = null;
              }
          }
          // Always zero hours/amount when executor changes or is cleared
          updateData.estimatedHours = 0;
          updateData.fixedAmount = 0;
          updateData.useFixedAmount = false;
      }
      
      // Row Version
      const currentVersion = existingAssignment.id ? (assignmentVersionsRef.current.get(existingAssignment.id) ?? existingAssignment.rowVersion) : existingAssignment.rowVersion;
      if (currentVersion !== undefined) updateData.rowVersion = currentVersion;
      
      if (existingAssignment.id) pendingUpdatesRef.current.add(existingAssignment.id);
        setIsPendingUpdate(true);
      
      // Optimistic Update
      const optimisticAssignment = { ...existingAssignment };
      
      // Handle virtual artist selection for optimistic update
      if (apiField === 'artistId' && typeof value === 'string' && value.startsWith('virtual-')) {
         const vArtist = VIRTUAL_ARTISTS.find(v => v.id === value);
         if (vArtist) {
           // For optimistic update, keep the virtual-* ID so UI shows correct selection
           (optimisticAssignment as any).artistId = value; // Keep virtual-* ID for UI
           (optimisticAssignment as any).isVirtualArtist = true;
           (optimisticAssignment as any).virtualArtistType = vArtist.type;
           (optimisticAssignment as any).artistLevel = vArtist.level;
         }
      } else {
         (optimisticAssignment as any)[apiField] = value;
      }
      
      if (updateData.hourlyRate !== undefined) {
          (optimisticAssignment as any).hourlyRate = updateData.hourlyRate;
      }
      if (updateData.isVirtualArtist !== undefined && !(typeof value === 'string' && value.startsWith('virtual-'))) {
          (optimisticAssignment as any).isVirtualArtist = updateData.isVirtualArtist;
      }
      if (updateData.estimatedHours !== undefined) {
          (optimisticAssignment as any).estimatedHours = updateData.estimatedHours;
      }
      if (updateData.fixedAmount !== undefined) {
          (optimisticAssignment as any).fixedAmount = updateData.fixedAmount;
      }
      if (updateData.useFixedAmount !== undefined) {
          (optimisticAssignment as any).useFixedAmount = updateData.useFixedAmount;
      }
      if (updateData.virtualArtistType !== undefined && !(typeof value === 'string' && value.startsWith('virtual-'))) {
          (optimisticAssignment as any).virtualArtistType = updateData.virtualArtistType;
      }
      
      if (isRevenue) {
          setRevenueAssignments(prev => prev.map(a => a.id === existingAssignment.id ? optimisticAssignment as ShotAssignment : a));
      } else {
          setExpenseAssignments(prev => prev.map(a => a.id === existingAssignment.id ? optimisticAssignment as ExpenseAssignment : a));
      }
      
      const endpoint = isRevenue
        ? `${API_CONFIG.API_URL}/bidding/assignments/${existingAssignment.id}`
        : `${API_CONFIG.API_URL}/bidding/expense-assignments/${existingAssignment.id}`;
        
      const putOnce = async (payload: any) =>
        fetch(endpoint, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

      let response = await putOnce(updateData);

      if (response.status === 409) {
        const conflict = await response.json().catch(() => ({}));
        if (handleBiddingMutationPayload(conflict, navigate, biddingSheetId)) {
          if (existingAssignment.id) {
            pendingUpdatesRef.current.delete(existingAssignment.id);
            if (pendingUpdatesRef.current.size === 0) setIsPendingUpdate(false);
          }
          return;
        }
        const serverVersion = conflict.currentData?.rowVersion;
        if (serverVersion !== undefined && existingAssignment.id) {
          rememberAssignmentVersion({ id: existingAssignment.id, rowVersion: serverVersion });
          response = await putOnce({ ...updateData, rowVersion: serverVersion });
        }
      }

      if (existingAssignment.id) {
        pendingUpdatesRef.current.delete(existingAssignment.id);
        if (pendingUpdatesRef.current.size === 0) setIsPendingUpdate(false);
      }
      setLastUpdateTime(Date.now());

      if (response.ok) {
        const updated = await response.json();
        if (handleBiddingMutationPayload(updated, navigate, biddingSheetId)) return;
        const normalized = isRevenue ? normalizeRevenueAssignment(updated) : updated;

        if (isRevenue) {
          setRevenueAssignments((prev) =>
            prev.map((a) => (a.id === existingAssignment.id ? normalized : a))
          );
        } else {
          setExpenseAssignments((prev) =>
            prev.map((a) => (a.id === existingAssignment.id ? (normalized as ExpenseAssignment) : a))
          );
        }

        rememberAssignmentVersion(normalized);
      } else {
        // Revert Optimistic Update
        if (isRevenue) {
          setRevenueAssignments((prev) =>
            prev.map((a) =>
              a.id === existingAssignment.id ? (existingAssignment as ShotAssignment) : a
            )
          );
        } else {
          setExpenseAssignments((prev) =>
            prev.map((a) =>
              a.id === existingAssignment.id ? (existingAssignment as ExpenseAssignment) : a
            )
          );
        }
        console.error('Update failed', response.status);
      }
      
    } catch (error) {
      console.error('Error updating assignment:', error);
    }
  };

  const performAssignmentFieldsUpdate = async (
    existingAssignment: ShotAssignment | ExpenseAssignment,
    fields: Record<string, string | number | boolean | null>,
    shotId: string,
    specialization: string
  ) => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const isRevenue = activeTable === 'revenue';
      const updateData: Record<string, string | number | boolean | null> = { ...fields };

      if (existingAssignment.id) {
        Object.keys(fields).forEach((field) => {
          localEditsRef.current.set(`${existingAssignment.id}:${field}`, Date.now());
        });
      }

      const currentVersion = existingAssignment.id
        ? (assignmentVersionsRef.current.get(existingAssignment.id) ?? existingAssignment.rowVersion)
        : existingAssignment.rowVersion;
      if (currentVersion !== undefined) updateData.rowVersion = currentVersion;

      if (existingAssignment.id) pendingUpdatesRef.current.add(existingAssignment.id);
      setIsPendingUpdate(true);

      const optimisticAssignment = { ...existingAssignment, ...fields };

      if (isRevenue) {
        setRevenueAssignments(prev =>
          prev.map(a => (a.id === existingAssignment.id ? optimisticAssignment as ShotAssignment : a))
        );
      } else {
        setExpenseAssignments(prev =>
          prev.map(a => (a.id === existingAssignment.id ? optimisticAssignment as ExpenseAssignment : a))
        );
      }

      const endpoint = isRevenue
        ? `${API_CONFIG.API_URL}/bidding/assignments/${existingAssignment.id}`
        : `${API_CONFIG.API_URL}/bidding/expense-assignments/${existingAssignment.id}`;

      const putOnce = async (payload: Record<string, string | number | boolean | null>) =>
        fetch(endpoint, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

      let response = await putOnce(updateData);

      if (response.status === 409 && existingAssignment.id) {
        const conflict = await response.json().catch(() => ({}));
        const serverVersion = conflict.currentData?.rowVersion;
        if (serverVersion !== undefined) {
          rememberAssignmentVersion({ id: existingAssignment.id, rowVersion: serverVersion });
          response = await putOnce({ ...updateData, rowVersion: serverVersion });
        }
      }

      if (existingAssignment.id) {
        pendingUpdatesRef.current.delete(existingAssignment.id);
        if (pendingUpdatesRef.current.size === 0) setIsPendingUpdate(false);
      }
      setLastUpdateTime(Date.now());

      if (response.ok) {
        const updated = await response.json();
        const normalized = isRevenue ? normalizeRevenueAssignment(updated) : updated;
        if (isRevenue) {
          setRevenueAssignments((prev) =>
            prev.map((a) => (a.id === existingAssignment.id ? normalized : a))
          );
        } else {
          setExpenseAssignments((prev) =>
            prev.map((a) => (a.id === existingAssignment.id ? (normalized as ExpenseAssignment) : a))
          );
        }
        rememberAssignmentVersion(normalized);
      } else {
        if (isRevenue) {
          setRevenueAssignments((prev) =>
            prev.map((a) =>
              a.id === existingAssignment.id ? (existingAssignment as ShotAssignment) : a
            )
          );
        } else {
          setExpenseAssignments((prev) =>
            prev.map((a) =>
              a.id === existingAssignment.id ? (existingAssignment as ExpenseAssignment) : a
            )
          );
        }
        console.error('Batch assignment update failed', response.status);
      }
    } catch (error) {
      console.error('Error batch updating assignment:', error);
    }
  };

  const resolvePasteField = (
    assignment: ShotAssignment | ExpenseAssignment,
    columnMode: BiddingValueColumnMode,
    isRevenue: boolean
  ): 'estimatedHours' | 'realEstimatedHours' | 'fixedAmount' => {
    if (columnMode === 'sum') return 'fixedAmount';
    return isRevenue ? 'estimatedHours' : 'realEstimatedHours';
  };

  const isValueCellEditable = (
    assignment: ShotAssignment | ExpenseAssignment | undefined,
    isRevenue: boolean
  ): boolean => {
    if (valueColumnMode === 'sum') return true;
    if (!assignment?.id) return false;
    if (isRevenue) {
      const a = assignment as ShotAssignment;
      return !!(a.artistId || a.isVirtualArtist);
    }
    return !!assignment.artistId;
  };

  const handleValueColumnPaste = async (
    e: React.ClipboardEvent<HTMLInputElement>,
    anchorShotId: string,
    spec: string
  ) => {
    e.preventDefault();
    const values = parseColumnPaste(e.clipboardData.getData('text/plain'));
    if (values.length === 0) return;

    const anchorIndex = shots.findIndex((s) => s.id === anchorShotId);
    if (anchorIndex === -1) return;

    const isRevenue = activeTable === 'revenue';

    let applied = 0;
    for (let i = 0; i < values.length; i++) {
      const targetShot = shots[anchorIndex + i];
      if (!targetShot) break;

      const assignment = isRevenue
        ? revenueAssignments.find(
            (a) => a.shotId === targetShot.id && a.specialization === spec
          )
        : expenseAssignments.find(
            (a) => a.shotId === targetShot.id && a.specialization === spec
          );
      if (!isValueCellEditable(assignment, isRevenue)) continue;

      const field = resolvePasteField(assignment!, valueColumnMode, isRevenue);
      let pasteValue = values[i];
      if (field === 'fixedAmount') {
        pasteValue = Math.round(pasteValue);
      } else if (field === 'estimatedHours' || field === 'realEstimatedHours') {
        pasteValue = Math.round(pasteValue * 10) / 10;
      } else {
        pasteValue = Math.round(pasteValue);
      }

      const needsImmediate = field === 'estimatedHours' || field === 'realEstimatedHours';
      if (field === 'fixedAmount') {
        await handleFixedAmountInput(targetShot.id, spec, assignment as ShotAssignment, pasteValue);
      } else {
        await handleUpdateAssignment(
          targetShot.id,
          spec,
          field,
          pasteValue,
          needsImmediate ? { immediate: true } : undefined
        );
      }
      applied++;
    }

    if (applied < values.length) {
      console.info(`[paste] Applied ${applied} of ${values.length} values`);
    }
  };

  const handleCopyRowValues = (shotId: string) => {
    const isRevenue = activeTable === 'revenue';
    const values: Record<string, number> = {};
    for (const spec of specializations) {
      const assignment = isRevenue
        ? revenueAssignments.find((a) => a.shotId === shotId && a.specialization === spec)
        : expenseAssignments.find((a) => a.shotId === shotId && a.specialization === spec);
      if (valueColumnMode === 'sum') {
        values[spec] = Number(assignment?.fixedAmount) || 0;
      } else {
        values[spec] = Number(assignment?.estimatedHours) || 0;
      }
    }
    setRowValueClipboard({ mode: valueColumnMode, values });
  };

  const handlePasteRowValues = async (shotId: string) => {
    if (!rowValueClipboard || rowValueClipboard.mode !== valueColumnMode) return;

    const isRevenue = activeTable === 'revenue';
    for (const [spec, raw] of Object.entries(rowValueClipboard.values)) {
      if (!specializations.includes(spec)) continue;

      const assignment = isRevenue
        ? revenueAssignments.find((a) => a.shotId === shotId && a.specialization === spec)
        : expenseAssignments.find((a) => a.shotId === shotId && a.specialization === spec);

      if (valueColumnMode === 'sum') {
        const pasteValue = Math.round(Number(raw) || 0);
        await handleFixedAmountInput(
          shotId,
          spec,
          assignment as ShotAssignment | undefined,
          pasteValue
        );
      } else {
        if (!isValueCellEditable(assignment, isRevenue)) continue;
        const pasteValue = Math.round((Number(raw) || 0) * 10) / 10;
        const field = isRevenue ? 'estimatedHours' : 'realEstimatedHours';
        await handleUpdateAssignment(shotId, spec, field, pasteValue, { immediate: true });
      }
    }
  };

  const canPasteRowValues =
    !!rowValueClipboard &&
    rowValueClipboard.mode === valueColumnMode &&
    Object.keys(rowValueClipboard.values).length > 0;

  // Helper function to check if revenue and expense assignments are linked
  const getCellLinkStatus = (shotId: string, specialization: string): 'linked' | 'missing-revenue' | 'missing-expense' | 'none' => {
    const revenueAssignment = revenueAssignments.find(a => a.shotId === shotId && a.specialization === specialization);
    const expenseAssignment = expenseAssignments.find(a => a.shotId === shotId && a.specialization === specialization);
    
    // Check if revenue has a real assignment (virtual artist with type, or real artistId that's not a virtual-* prefix)
    const hasRevenueAssignment = revenueAssignment && (
      (revenueAssignment.isVirtualArtist && revenueAssignment.virtualArtistType) || 
      (revenueAssignment.artistId && !String(revenueAssignment.artistId).startsWith('virtual-'))
    );
    
    // Check if expense has a real artist assigned (not null, not empty)
    const hasExpenseAssignment = expenseAssignment && expenseAssignment.artistId && expenseAssignment.artistId !== '';
    
    if (hasRevenueAssignment && hasExpenseAssignment) {
      return 'linked'; // Both sides have assignments - linked (yellow highlight)
    } else if (hasRevenueAssignment && !hasExpenseAssignment) {
      return 'missing-expense'; // Revenue has assignment but expense doesn't (orange highlight)
    } else if (!hasRevenueAssignment && hasExpenseAssignment) {
      return 'missing-revenue'; // Expense has assignment but revenue doesn't (orange highlight)
    }
    
    return 'none'; // No assignments on either side (both "не назначено")
  };

  const getAssignment = (shotId: string, specialization: string) => {
    return assignments.find(a => a.shotId === shotId && a.specialization === specialization);
  };

  const getRelevantAssignments = (): ShotAssignment[] => {
    if (activeTable === 'expense') {
      return assignments.filter(a => !a.isVirtualArtist);
    }
    return assignments;
  };

  const assignmentHasExecutor = (assignment: ShotAssignment): boolean => {
    if (activeTable === 'expense') {
      return !!(assignment.artistId || assignment.realArtistId);
    }
    // Same rule as orphan-cleanup: any artistId or virtual flag keeps values in totals
    return !!(
      assignment.artistId ||
      assignment.isVirtualArtist ||
      assignment.virtualArtistType
    );
  };

  const getAssignmentAmount = (assignment: ShotAssignment): number => {
    if (!assignmentHasExecutor(assignment)) return 0;
    if (valueColumnMode === 'sum') {
      return Number(assignment.fixedAmount) || 0;
    }
    if (activeTable === 'expense') {
      const hours = Number(assignment.realEstimatedHours ?? assignment.estimatedHours) || 0;
      const rate = Number(assignment.realHourlyRate ?? assignment.hourlyRate) || 0;
      return hours * rate;
    }
    return (Number(assignment.estimatedHours) || 0) * (Number(assignment.hourlyRate) || 0);
  };

  const getAssignmentHours = (assignment: ShotAssignment): number => {
    if (!assignmentHasExecutor(assignment)) return 0;
    if (valueColumnMode === 'sum') {
      return 0;
    }
    if (activeTable === 'expense') {
      return Number(assignment.realEstimatedHours ?? assignment.estimatedHours) || 0;
    }
    return Number(assignment.estimatedHours) || 0;
  };

  const getRawAssignmentAmount = (
    assignment: { estimatedHours?: number; hourlyRate?: number; fixedAmount?: number | null },
    mode: BiddingValueColumnMode = valueColumnMode
  ): number => {
    if (mode === 'sum') {
      return Number(assignment.fixedAmount) || 0;
    }
    return (Number(assignment.estimatedHours) || 0) * (Number(assignment.hourlyRate) || 0);
  };

  const resolveAssignmentLevel = (assignment: ShotAssignment): AssignmentLevelKey => {
    if (assignment.virtualArtistType) {
      return normalizeAssignmentLevel(assignment.virtualArtistType);
    }
    if (assignment.artistLevel) {
      return normalizeAssignmentLevel(assignment.artistLevel);
    }
    return 'other';
  };

  const calculateSpecTotals = (): Record<string, { hours: number; amount: number }> => {
    const totals: Record<string, { hours: number; amount: number }> = {};
    specializations.forEach(spec => {
      totals[spec] = { hours: 0, amount: 0 };
    });
    getRelevantAssignments().forEach(assignment => {
      if (!totals[assignment.specialization]) {
        totals[assignment.specialization] = { hours: 0, amount: 0 };
      }
      totals[assignment.specialization].hours += getAssignmentHours(assignment);
      totals[assignment.specialization].amount += getAssignmentAmount(assignment);
    });
    return totals;
  };

  const calculateLevelTotals = (): Record<AssignmentLevelKey, { hours: number; amount: number }> => {
    const totals = AGGREGATION_LEVEL_ORDER.reduce((acc, level) => {
      acc[level] = { hours: 0, amount: 0 };
      return acc;
    }, {} as Record<AssignmentLevelKey, { hours: number; amount: number }>);
    getRelevantAssignments().forEach(assignment => {
      const level = resolveAssignmentLevel(assignment);
      totals[level].hours += getAssignmentHours(assignment);
      totals[level].amount += getAssignmentAmount(assignment);
    });
    return totals;
  };

  const getLevelLabel = (level: AssignmentLevelKey): string => {
    switch (level) {
      case 'junior':
        return t('bidding.levelJunior');
      case 'mid':
        return t('bidding.levelMid');
      case 'senior':
        return t('bidding.levelSenior');
      case 'lead':
        return t('bidding.levelLead');
      default:
        return t('bidding.levelOther');
    }
  };

  const calculateShotTotal = (shotId: string) => {
    const shotAssignments = assignments.filter(a => a.shotId === shotId);
    
    // In expense table, filter out virtual artists
    const relevantAssignments = activeTable === 'expense' 
      ? shotAssignments.filter(a => !a.isVirtualArtist)
      : shotAssignments;
    
    return relevantAssignments.reduce((sum, a) => sum + getAssignmentAmount(a), 0);
  };

  const calculateProjectTotal = () => {
    const shotsTotal = shots.reduce((sum, shot) => sum + calculateShotTotal(shot.id), 0);
    return shotsTotal;
  };

  const calculateGrandTotal = () => {
    const projectTotal = calculateProjectTotal();
    // Use either fixed fee or percentage for each manager
    const producerFee = useProducerFixedFee 
      ? (Number(producerFixedFee) || 0)
      : projectTotal * ((Number(producerPercentage) || 0) / 100);
    const coordFee = useCoordinatorFixedFee
      ? (Number(coordinatorFee) || 0)
      : projectTotal * ((Number(coordinatorPercentage) || 0) / 100);
    const supFee = useSupervisorFixedFee
      ? (Number(supervisorFee) || 0)
      : projectTotal * ((Number(supervisorPercentage) || 0) / 100);
    const findFee = useFinderFixedFee
      ? (Number(finderFee) || 0)
      : projectTotal * ((Number(finderPercentage) || 0) / 100);
    return projectTotal + producerFee + coordFee + supFee + findFee;
  };

  const calculateVatAmount = () => {
    const net = calculateGrandTotal();
    return net * ((Number(vatRate) || 0) / 100);
  };

  const calculateGrossTotal = () => {
    return calculateGrandTotal() + calculateVatAmount();
  };

  // Calculate profit pool (studio's remaining profit after all expenses)
  // Profit Pool = Revenue (base) - Expense (artists) - Management Fees
  // Uses dedicated data sources, NOT dependent on activeTable UI state
  const calculateProfitPool = async (): Promise<number | null> => {
    if (!canViewExpense) return null;
    
    try {
      // Calculate revenue/expense base totals using active valueColumnMode (hours or sum)
      const revenueBaseTotal = revenueAssignments.reduce(
        (sum, a) => sum + getRawAssignmentAmount(a),
        0
      );

      const expenseBaseTotal = expenseAssignments.reduce(
        (sum, a) => sum + getRawAssignmentAmount(a),
        0
      );
      
      // Calculate management fees for REVENUE (based on revenue base - what client pays)
      const revProducerFee = useProducerFixedFee
        ? (Number(producerFixedFee) || 0)
        : revenueBaseTotal * ((Number(producerPercentage) || 0) / 100);
      const revCoordFee = useCoordinatorFixedFee
        ? (Number(coordinatorFee) || 0)
        : revenueBaseTotal * ((Number(coordinatorPercentage) || 0) / 100);
      const revSupFee = useSupervisorFixedFee
        ? (Number(supervisorFee) || 0)
        : revenueBaseTotal * ((Number(supervisorPercentage) || 0) / 100);
      const revFindFee = useFinderFixedFee
        ? (Number(finderFee) || 0)
        : revenueBaseTotal * ((Number(finderPercentage) || 0) / 100);
      const revTotalFees = revProducerFee + revCoordFee + revSupFee + revFindFee;
      
      // Revenue Grand Total = base + fees (what client pays in total)
      const revenueGrandTotal = revenueBaseTotal + revTotalFees;
      
      // Calculate management fees for EXPENSE (based on expense base - real costs)
      const expProducerFee = useProducerFixedFee
        ? (Number(producerFixedFee) || 0)
        : expenseBaseTotal * ((Number(producerPercentage) || 0) / 100);
      const expCoordFee = useCoordinatorFixedFee
        ? (Number(coordinatorFee) || 0)
        : expenseBaseTotal * ((Number(coordinatorPercentage) || 0) / 100);
      const expSupFee = useSupervisorFixedFee
        ? (Number(supervisorFee) || 0)
        : expenseBaseTotal * ((Number(supervisorPercentage) || 0) / 100);
      const expFindFee = useFinderFixedFee
        ? (Number(finderFee) || 0)
        : expenseBaseTotal * ((Number(finderPercentage) || 0) / 100);
      const expTotalFees = expProducerFee + expCoordFee + expSupFee + expFindFee;
      
      // Expense Grand Total = base + fees (what we actually pay out)
      const expenseGrandTotal = expenseBaseTotal + expTotalFees;
      
      // Profit Pool = Revenue Grand Total - Expense Grand Total
      // This is the difference between what client pays and what we pay out
      return revenueGrandTotal - expenseGrandTotal;
    } catch (error) {
      console.error('Error calculating profit pool:', error);
      return null;
    }
  };

  // Calculate individual artist payments with shot count and hours
  const calculateArtistPayments = () => {
    const payments: Record<string, { 
      name: string; 
      total: number; 
      hours: number;
      shotIds: Set<string>;
      color: string;
    }> = {};
    
    // Filter assignments based on active table
    const relevantAssignments = activeTable === 'expense'
      ? assignments.filter(a => a.realArtistId) // Only real artists in expense table
      : assignments.filter(a => a.artistId || a.isVirtualArtist); // All artists including virtual in revenue table
    
    relevantAssignments.forEach(assignment => {
      const amount = getAssignmentAmount(assignment);
      const hours = getAssignmentHours(assignment);

      if (assignment.isVirtualArtist && activeTable === 'revenue') {
        const virtualArtistName = assignment.virtualArtistType
          ? `${assignment.virtualArtistType.charAt(0).toUpperCase() + assignment.virtualArtistType.slice(1)} Artist`
          : 'Artist';

        const key = `virtual_${assignment.virtualArtistType || 'unknown'}`;
        if (!payments[key]) {
          payments[key] = {
            name: virtualArtistName,
            total: 0,
            hours: 0,
            shotIds: new Set<string>(),
            color: 'bg-gray-500/20 text-gray-300',
          };
        }
        payments[key].total += amount;
        payments[key].hours += hours;
        payments[key].shotIds.add(assignment.shotId);
      } else if (activeTable === 'expense' ? assignment.realArtistId : assignment.artistId) {
        const currentArtistId = activeTable === 'expense' ? assignment.realArtistId : assignment.artistId;
        const artist = artists.find(a => a.id === currentArtistId);
        if (artist) {
          if (!payments[currentArtistId!]) {
            payments[currentArtistId!] = {
              name: artist.name,
              total: 0,
              hours: 0,
              shotIds: new Set<string>(),
              color: artistColors[currentArtistId!]?.class || 'bg-gray-500/20 text-gray-300',
            };
          }
          payments[currentArtistId!].total += amount;
          payments[currentArtistId!].hours += hours;
          payments[currentArtistId!].shotIds.add(assignment.shotId);
        }
      }
    });
    
    // Convert to final format with shotCount
    return Object.entries(payments)
      .map(([artistId, payment]) => ({
        artistId,
        name: payment.name,
        total: payment.total,
        hours: payment.hours,
        shotCount: payment.shotIds.size,
        color: payment.color
      }))
      .sort((a, b) => b.total - a.total);
  };

  // Get participant details for modal
  const getParticipantDetails = (artistId: string) => {
    // Filter assignments by artistId (including virtual artists with matching key)
    const artistAssignments = assignments.filter(a => {
      if (a.artistId === artistId) return true;
      // Handle virtual artists - check if artistId matches virtual artist key format
      if (a.isVirtualArtist && artistId.startsWith('virtual_')) {
        return `virtual_${a.virtualArtistType}` === artistId;
      }
      return false;
    });
    
    return artistAssignments.map(assignment => {
      const shot = shots.find(s => s.id === assignment.shotId);
      if (!shot) return null;
      
      const hours = getAssignmentHours(assignment);
      const payment = getAssignmentAmount(assignment);
      
      return {
        shot,
        assignment: {
          ...assignment,
          artistId: assignment.artistId || '' // Ensure artistId is defined for type compatibility
        },
        payment
      };
    }).filter((item): item is NonNullable<typeof item> => item !== null)
      .sort((a, b) => a.shot.shotNumber - b.shot.shotNumber);
  };

  // Open participant details modal
  const handleOpenParticipantDetails = (artistId: string, artistName: string, artistColor: string) => {
    setParticipantDetailsModal({
      isOpen: true,
      artistId,
      artistName,
      artistColor
    });
  };

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(shots.length / shotsPerPage));
  const startIndex = (currentPage - 1) * shotsPerPage;
  const endIndex = startIndex + shotsPerPage;
  const paginatedShots = shots.slice(startIndex, endIndex);
  const specTotals = calculateSpecTotals();
  const levelTotals = calculateLevelTotals();
  const visibleLevels = AGGREGATION_LEVEL_ORDER.filter(
    (level) => levelTotals[level].hours > 0 || levelTotals[level].amount > 0
  );
  const showFooterCheckboxCol = isDeleteMode || (activeTable === 'expense' && canViewExpense);
  const showDragHandle = !isDeleteMode && activeTable !== 'expense';
  const showStandaloneDragCol = showDragHandle && !isColumnVisible('number');
  const footerLeadingColCount =
    (showFooterCheckboxCol ? 1 : 0) + (showStandaloneDragCol ? 1 : 0) + visibleLeadingCount;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShotsPerPageChange = (raw: string) => {
    setShotsPerPageInput(raw);
  };

  const commitShotsPerPage = () => {
    const parsed = parseInt(shotsPerPageInput, 10);
    if (Number.isNaN(parsed)) {
      setShotsPerPageInput(String(shotsPerPage));
      return;
    }
    const clamped = Math.min(200, Math.max(1, parsed));
    setShotsPerPageInput(String(clamped));
    if (clamped !== shotsPerPage) {
      setShotsPerPage(clamped);
      setCurrentPage(1);
      writeBiddingShotsPerPage(clamped);
    }
  };


  const formatTimecode = (seconds: number | undefined, fps: number = 24): string => {
    if (seconds === undefined) return '--:--:--:--';
    const totalSeconds = Number(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = Math.floor(totalSeconds % 60);
    const frames = Math.floor((totalSeconds % 1) * fps);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}:${String(frames).padStart(2, '0')}`;
  };

  const parseTimecode = (timecode: string, fps: number = 24): number => {
    const parts = timecode.split(':').map(p => parseInt(p) || 0);
    if (parts.length !== 4) return 0;
    
    const [hours, minutes, seconds, frames] = parts;
    const totalSeconds = hours * 3600 + minutes * 60 + seconds + frames / fps;
    return totalSeconds;
  };

  const hasBiddingSourceVideo = Boolean(biddingSheet.videoPath || biddingSheet.videoUrl);

  const runThumbnailCapture = async (shotId: string, timecodeInSeconds: number) => {
    if (timecodeInSeconds <= 0 || !hasBiddingSourceVideo) return;

    const seq = (thumbnailCaptureSeqRef.current.get(shotId) || 0) + 1;
    thumbnailCaptureSeqRef.current.set(shotId, seq);
    thumbnailLastCaptureAtRef.current.set(shotId, Date.now());

    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(
        `${API_CONFIG.API_URL}/bidding/shots/${shotId}/thumbnail/from-timecode`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ timecodeInSeconds })
        }
      );
      if (!response.ok) {
        console.error('Thumbnail capture failed:', response.status);
        return;
      }
      const data = await response.json();
      if (data?.skipped) return;
      if (thumbnailCaptureSeqRef.current.get(shotId) !== seq) return;
      if (data?.thumbnailUrl) {
        setShots(prev =>
          prev.map(s => (s.id === shotId ? { ...s, thumbnailUrl: data.thumbnailUrl } : s))
        );
      }
    } catch (error) {
      console.error('Thumbnail capture error:', error);
    }
  };

  /** Schedule capture at TC IN; at most once per 5s per shot; collapses to latest IN. */
  const scheduleThumbnailCapture = (shotId: string, timecodeInSeconds: number) => {
    if (timecodeInSeconds <= 0 || !hasBiddingSourceVideo) {
      thumbnailPendingInRef.current.delete(shotId);
      const existing = thumbnailThrottleTimerRef.current.get(shotId);
      if (existing) {
        clearTimeout(existing);
        thumbnailThrottleTimerRef.current.delete(shotId);
      }
      return;
    }

    thumbnailPendingInRef.current.set(shotId, timecodeInSeconds);

    const lastAt = thumbnailLastCaptureAtRef.current.get(shotId) || 0;
    const elapsed = Date.now() - lastAt;
    const waitMs = Math.max(0, THUMBNAIL_CAPTURE_MIN_INTERVAL_MS - elapsed);

    if (thumbnailThrottleTimerRef.current.has(shotId)) {
      return;
    }

    const fire = () => {
      thumbnailThrottleTimerRef.current.delete(shotId);
      const pending = thumbnailPendingInRef.current.get(shotId);
      thumbnailPendingInRef.current.delete(shotId);
      if (pending === undefined || pending <= 0) return;
      void runThumbnailCapture(shotId, pending);
    };

    if (waitMs <= 0) {
      fire();
      return;
    }

    const timerId = setTimeout(fire, waitMs);
    thumbnailThrottleTimerRef.current.set(shotId, timerId);
  };

  const handleTimecodeChange = async (shotId: string, field: 'timecodeIn' | 'timecodeOut', seconds: number) => {
    try {
      const token = localStorage.getItem('spellbook_token');
      
      // Track local edit to avoid highlighting on this machine
      const editKey = `${shotId}:${field}`;
      localEditsRef.current.set(editKey, Date.now());
      
      const updateData: any = {};
      if (field === 'timecodeIn') {
        updateData.timecodeInSeconds = seconds;
      } else {
        updateData.timecodeOutSeconds = seconds;
      }

      // Optimistic update - use functional update to ensure we're working with latest state
      setShots(prevShots => {
        const currentShot = prevShots.find(s => s.id === shotId);
        // Only update if the new value is different from current
        if (currentShot && (
          (field === 'timecodeIn' && currentShot.timecodeInSeconds !== seconds) ||
          (field === 'timecodeOut' && currentShot.timecodeOutSeconds !== seconds)
        )) {
          return prevShots.map(s => 
        s.id === shotId ? { ...s, ...updateData } : s
          );
        }
        return prevShots;
      });
      
      // Store current value for onBlur save
      const valueKey = `${shotId}:${field}`;
      timecodeValuesRef.current.set(valueKey, seconds);

      // Clear existing timeout
      const timeoutKey = `${shotId}:${field}`;
      if (timecodeTimeoutsRef.current.has(timeoutKey)) {
        clearTimeout(timecodeTimeoutsRef.current.get(timeoutKey)!);
      }
      
      // Set new timeout for debounced save (800ms - longer to avoid conflicts during rapid arrow key changes)
      const timeoutId = setTimeout(async () => {
        // Double-check that field is still not being actively edited before saving
        const editKey = `${shotId}:${field}`;
        if (activelyEditingRef.current.has(editKey)) {
          // Still being edited, don't save yet - will be saved on blur
          return;
        }
        
        try {
          // Get the latest value from ref (most recent change)
          const latestValue = timecodeValuesRef.current.get(valueKey);
          if (latestValue === undefined) return;
          
          const saveData: any = {};
          if (field === 'timecodeIn') {
            saveData.timecodeInSeconds = latestValue;
          } else {
            saveData.timecodeOutSeconds = latestValue;
          }
          
          const response = await fetch(`${API_CONFIG.API_URL}/bidding/shots/${shotId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
            body: JSON.stringify(saveData)
      });
          if (response.ok) {
            setLastUpdateTime(Date.now());
            timecodeTimeoutsRef.current.delete(timeoutKey);
            timecodeValuesRef.current.delete(valueKey);
            if (field === 'timecodeIn') {
              scheduleThumbnailCapture(shotId, latestValue);
            }
          } else {
            console.error('Error updating timecode:', response.status);
          }
    } catch (error) {
      console.error('Error updating timecode:', error);
          // Don't fetchData on error - let user continue editing
        }
      }, 800); // Increased from 500ms to 800ms for better debounce during rapid changes
      
      timecodeTimeoutsRef.current.set(timeoutKey, timeoutId);
    } catch (error) {
      console.error('Error updating timecode:', error);
      // Don't fetchData on error - let user continue editing
    }
  };

  const handleDurationChange = async (shotId: string, value: number) => {
    try {
      const token = localStorage.getItem('spellbook_token');

      // Track local edit to avoid highlighting on this machine
      const editKey = `${shotId}:durationFrames`;
      localEditsRef.current.set(editKey, Date.now());

      // Optimistic update
      setShots(prevShots => prevShots.map(s => 
        s.id === shotId ? { ...s, durationFrames: value } : s
      ));

      // Store current value for onBlur save
      durationValuesRef.current.set(shotId, value);

      // Clear existing timeout
      const timeoutKey = `${shotId}:durationFrames`;
      if (durationTimeoutsRef.current.has(timeoutKey)) {
        clearTimeout(durationTimeoutsRef.current.get(timeoutKey)!);
      }
      
      // Set new timeout for debounced save (500ms)
      const timeoutId = setTimeout(async () => {
        try {
          const response = await fetch(`${API_CONFIG.API_URL}/bidding/shots/${shotId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ durationFrames: value })
      });
          if (response.ok) {
            setLastUpdateTime(Date.now());
          }
        } catch (error) {
          console.error('Error updating duration:', error);
          fetchData();
        }
        durationTimeoutsRef.current.delete(timeoutKey);
        durationValuesRef.current.delete(shotId);
      }, 500);
      
      durationTimeoutsRef.current.set(timeoutKey, timeoutId);
    } catch (error) {
      console.error('Error updating duration:', error);
      fetchData();
    }
  };

  // Shot Name functions
  const handleShotNameChange = (shotId: string, newName: string) => {
    // Track local edit to avoid highlighting on this machine
    const editKey = `${shotId}:shotName`;
    localEditsRef.current.set(editKey, Date.now());
    
    // Update local state immediately for responsive UI
    setShotNameChanges(prev => ({ ...prev, [shotId]: newName }));
    setShots(prevShots => prevShots.map(s => s.id === shotId ? { ...s, shotName: newName } : s));

    // Clear existing timeout
    if (shotNameTimeouts.current[shotId]) {
      clearTimeout(shotNameTimeouts.current[shotId]);
    }

    // Set new timeout for auto-save (debounce)
    shotNameTimeouts.current[shotId] = setTimeout(() => {
      handleShotNameSave(shotId, newName);
    }, 1000);
  };

  const handleShotNameSave = async (shotId: string, shotName?: string) => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const nameToSave = shotName ?? shotNameChanges[shotId];
      
      const response = await fetch(`${API_CONFIG.API_URL}/annotations/shots/${shotId}/name`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ shotName: nameToSave })
      });

      if (!response.ok) {
        console.error(`Failed to save shot name: ${response.status} ${response.statusText}`);
        // Don't call fetchData() to avoid interrupting user input
        // Just keep the local change and retry later
        return;
      }

      // Clear from pending changes only if save was successful
      setShotNameChanges(prev => {
        const { [shotId]: _, ...rest } = prev;
        return rest;
      });
      
      // Update lastUpdateTime to acknowledge successful save
      setLastUpdateTime(Date.now());
    } catch (error) {
      console.error('Failed to save shot name:', error);
      // Don't call fetchData() to avoid interrupting user input
    }
  };

  // Annotation functions
  const openAnnotationModal = (shot: Shot, commentId?: string) => {
    // Close autofill modal if it's open to prevent interference
    if (autofillModal.show) {
      setAutofillModal({ show: false, pattern: null, startIndex: 0 });
    }
    setAnnotationModal({ show: true, shot, commentId });
    // Update URL to include shotId and optional commentId
    const url = commentId 
      ? `/bidding/${biddingSheetId}/shot/${shot.id}?comment=${commentId}`
      : `/bidding/${biddingSheetId}/shot/${shot.id}`;
    navigate(url, { replace: true });
  };

  const closeAnnotationModal = () => {
    setAnnotationModal({ show: false, shot: null });
    // Update URL to remove shotId when closing modal
    if (params.shotId) {
      navigate(`/bidding/${biddingSheetId}`, { replace: true });
    }
  };
  
  // Handle shotId from URL - open annotation modal for specific shot
  const location = useLocation();
  useEffect(() => {
    if (params.shotId && shots.length > 0 && !annotationModal.show) {
      const shot = shots.find(s => s.id === params.shotId);
      if (shot) {
        // Check for commentId in URL query params
        const searchParams = new URLSearchParams(location.search);
        const commentId = searchParams.get('comment') || undefined;
        setAnnotationModal({ show: true, shot, commentId });
        // Don't call openAnnotationModal here to avoid infinite loop
      } else {
        // Shot not found, remove shotId from URL
        navigate(`/bidding/${biddingSheetId}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.shotId, shots.length, annotationModal.show, location.search]);

  const handleCommentsCountChange = (shotId: string, count: number) => {
    // Track local edit to avoid highlighting on this machine
    const editKey = `${shotId}:commentsCount`;
    localEditsRef.current.set(editKey, Date.now());
    
    // Update comments count in local state
    setShots(shots.map(s => 
      s.id === shotId ? { ...s, commentsCount: count } : s
    ));
  };

  const handleSaveAnnotation = async (shotId: string, dataUrl: string | null) => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const userId = localStorage.getItem('userId');
      
      const annotation: ShotAnnotation | null = dataUrl ? {
        id: crypto.randomUUID(),
        type: 'freehand',
        dataUrl,
        createdBy: userId || 'unknown',
        createdAt: new Date().toISOString()
      } : null;

      const response = await fetch(`${API_CONFIG.API_URL}/annotations/shots/${shotId}/annotations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          annotations: annotation ? [annotation] : []
        })
      });

      if (response.ok) {
        // Update local state instead of reloading all data (prevents playhead reset)
        setShots(prevShots => prevShots.map(shot => {
          if (shot.id === shotId) {
            return {
              ...shot,
              annotations: annotation ? [annotation] : []
            };
          }
          return shot;
        }));
      } else {
        console.error('Failed to save annotation:', await response.text());
      }
    } catch (error) {
      console.error('Failed to save annotation:', error);
      throw error; // Re-throw so modal can handle error
    }
  };

  // Autofill functions
  const handleConfirmAutofill = async (updates: Array<{ shotId: string; shotName: string }>) => {
    try {
      const token = localStorage.getItem('spellbook_token');
      
      // Bulk update shot names
      const response = await fetch(`${API_CONFIG.API_URL}/annotations/shots/names/bulk`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ updates })
      });

      if (response.ok) {
        // Update local state
        setShots(prevShots => {
          const updatesMap = new Map(updates.map(u => [u.shotId, u.shotName]));
          return prevShots.map(s => 
            updatesMap.has(s.id) ? { ...s, shotName: updatesMap.get(s.id) } : s
          );
        });
        
        // Mark autofill as dismissed so it won't show again
        markAutofillDismissed(biddingSheetId);
        
        // Close modal
        setAutofillModal({ show: false, pattern: null, startIndex: 0 });
      } else {
        console.error('Failed to bulk update shot names:', await response.text());
      }
    } catch (error) {
      console.error('Failed to bulk update shot names:', error);
      throw error;
    }
  };

  const handleCancelAutofill = () => {
    // Mark autofill as dismissed so it won't show again
    markAutofillDismissed(biddingSheetId);
    setAutofillModal({ show: false, pattern: null, startIndex: 0 });
    // Don't trigger sync - preserve current state
  };

  // Force autofill check (ignores dismissed flag)
  const handleForceAutofill = () => {
    if (shots.length < 2) return;
    
    // Check if there are unfilled shots after filled ones
    if (!hasUnfilledShotsAfterFilled(shots)) {
      // Show message that all shots are filled
      alert(t('bidding.autofill.allFilled') || 'All shots are already filled');
      return;
    }
    
    // Detect pattern
    const pattern = detectShotNamePattern(shots);
    if (!pattern) {
      // More specific error message
      const namedShots = shots.filter(s => s.shotName && s.shotName.trim());
      if (namedShots.length < 2) {
        alert(t('bidding.autofill.needTwoShots') || 'Need at least 2 filled shot names to detect a pattern');
      } else {
        alert(t('bidding.autofill.noPattern') || 'No pattern detected. Make sure shot names follow a consistent pattern (e.g., SHOT_0010, SHOT_0020)');
      }
      return;
    }
    
    // Find first unfilled index after last filled
    const lastFilledIndex = shots.map((s, i) => ({ shot: s, index: i }))
      .filter(({ shot }) => shot.shotName && shot.shotName.trim())
      .pop()?.index ?? -1;
    
    const startIndex = lastFilledIndex + 1;
    
    // Don't show if annotation modal is open
    if (annotationModal.show) {
      alert(t('bidding.autofill.closeAnnotation') || 'Please close the annotation modal first');
      return;
    }
    
    if (startIndex < shots.length) {
      setAutofillModal({ show: true, pattern, startIndex, isManual: true });
      // Store current user as the one who triggered autofill
      const currentUserId = localStorage.getItem('userId');
      if (currentUserId) {
        const autofillUserIdKey = `autofill_user_${biddingSheetId}`;
        localStorage.setItem(autofillUserIdKey, currentUserId);
      }
    } else {
      alert(t('bidding.autofill.allFilled') || 'All shots are already filled');
    }
  };

  const handleProducerPercentageChange = async (value: number) => {
    setProducerPercentage(value);
    
    // Save to backend
    try {
      const token = localStorage.getItem('spellbook_token');
      await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ producerPercentage: value })
      });
    } catch (error) {
      console.error('Error saving producer percentage:', error);
    }
  };

  const handleCoordinatorFeeChange = async (value: number) => {
    setCoordinatorFee(value);
    
    // Save to backend
    try {
      const token = localStorage.getItem('spellbook_token');
      await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ coordinatorFee: value })
      });
    } catch (error) {
      console.error('Error saving coordinator fee:', error);
    }
  };

  const handleSupervisorFeeChange = async (value: number) => {
    setSupervisorFee(value);
    
    // Save to backend
    try {
      const token = localStorage.getItem('spellbook_token');
      await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ supervisorFee: value })
      });
    } catch (error) {
      console.error('Error saving supervisor fee:', error);
    }
  };

  const handleVatRateChange = async (value: number) => {
    setVatRate(value);
    
    // Save to backend
    try {
      const token = localStorage.getItem('spellbook_token');
      await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ vatRate: value })
      });
    } catch (error) {
      console.error('Error saving VAT rate:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] w-full">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500" />
      </div>
    );
  }

  const handleClose = () => {
    onSave(); // Auto-save on navigation
    onClose();
  };

  // --- Combined Excel export (revenue + expense in one file) ---
  const handleExportCombined = async (audience: 'client' | 'internal' = 'internal') => {
    try {
      const { exportCombinedBiddingToExcel } = await import('../utils/excelExport');
      // Map ExpenseAssignment[] → Assignment[] for the export utility
      const mappedExpense = expenseAssignments.map(ea => ({
        shotId: ea.shotId,
        specialization: ea.specialization,
        artistId: ea.artistId || undefined,
        estimatedHours: ea.estimatedHours,
        hourlyRate: ea.hourlyRate,
        fixedAmount: ea.fixedAmount != null ? Number(ea.fixedAmount) : undefined,
        isVirtualArtist: (ea as any).isVirtualArtist,
        virtualArtistType: (ea as any).virtualArtistType,
      }));
      await exportCombinedBiddingToExcel(
        {
          ...currentBiddingSheet,
          valueColumnMode,
        },
        shots,
        revenueAssignments as any, // ShotAssignment is a superset of Assignment
        mappedExpense,
        artists,
        specializations,
        language,
        audience
      );
    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  };

  return (
    <div className={`h-full min-h-0 flex flex-col gap-4 rounded-lg transition-colors duration-300 ${
      activeTable === 'expense' 
        ? 'bg-[#1a1d2d] border border-indigo-900/30 p-4' 
        : ''
    }`}>
      <BiddingRevisionBanner
        kind={revisionBannerKind}
        onDismiss={() => setRevisionBannerKind(null)}
      />
      {/* Toolbar: left toggles | centered title | right actions */}
      <div className="shrink-0 flex items-center gap-2">
        <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={handleClose}
          className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors shrink-0"
          title={t('common.cancel')}
        >
          <ICONS.ArrowLeft className="w-5 h-5 text-gray-400" />
        </button>

        <div className="flex items-center space-x-2 shrink-0">
        {/* Table Switching Buttons - always visible for Producer and above */}
        {canViewExpense && (
          <>
            <button
              onClick={() => handleActiveTableChange('revenue')}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeTable === 'revenue'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {t('bidding.revenueTable') || 'Приходная'}
            </button>
            <button
              onClick={() => handleActiveTableChange('expense')}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeTable === 'expense'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {t('bidding.expenseTable') || 'Расходная'}
            </button>
          </>
        )}
        {canToggleValueColumnMode && (
        <div className="inline-flex bg-gray-800 rounded-lg p-0.5 text-sm ml-2 pl-2 border-l border-gray-600">
          <button
            type="button"
            onClick={() => handleValueColumnModeChange('hours')}
            className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
              valueColumnMode === 'hours'
                ? 'bg-indigo-600 text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            {t('bidding.hours')}
          </button>
          <button
            type="button"
            onClick={() => handleValueColumnModeChange('sum')}
            className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
              valueColumnMode === 'sum'
                ? 'bg-indigo-600 text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            {t('bidding.sum')}
          </button>
        </div>
        )}
        </div>
        </div>

        {/* Title + meta — centered between left/right button groups */}
        <div className="flex-1 min-w-0 flex items-center justify-center gap-2 px-2">
          {isEditingName ? (
            <input
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onBlur={handleNameSave}
              onKeyDown={handleNameKeyDown}
              className="text-sm font-semibold text-white bg-gray-700 border border-indigo-500 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-0 max-w-full"
              autoFocus
            />
          ) : (
            <button
              type="button"
              onDoubleClick={handleNameDoubleClick}
              className="min-w-0 max-w-full truncate text-center text-sm leading-none py-1.5 text-gray-200 hover:text-indigo-300 transition-colors"
              title={`${biddingSheet.name} / ${t('bidding.shotsTable')} · ${t('bidding.shots')}: ${shots.length} | ${t('common.currency')}: ${currency} — ${t('bidding.doubleClickToEdit') || 'Double-click to edit'}`}
            >
              <span className="font-semibold text-white">{biddingSheet.name}</span>
              <span className="text-gray-500 mx-1.5">/</span>
              <span className="text-gray-400">{t('bidding.shotsTable')}</span>
              <span className="text-gray-600 mx-1.5">·</span>
              <span className="text-gray-500">
                {t('bidding.shots')}: {shots.length} | {t('common.currency')}: {currency}
              </span>
            </button>
          )}
          {currentBiddingSheet.status && currentBiddingSheet.status !== 'draft' && (
            <span className={`shrink-0 px-2 py-0.5 text-xs rounded border leading-none ${
              currentBiddingSheet.status === 'approved' ? 'bg-green-900/50 text-green-400 border-green-500/50' :
              currentBiddingSheet.status === 'rejected' ? 'bg-red-900/50 text-red-400 border-red-500/50' :
              currentBiddingSheet.status === 'sent' ? 'bg-blue-900/50 text-blue-400 border-blue-500/50' :
              'bg-gray-700 text-gray-300 border-gray-600'
            }`}>
              {t(`bidding.statuses.${currentBiddingSheet.status}`) || currentBiddingSheet.status}
            </span>
          )}
          <button
            type="button"
            onClick={() => setIsSettingsModalOpen(true)}
            className="shrink-0 p-1.5 hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-white"
            title={t('bidding.settings') || 'Настройки сметы'}
          >
            <ICONS.Settings className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center space-x-2 shrink-0">
          {hiddenColumnIds.length > 0 && (
            <button
              onClick={handleUnhideAllColumns}
              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm flex items-center space-x-1"
              title={t('bidding.columns.unhideAll') || 'Unhide all columns'}
            >
              <ICONS.Eye className="w-4 h-4" />
              <span>{t('bidding.columns.unhideAll') || 'Unhide all columns'}</span>
            </button>
          )}
          <button
            onClick={() => setIsExportExcelOpen(true)}
            className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm flex items-center space-x-1"
            title={t('bidding.exportExcel') || 'Export Excel'}
          >
            <ICONS.Export className="w-4 h-4" />
            <span>{t('bidding.exportExcel') || 'Excel'}</span>
          </button>
          <button
            onClick={() => setIsExportVideoOpen(true)}
            className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm flex items-center space-x-1 disabled:opacity-50"
            title={t('bidding.exportVideo') || 'Export bid to video'}
            disabled={shots.length === 0}
          >
            <ICONS.Video className="w-4 h-4" />
            <span>{t('bidding.exportVideoShort') || 'Video'}</span>
          </button>
          <button
            onClick={handleForceAutofill}
            className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm flex items-center space-x-1"
            title={t('bidding.autofillForce') || 'Force autofill'}
          >
            <ICONS.MagicWand className="w-4 h-4" />
            <span>{t('bidding.autofillButton') || 'Autofill'}</span>
          </button>
          <button
            onClick={() => setIsAiBidOpen(true)}
            className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm flex items-center space-x-1 disabled:opacity-50"
            title={t('bidding.ai.title') || 'AI Bid Allocator'}
            disabled={shots.length === 0}
          >
            <ICONS.MagicWand className="w-4 h-4" />
            <span>{t('bidding.ai.button') || 'AI Bid'}</span>
          </button>
          <button
            onClick={() => setIsRatesModalOpen(true)}
            className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm flex items-center space-x-1 transition-colors"
            title={activeTable === 'expense' ? (t('bidding.artistRates.titleExpense') || "Artist's Rates") : (t('bidding.artistRates.titleRevenue') || 'Role Rates')}
          >
            <ICONS.CreditCard className="w-4 h-4" />
            <span>{activeTable === 'expense' ? (t('bidding.artistRates.buttonExpense') || "Artist's Rates") : (t('bidding.artistRates.buttonRevenue') || 'Role Rates')}</span>
          </button>
          <button
            onClick={handleAddRow}
            className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm flex items-center space-x-1"
          >
            <ICONS.Plus className="w-4 h-4" />
            <span>{t('bidding.addShot')}</span>
          </button>
          {activeTable === 'expense' && canViewExpense && selectedShots.size > 0 && (
            <button
              onClick={() => setIsBulkApproveModalOpen(true)}
              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm flex items-center space-x-1"
              title={t('bulkApprove.title') || 'Отправить в финансы'}
            >
              <ICONS.Check className="w-4 h-4" />
              <span>{t('bulkApprove.title') || 'Отправить в финансы'} ({selectedShots.size})</span>
            </button>
          )}
          <button
            onClick={handleDeleteButtonClick}
            disabled={isDeleteMode && selectedShots.size === 0}
            className={`px-3 py-1 rounded text-white text-sm flex items-center space-x-1 transition-colors ${
              isDeleteMode 
                ? 'bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:cursor-not-allowed' 
                : 'bg-gray-700 hover:bg-gray-600'
            }`}
          >
            <ICONS.Trash className="w-4 h-4" />
            <span>
              {isDeleteMode 
                ? `${t('bidding.confirmDelete')} ${selectedShots.size > 0 ? `(${selectedShots.size})` : ''}` 
                : t('bidding.deleteShots')}
            </span>
          </button>
          {isDeleteMode && (
            <button
              onClick={handleCancelDeleteMode}
              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm flex items-center space-x-1"
            >
              <ICONS.X className="w-4 h-4" />
              <span>{t('common.cancel')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Single vertical (and horizontal) scrollport — sticky thead */}
      <div
        ref={tableScrollRef}
        className="w-full min-w-0 flex-1 min-h-0 overflow-auto"
      >
        <table className="min-w-max mx-auto text-sm text-left table-auto">
          <thead className="text-xs text-gray-400 uppercase bg-gray-800 sticky top-0 z-10">
            <tr>
              {(isDeleteMode || (activeTable === 'expense' && canViewExpense)) && (
                <th className="px-2 py-3 w-12">
                  <input
                    type="checkbox"
                    checked={shots.length > 0 && selectedShots.size === shots.length}
                    onChange={handleToggleSelectAll}
                    className="w-4 h-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                  />
                </th>
              )}
              {showStandaloneDragCol && (
                <th
                  className="p-0 text-center align-middle whitespace-nowrap"
                  style={{ width: 24, minWidth: 24, maxWidth: 24 }}
                />
              )}
              {isColumnVisible('number') && (
                <th className="w-0 px-1 py-3 text-center whitespace-nowrap min-w-[3.25rem] max-w-[4.5rem]">
                  <ColumnHeader
                    label="#"
                    columnId="number"
                    onHide={hideColumn}
                    hideTitle={columnHideTitle}
                    className="justify-center"
                  />
                </th>
              )}
              {isColumnVisible('thumbnail') && (
                <th className="px-2 py-3 w-24 min-w-[6rem] whitespace-nowrap">
                  <ColumnHeader label={t('bidding.thumbnail')} columnId="thumbnail" onHide={hideColumn} hideTitle={columnHideTitle} />
                </th>
              )}
              {isColumnVisible('shotName') && (
                <th className="px-2 py-3 w-40 min-w-[10rem] whitespace-nowrap">
                  <ColumnHeader label={t('bidding.shotName') || 'Shot Name'} columnId="shotName" onHide={hideColumn} hideTitle={columnHideTitle} />
                </th>
              )}
              {isColumnVisible('ai') && (
                <th className="px-2 py-3 w-28 min-w-[7rem] whitespace-nowrap">
                  <ColumnHeader label={t('bidding.ai.column') || 'AI'} columnId="ai" onHide={hideColumn} hideTitle={columnHideTitle} />
                </th>
              )}
              {isColumnVisible('timecodeIn') && (
                <th className="px-2 py-3 w-32 min-w-[8rem] whitespace-nowrap">
                  <ColumnHeader label={t('bidding.timecodeIn')} columnId="timecodeIn" onHide={hideColumn} hideTitle={columnHideTitle} />
                </th>
              )}
              {isColumnVisible('timecodeOut') && (
                <th className="px-2 py-3 w-32 min-w-[8rem] whitespace-nowrap">
                  <ColumnHeader label={t('bidding.timecodeOut')} columnId="timecodeOut" onHide={hideColumn} hideTitle={columnHideTitle} />
                </th>
              )}
              {isColumnVisible('duration') && (
                <th className="px-2 py-3 w-24 min-w-[6rem] whitespace-nowrap">
                  <ColumnHeader label={t('bidding.duration')} columnId="duration" onHide={hideColumn} hideTitle={columnHideTitle} />
                </th>
              )}
              {visibleSpecs.map((spec) => {
                const index = specializations.indexOf(spec);
                const bgClass = index % 2 === 0 ? 'bg-gray-800/30' : 'bg-gray-800/50';
                return (
                  <React.Fragment key={spec}>
                    <th className={`px-2 py-3 w-32 min-w-[8rem] whitespace-nowrap ${bgClass} border-l border-gray-700/50`}>
                      <ColumnHeader
                        label={spec}
                        columnId={specColumnId(spec)}
                        onHide={hideColumn}
                        hideTitle={columnHideTitle}
                        onContextMenu={(e) => {
                          if (activeTable !== 'revenue') return;
                          e.preventDefault();
                          setColumnContextMenu({ x: e.clientX, y: e.clientY, spec });
                        }}
                      />
                    </th>
                    <th className={`px-2 py-3 w-20 min-w-[5rem] whitespace-nowrap ${bgClass} border-r border-gray-700/50`}>
                      {valueColumnMode === 'sum' ? t('bidding.sum') : t('bidding.hours')}
                    </th>
                  </React.Fragment>
                );
              })}
              {isColumnVisible('total') && (
                <th className="px-2 py-3 w-24 min-w-[6rem] whitespace-nowrap">
                  <ColumnHeader label={t('bidding.total')} columnId="total" onHide={hideColumn} hideTitle={columnHideTitle} />
                </th>
              )}
              <th className="px-2 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {paginatedShots.map((shot) => (
              <tr 
                key={shot.id} 
                className={`border-b transition-all relative ${
                  draggedShotId === shot.id 
                    ? 'opacity-40 bg-indigo-900/50 scale-[0.98]' 
                    : ''
                } ${
                  dragOverShotId === shot.id && draggedShotId !== shot.id
                    ? 'border-t-4 border-t-indigo-500 bg-indigo-900/20 shadow-lg ring-2 ring-indigo-500/50' 
                    : 'border-gray-700'
                } ${
                  selectedShots.has(shot.id) && isDeleteMode 
                    ? 'bg-indigo-900/20' 
                    : 'hover:bg-gray-800/30'
                }`}
                style={{
                  marginBottom: dragOverShotId === shot.id && draggedShotId !== shot.id ? '12px' : '0',
                  marginTop: dragOverShotId === shot.id && draggedShotId !== shot.id ? '12px' : '0',
                  paddingTop: dragOverShotId === shot.id && draggedShotId !== shot.id ? '8px' : '0',
                  paddingBottom: dragOverShotId === shot.id && draggedShotId !== shot.id ? '8px' : '0',
                }}
                onDragOver={!isDeleteMode ? (e) => handleDragOver(e, shot.id) : undefined}
                onDrop={(e) => !isDeleteMode && handleDrop(e, shot.id)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setContextMenu({ x: e.clientX, y: e.clientY, shotId: shot.id });
                }}
              >
                {(isDeleteMode || (activeTable === 'expense' && canViewExpense)) && (
                  <td className="px-2 py-3">
                    <input
                      type="checkbox"
                      checked={selectedShots.has(shot.id)}
                      onChange={() => handleToggleSelectShot(shot.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500 cursor-pointer"
                    />
                  </td>
                )}
                {showStandaloneDragCol && (
                  <td
                    className="p-0 text-center align-middle whitespace-nowrap"
                    style={{ width: 24, minWidth: 24, maxWidth: 24 }}
                  >
                    <div
                      draggable
                      onDragStart={(e) => handleDragStart(e, shot.id)}
                      onDragEnd={handleDragEnd}
                      className="inline-flex items-center justify-center w-4 h-5 mx-auto flex-shrink-0 rounded-sm cursor-grab active:cursor-grabbing bg-gray-700/80 hover:bg-gray-600 text-gray-400 hover:text-gray-200 transition-colors"
                      title="Drag to reorder"
                    >
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 16 16">
                        <circle cx="5" cy="3" r="1.5"/>
                        <circle cx="5" cy="8" r="1.5"/>
                        <circle cx="5" cy="13" r="1.5"/>
                        <circle cx="11" cy="3" r="1.5"/>
                        <circle cx="11" cy="8" r="1.5"/>
                        <circle cx="11" cy="13" r="1.5"/>
                      </svg>
                    </div>
                  </td>
                )}
                {isColumnVisible('number') && (
                <td className="w-0 px-1 py-3 text-center text-gray-400 tabular-nums whitespace-nowrap">
                  <div className="inline-flex items-center justify-center gap-2">
                    {showDragHandle && (
                      <div
                        draggable
                        onDragStart={(e) => handleDragStart(e, shot.id)}
                        onDragEnd={handleDragEnd}
                        className="inline-flex items-center justify-center w-4 h-5 flex-shrink-0 rounded-sm cursor-grab active:cursor-grabbing bg-gray-700/80 hover:bg-gray-600 text-gray-400 hover:text-gray-200 transition-colors"
                        title="Drag to reorder"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 16 16">
                          <circle cx="5" cy="3" r="1.5"/>
                          <circle cx="5" cy="8" r="1.5"/>
                          <circle cx="5" cy="13" r="1.5"/>
                          <circle cx="11" cy="3" r="1.5"/>
                          <circle cx="11" cy="8" r="1.5"/>
                          <circle cx="11" cy="13" r="1.5"/>
                        </svg>
                      </div>
                    )}
                    <span>{shot.shotNumber}</span>
                  </div>
                </td>
                )}
                {isColumnVisible('thumbnail') && (
                <td className="px-2 py-3">
                  <div className="flex items-center gap-2">
                    {/* Annotate button - changes appearance if comments exist */}
                    <button
                      onClick={() => openAnnotationModal(shot)}
                      className={`p-1.5 rounded transition flex-shrink-0 relative ${
                        recentlyUpdated.shotFields.get(shot.id)?.has('commentsCount') && shot.commentsCount && shot.commentsCount > 0
                          ? 'ring-2 ring-yellow-500/50'
                          : ''
                      } ${
                        shot.commentsCount && shot.commentsCount > 0
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-indigo-600 hover:bg-indigo-700'
                      }`}
                      title={shot.commentsCount && shot.commentsCount > 0 
                        ? `${shot.commentsCount} comment${shot.commentsCount > 1 ? 's' : ''}`
                        : 'Annotate shot'}
                    >
                      {shot.commentsCount && shot.commentsCount > 0 ? (
                        <>
                          <ICONS.MessageSquare className="w-3.5 h-3.5 text-white" />
                          {/* Comment count badge */}
                          <span className="absolute -top-1 -right-1 bg-yellow-500 text-xs font-bold text-black rounded-full w-4 h-4 flex items-center justify-center">
                            {shot.commentsCount}
                          </span>
                        </>
                      ) : (
                        <ICONS.Edit className="w-3.5 h-3.5 text-white" />
                      )}
                    </button>
                    
                    {/* Thumbnail */}
                    <div className="relative group">
                      <ShotThumbnailUpload
                        shotId={shot.id}
                        currentThumbnailUrl={shot.thumbnailUrl}
                        onThumbnailUpdate={(thumbnailUrl) => {
                          setShots(shots.map(s => s.id === shot.id ? { ...s, thumbnailUrl } : s));
                        }}
                        onClick={() => {
                          if (videoUrl && shot.thumbnailUrl) {
                            setVideoPlayerShot(shot);
                          }
                        }}
                      />
                      {videoUrl && shot.thumbnailUrl && (
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          <div className="bg-black/60 rounded-full p-2">
                            <ICONS.Play className="w-4 h-4 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                )}
                {isColumnVisible('shotName') && (
                <td className="px-2 py-3">
                  <input
                    type="text"
                    value={shot.shotName || ''}
                    onChange={(e) => handleShotNameChange(shot.id, e.target.value)}
                    onFocus={() => {
                      const editKey = `${shot.id}:shotName`;
                      activelyEditingRef.current.add(editKey);
                    }}
                    onBlur={() => {
                      const editKey = `${shot.id}:shotName`;
                      activelyEditingRef.current.delete(editKey);
                      // Trigger immediate save if there's a pending timeout
                      if (shotNameTimeouts.current[shot.id]) {
                        const timeoutId = shotNameTimeouts.current[shot.id];
                        clearTimeout(timeoutId);
                        delete shotNameTimeouts.current[shot.id];
                        // Save immediately on blur
                        handleShotNameSave(shot.id);
                      }
                    }}
                    placeholder={t('bidding.shotNamePlaceholder') || 'e.g., SHOT_0010'}
                    autoComplete="off"
                    className={`w-full px-2 py-1 bg-gray-700 border rounded text-white text-xs focus:outline-none focus:border-indigo-500 transition-all ${
                      recentlyUpdated.shotFields.get(shot.id)?.has('shotName') 
                        ? 'border-yellow-500 ring-2 ring-yellow-500/50' 
                        : 'border-gray-600'
                    }`}
                  />
                </td>
                )}
                {isColumnVisible('ai') && (
                <td className="px-2 py-3 align-top">
                  <div className="space-y-1 min-w-[6.5rem]">
                    {aiAnalysesByShot[shot.id] ? (
                      <div
                        className="text-[10px] text-teal-300 leading-tight"
                        title={aiAnalysesByShot[shot.id].rationale || ''}
                      >
                        <span className="font-semibold">
                          {aiAnalysesByShot[shot.id].shotSize || '?'}
                        </span>
                        <span className="text-gray-500"> · </span>
                        <span>C{aiAnalysesByShot[shot.id].complexityScore ?? '-'}</span>
                        {(aiAnalysesByShot[shot.id].vfxSignals || []).length > 0 && (
                          <div className="text-gray-400 truncate max-w-[7rem]">
                            {(aiAnalysesByShot[shot.id].vfxSignals || []).slice(0, 2).join(', ')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-[10px] text-gray-600">—</div>
                    )}
                    <input
                      type="text"
                      defaultValue={shot.description || ''}
                      key={`desc-${shot.id}-${shot.description || ''}`}
                      onBlur={async (e) => {
                        const next = e.target.value;
                        if ((shot.description || '') === next) return;
                        const token = localStorage.getItem('spellbook_token');
                        try {
                          const res = await fetch(`${API_CONFIG.API_URL}/bidding/shots/${shot.id}`, {
                            method: 'PUT',
                            headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({ description: next || null }),
                          });
                          if (res.ok) {
                            setShots((prev) =>
                              prev.map((s) =>
                                s.id === shot.id ? { ...s, description: next || null } : s
                              )
                            );
                          }
                        } catch (err) {
                          console.error('Failed to save description', err);
                        }
                      }}
                      placeholder={t('bidding.ai.descriptionPlaceholder') || 'VFX notes…'}
                      className="w-full px-1.5 py-0.5 bg-gray-800 border border-gray-700 rounded text-gray-300 text-[10px] focus:outline-none focus:border-teal-600"
                      title={t('bidding.ai.description') || 'Description'}
                    />
                  </div>
                </td>
                )}
                {isColumnVisible('timecodeIn') && (
                <td className="px-2 py-3 text-center">
                  <TimecodeInput
                    value={shot.timecodeInSeconds}
                    onChange={(seconds) => handleTimecodeChange(shot.id, 'timecodeIn', seconds)}
                    onFocus={() => {
                      const editKey = `${shot.id}:timecodeIn`;
                      activelyEditingRef.current.add(editKey);
                    }}
                    onBlur={() => {
                      const editKey = `${shot.id}:timecodeIn`;
                      activelyEditingRef.current.delete(editKey);
                      // Trigger immediate save if there's a pending timeout
                      const timeoutKey = `${shot.id}:timecodeIn`;
                      if (timecodeTimeoutsRef.current.has(timeoutKey)) {
                        const timeoutId = timecodeTimeoutsRef.current.get(timeoutKey)!;
                        clearTimeout(timeoutId);
                        timecodeTimeoutsRef.current.delete(timeoutKey);
                        // Save immediately on blur using stored value
                        const token = localStorage.getItem('spellbook_token');
                        const seconds = timecodeValuesRef.current.get(timeoutKey) ?? shot.timecodeInSeconds ?? 0;
                        fetch(`${API_CONFIG.API_URL}/bidding/shots/${shot.id}`, {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({ timecodeInSeconds: seconds })
                        }).then(response => {
                          if (response.ok) {
                            setLastUpdateTime(Date.now());
                            timecodeValuesRef.current.delete(timeoutKey);
                            scheduleThumbnailCapture(shot.id, seconds);
                          }
                        }).catch(error => {
                          console.error('Error updating timecode:', error);
                          fetchData();
                        });
                      }
                    }}
                    fps={24}
                    className={`w-full px-2 py-1 bg-gray-700 border rounded text-white text-xs font-mono text-center focus:outline-none focus:border-indigo-500 transition-all ${
                      recentlyUpdated.shotFields.get(shot.id)?.has('timecodeIn') 
                        ? 'border-yellow-500 ring-2 ring-yellow-500/50' 
                        : 'border-gray-600'
                    }`}
                    placeholder="00:00:00:00"
                  />
                </td>
                )}
                {isColumnVisible('timecodeOut') && (
                <td className="px-2 py-3 text-center">
                  <TimecodeInput
                    value={shot.timecodeOutSeconds}
                    onChange={(seconds) => handleTimecodeChange(shot.id, 'timecodeOut', seconds)}
                    onFocus={() => {
                      const editKey = `${shot.id}:timecodeOut`;
                      activelyEditingRef.current.add(editKey);
                    }}
                    onBlur={() => {
                      const editKey = `${shot.id}:timecodeOut`;
                      activelyEditingRef.current.delete(editKey);
                      // Trigger immediate save if there's a pending timeout
                      const timeoutKey = `${shot.id}:timecodeOut`;
                      if (timecodeTimeoutsRef.current.has(timeoutKey)) {
                        const timeoutId = timecodeTimeoutsRef.current.get(timeoutKey)!;
                        clearTimeout(timeoutId);
                        timecodeTimeoutsRef.current.delete(timeoutKey);
                        // Save immediately on blur using stored value
                        const token = localStorage.getItem('spellbook_token');
                        const seconds = timecodeValuesRef.current.get(timeoutKey) ?? shot.timecodeOutSeconds ?? 0;
                        fetch(`${API_CONFIG.API_URL}/bidding/shots/${shot.id}`, {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({ timecodeOutSeconds: seconds })
                        }).then(response => {
                          if (response.ok) {
                            setLastUpdateTime(Date.now());
                            timecodeValuesRef.current.delete(timeoutKey);
                          }
                        }).catch(error => {
                          console.error('Error updating timecode:', error);
                          fetchData();
                        });
                      }
                    }}
                    fps={24}
                    className={`w-full px-2 py-1 bg-gray-700 border rounded text-white text-xs font-mono text-center focus:outline-none focus:border-indigo-500 transition-all ${
                      recentlyUpdated.shotFields.get(shot.id)?.has('timecodeOut') 
                        ? 'border-yellow-500 ring-2 ring-yellow-500/50' 
                        : 'border-gray-600'
                    }`}
                    placeholder="00:00:00:00"
                  />
                </td>
                )}
                {isColumnVisible('duration') && (
                <td className="px-2 py-3 text-center">
                  <input
                    type="number"
                    min="1"
                    value={shot.durationFrames || 1}
                    onChange={(e) => handleDurationChange(shot.id, parseInt(e.target.value) || 1)}
                    onFocus={() => {
                      const editKey = `${shot.id}:durationFrames`;
                      activelyEditingRef.current.add(editKey);
                    }}
                    onBlur={() => {
                      const editKey = `${shot.id}:durationFrames`;
                      activelyEditingRef.current.delete(editKey);
                      // Trigger immediate save if there's a pending timeout
                      const timeoutKey = `${shot.id}:durationFrames`;
                      if (durationTimeoutsRef.current.has(timeoutKey)) {
                        const timeoutId = durationTimeoutsRef.current.get(timeoutKey)!;
                        clearTimeout(timeoutId);
                        durationTimeoutsRef.current.delete(timeoutKey);
                        // Save immediately on blur using stored value
                        const token = localStorage.getItem('spellbook_token');
                        const value = durationValuesRef.current.get(shot.id) ?? shot.durationFrames ?? 1;
                        fetch(`${API_CONFIG.API_URL}/bidding/shots/${shot.id}`, {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({ durationFrames: value })
                        }).then(response => {
                          if (response.ok) {
                            setLastUpdateTime(Date.now());
                            durationValuesRef.current.delete(shot.id);
                          }
                        }).catch(error => {
                          console.error('Error updating duration:', error);
                          fetchData();
                        });
                      }
                    }}
                    className={`w-full px-2 py-1 bg-gray-700 border rounded text-white text-sm text-center focus:outline-none focus:border-indigo-500 transition-all ${
                      recentlyUpdated.shotFields.get(shot.id)?.has('durationFrames') 
                        ? 'border-yellow-500 ring-2 ring-yellow-500/50' 
                        : 'border-gray-600'
                    }`}
                  />
                </td>
                )}
                {visibleSpecs.map((spec) => {
                  const index = specializations.indexOf(spec);
                  const assignment = getAssignment(shot.id, spec);
                  const linkStatus = getCellLinkStatus(shot.id, spec);
                  
                  // In expense table, show only real artists (ignore virtual artist data)
                  const artistIdToUse = activeTable === 'expense' ? assignment?.realArtistId : assignment?.artistId;
                  const selectedArtist = artistIdToUse
                    ? artists.find(a => a.id === artistIdToUse)
                    : null;
                  const selectedColor = selectedArtist ? artistColors[selectedArtist.id] : null;
                  const bgClass = index % 2 === 0 ? 'bg-gray-800/30' : 'bg-gray-800/50';
                  
                  // Check if this expense assignment is paid (works for both tables)
                  const expenseAssignment = expenseAssignments.find(ea => ea.shotId === shot.id && ea.specialization === spec);
                  const isPaid = expenseAssignment?.isPaid === true;
                  
                  // Add highlight classes based on link status or paid status
                  let highlightClass = '';
                  if (isPaid) {
                    highlightClass = 'bg-green-900/50 border-green-500/30'; // Green for paid assignments (both tables)
                  } else if (linkStatus === 'linked') {
                    highlightClass = 'bg-yellow-900/20 border-yellow-500/50'; // Yellow for linked cells
                  } else if (linkStatus === 'missing-revenue' || linkStatus === 'missing-expense') {
                    highlightClass = 'bg-orange-900/20 border-orange-500/50'; // Orange for missing link
                  }
                  
                  return (
                    <React.Fragment key={spec}>
                      <td className={`px-2 py-3 ${bgClass} ${highlightClass} border-l border-gray-700/50 transition-colors`}>
                        <div className="relative flex items-center gap-1">
                          <div 
                            className={`w-3 h-3 rounded-full flex-shrink-0 border-2 transition-all ${
                              selectedArtist ? 'cursor-grab active:cursor-grabbing' : ''
                            } ${
                              draggedAssignment?.shotId === shot.id && draggedAssignment?.spec === spec 
                                ? 'opacity-50 scale-110' 
                                : ''
                            }`}
                            style={{
                              backgroundColor: selectedColor ? selectedColor.text : 'rgb(55, 65, 81)',
                              borderColor: selectedColor ? selectedColor.border : 'rgb(75, 85, 99)',
                              boxShadow: selectedColor ? `0 0 8px ${selectedColor.border}` : 'none'
                            }}
                            title={selectedArtist?.name || (assignment as any)?.artistLevel || (assignment?.isVirtualArtist && assignment?.virtualArtistType ? `${assignment.virtualArtistType.charAt(0).toUpperCase() + assignment.virtualArtistType.slice(1)} Artist` : t('common.notAssigned'))}
                            draggable={!!selectedArtist}
                            onDragStart={(e) => selectedArtist && handleBadgeDragStart(e, shot.id, spec, selectedArtist.id)}
                            onDragOver={handleBadgeDragOver}
                            onDrop={(e) => handleBadgeDrop(e, shot.id, spec)}
                            onDragEnd={handleBadgeDragEnd}
                          />
                          {activeTable === 'expense' ? (
                            // In expense table, show artist selector (no virtual artists)
                            // Highlight if revenue has virtual artist but expense doesn't have real artist
                            <>
                          <select
                            value={assignment?.artistId || ''}
                            onChange={(e) => handleUpdateAssignment(shot.id, spec, 'artistId', e.target.value)}
                            className={`flex-1 px-2 py-1 border rounded text-xs font-medium transition-all ${
                              recentlyUpdated.assignmentFields.get(assignment?.id || '')?.has('artistId') 
                                ? 'ring-2 ring-yellow-500/50 border-yellow-500'
                                : assignment?.isVirtualArtist && !assignment?.artistId
                                ? 'ring-2 ring-yellow-400/50 border-yellow-400 bg-yellow-900/20'
                                : ''
                            } ${
                              selectedColor 
                                ? `${selectedColor.class}` 
                                : 'bg-gray-800 border-gray-700 text-white'
                            }`}
                          >
                            <option value="">
                              {(assignment as any)?.artistLevel || t('common.notAssigned')}
                            </option>
                            {artists.filter(a => 
                              (!a.name.includes('Compositor Mid') && 
                              !a.name.includes('Compositor Lead') && 
                              !a.name.includes('MattePainter') && 
                              !a.name.includes('Roto Artist') && 
                              !a.name.includes('CG Generalist') &&
                              !a.name.includes('Junior Artist') &&
                              !a.name.includes('Mid Artist') &&
                              !a.name.includes('Senior Artist') &&
                              !a.name.includes('Lead Artist')) &&
                              // Show only if assigned to project OR is the currently selected artist
                              (a.assignedProjects?.includes(projectId || '') || a.id === assignment?.artistId)
                            ).map(artist => {
                              const color = artistColors[artist.id];
                              return (
                                <option 
                                  key={artist.id} 
                                  value={artist.id}
                                  className={color ? color.class : ''}
                                >
                                  {artist.name}
                                </option>
                              );
                            })}
                          </select>
                          {/* Yellow indicator light for expense when revenue has assignment */}
                          {assignment?.isVirtualArtist && !assignment?.artistId && (
                            <div 
                              className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse flex-shrink-0" 
                              title="Требуется назначение в соответствии с приходной"
                            />
                          )}
                          {/* TODO: Status indicators will be fetched from MagicBox API in future */}
                            </>
                          ) : (
                            // In revenue table, show only virtual artists selector with color coding
                            <>
                            <select
                              value={assignment?.artistId || ''}
                              onChange={(e) => handleUpdateAssignment(shot.id, spec, 'artistId', e.target.value)}
                              className={`flex-1 px-2 py-1 border rounded text-xs font-medium transition-all ${
                                recentlyUpdated.assignmentFields.get(assignment?.id || '')?.has('artistId') 
                                  ? 'ring-2 ring-yellow-500/50 border-yellow-500'
                                  : ''
                              } bg-gray-800 border-gray-700 text-white`}
                            >
                              <option value="">
                                {t('common.notAssigned')}
                              </option>
                              {artists.filter(a => 
                                a.name === 'Junior Artist' || 
                                a.name === 'Mid Artist' || 
                                a.name === 'Senior Artist' || 
                                a.name === 'Lead Artist' ||
                                a.name.includes('(Virtual)')
                              ).map(artist => (
                                <option key={artist.id} value={artist.id}>
                                  {artist.name.replace(' (Virtual)', '')}
                                </option>
                              ))}
                            </select>
                            {/* Green indicator light when virtual role is selected */}
                            {assignment?.isVirtualArtist && assignment?.virtualArtistType && (
                              <div 
                                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                  assignment.virtualArtistType === 'junior' ? 'bg-green-400' :
                                  assignment.virtualArtistType === 'mid' ? 'bg-blue-400' :
                                  assignment.virtualArtistType === 'senior' ? 'bg-orange-400' :
                                  'bg-purple-400'
                                }`}
                                title={`${assignment.virtualArtistType} artist assigned`}
                              />
                            )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className={`px-2 py-3 ${bgClass} ${highlightClass} border-r border-gray-700/50 transition-colors`}>
                        {activeTable === 'expense' ? (
                          <div className="space-y-1">
                            {valueColumnMode === 'sum' ? (
                              // Fixed amount input
                              <input
                                type="number"
                                min="0"
                                step="100"
                                value={(() => {
                                  const currentValue = assignment?.fixedAmount ?? 0;
                                  return currentValue === 0 ? '' : Math.round(currentValue);
                                })()}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (inputValue === '') {
                                    handleFixedAmountInput(shot.id, spec, assignment, 0);
                                    return;
                                  }
                                  const numValue = parseFloat(inputValue);
                                  if (!isNaN(numValue) && numValue >= 0) {
                                    handleFixedAmountInput(shot.id, spec, assignment, numValue);
                                  }
                                }}
                                onFocus={() => {
                                  const editKey = `${shot.id}:${spec}:fixedAmount`;
                                  activelyEditingRef.current.add(editKey);
                                }}
                                onBlur={() => {
                                  const editKey = `${shot.id}:${spec}:fixedAmount`;
                                  activelyEditingRef.current.delete(editKey);
                                }}
                                onPaste={(e) => handleValueColumnPaste(e, shot.id, spec)}
                                className={`w-full px-2 py-1 bg-purple-900/30 border rounded text-white text-xs transition-all ${
                                  recentlyUpdated.assignmentFields.get(assignment?.id || '')?.has('fixedAmount') 
                                    ? 'ring-2 ring-yellow-500/50 border-yellow-500'
                                    : 'border-purple-500/50'
                                }`}
                                placeholder={t('bidding.sum')}
                              />
                            ) : (
                              // Hours input
                              <input
                                type="number"
                                min="0"
                                step="0.1"
                                value={(() => {
                                  const currentValue = assignment?.realEstimatedHours ?? 0;
                                  return currentValue === 0 ? '' : Math.round(currentValue * 10) / 10;
                                })()}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (inputValue === '') {
                                    handleUpdateAssignment(shot.id, spec, 'realEstimatedHours', 0);
                                    return;
                                  }
                                  const numValue = parseFloat(inputValue);
                                  if (!isNaN(numValue) && numValue >= 0) {
                                    handleUpdateAssignment(shot.id, spec, 'realEstimatedHours', numValue);
                                  }
                                }}
                                onFocus={() => {
                                  const editKey = `${shot.id}:${spec}:realEstimatedHours`;
                                  activelyEditingRef.current.add(editKey);
                                }}
                                onBlur={() => {
                                  const editKey = `${shot.id}:${spec}:realEstimatedHours`;
                                  activelyEditingRef.current.delete(editKey);
                                  
                                  const timeoutKey = `${shot.id}:${spec}:exp`;
                                  if (estimatedHoursTimeoutsRef.current.has(timeoutKey)) {
                                    const timeoutId = estimatedHoursTimeoutsRef.current.get(timeoutKey)!;
                                    clearTimeout(timeoutId);
                                    estimatedHoursTimeoutsRef.current.delete(timeoutKey);
                                    if (assignment?.id) {
                                      const valueToSave = assignment.realEstimatedHours ?? 0;
                                      const normalizedValue = Math.round(valueToSave * 10) / 10;
                                      performAssignmentUpdate(assignment, 'estimatedHours', normalizedValue, shot.id, spec);
                                    }
                                  }
                                }}
                                onPaste={(e) => handleValueColumnPaste(e, shot.id, spec)}
                                className={`w-full px-2 py-1 bg-gray-800 border rounded text-white text-xs transition-all ${
                                  recentlyUpdated.assignmentFields.get(assignment?.id || '')?.has('realEstimatedHours') 
                                    ? 'ring-2 ring-yellow-500/50 border-yellow-500'
                                    : 'border-gray-700'
                                }`}
                                placeholder="0"
                                disabled={!assignment?.realArtistId}
                              />
                            )}
                          </div>
                        ) : (
                          <div className="space-y-1">
                            {valueColumnMode === 'sum' ? (
                              // Fixed amount input for revenue
                              <input
                                type="number"
                                min="0"
                                step="100"
                                value={(() => {
                                  const currentValue = assignment?.fixedAmount ?? 0;
                                  return currentValue === 0 ? '' : Math.round(currentValue);
                                })()}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (inputValue === '') {
                                    handleFixedAmountInput(shot.id, spec, assignment, 0);
                                    return;
                                  }
                                  const numValue = parseFloat(inputValue);
                                  if (!isNaN(numValue) && numValue >= 0) {
                                    handleFixedAmountInput(shot.id, spec, assignment, numValue);
                                  }
                                }}
                                onFocus={() => {
                                  const editKey = `${shot.id}:${spec}:fixedAmount`;
                                  activelyEditingRef.current.add(editKey);
                                }}
                                onBlur={() => {
                                  const editKey = `${shot.id}:${spec}:fixedAmount`;
                                  activelyEditingRef.current.delete(editKey);
                                }}
                                onPaste={(e) => handleValueColumnPaste(e, shot.id, spec)}
                                className={`w-full px-2 py-1 bg-purple-900/30 border rounded text-white text-xs transition-all ${
                                  recentlyUpdated.assignmentFields.get(assignment?.id || '')?.has('fixedAmount') 
                                    ? 'ring-2 ring-yellow-500/50 border-yellow-500'
                                    : 'border-purple-500/50'
                                }`}
                                placeholder={t('bidding.sum')}
                              />
                            ) : (
                              <input
                                type="number"
                                min="0"
                                step="0.1"
                                value={(() => {
                                  const currentValue = assignment?.estimatedHours ?? 0;
                                  return currentValue === 0 ? '' : Math.round(currentValue * 10) / 10;
                                })()}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (inputValue === '') {
                                    handleUpdateAssignment(shot.id, spec, 'estimatedHours', 0);
                                    return;
                                  }
                                  const numValue = parseFloat(inputValue);
                                  if (!isNaN(numValue) && numValue >= 0) {
                                    handleUpdateAssignment(shot.id, spec, 'estimatedHours', numValue);
                                  }
                                }}
                                onFocus={() => {
                                  const editKey = `${shot.id}:${spec}:estimatedHours`;
                                  activelyEditingRef.current.add(editKey);
                                }}
                                onBlur={() => {
                                  const editKey = `${shot.id}:${spec}:estimatedHours`;
                                  activelyEditingRef.current.delete(editKey);
                                  
                                  const timeoutKey = `${shot.id}:${spec}`;
                                  if (estimatedHoursTimeoutsRef.current.has(timeoutKey)) {
                                    const timeoutId = estimatedHoursTimeoutsRef.current.get(timeoutKey)!;
                                    clearTimeout(timeoutId);
                                    estimatedHoursTimeoutsRef.current.delete(timeoutKey);
                                    if (assignment?.id) {
                                      const changeKey = `${shot.id}:${spec}`;
                                      const valueToSave = estimatedHoursChanges[changeKey] ?? assignment.estimatedHours ?? 0;
                                      const normalizedValue = Math.round(valueToSave * 10) / 10;
                                      performAssignmentUpdate(assignment, 'estimatedHours', normalizedValue, shot.id, spec);
                                    }
                                  }
                                }}
                                onPaste={(e) => handleValueColumnPaste(e, shot.id, spec)}
                                className={`w-full px-2 py-1 bg-gray-800 border rounded text-white text-xs transition-all ${
                                  recentlyUpdated.assignmentFields.get(assignment?.id || '')?.has('estimatedHours') 
                                    ? 'ring-2 ring-yellow-500/50 border-yellow-500'
                                    : 'border-gray-700'
                                }`}
                                placeholder="0"
                                disabled={!assignment?.artistId && !assignment?.isVirtualArtist}
                              />
                            )}
                          </div>
                        )}
                      </td>
                    </React.Fragment>
                  );
                })}
                {isColumnVisible('total') && (
                <td className="px-2 py-3 text-right text-white font-medium">
                  {getCurrencySymbol(currency)}{(Number(calculateShotTotal(shot.id)) || 0).toFixed(2)}
                </td>
                )}
                <td className="px-2 py-3">
                  {!isDeleteMode && (
                    <button
                      onClick={() => handleDeleteSingleShot(shot.id)}
                      className="text-red-400 hover:text-red-300"
                      title={t('common.delete')}
                    >
                      <ICONS.Trash className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          {shots.length > 0 && (
            <tfoot className="bg-gray-800/70 border-t-2 border-indigo-500/30 text-sm">
              <tr>
                <td className="px-2 py-2 align-middle whitespace-nowrap">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-gray-300 font-semibold text-xs uppercase tracking-wide">
                      {t('bidding.totalsRow')}
                    </span>
                    <div className="inline-flex bg-gray-800 rounded-lg p-0.5 text-[11px]">
                      <button
                        type="button"
                        onClick={() => handleAggregationModeChange('specialization')}
                        className={`px-2 py-1 rounded-md font-medium transition-colors ${
                          aggregationMode === 'specialization'
                            ? 'bg-indigo-600 text-white'
                            : 'text-gray-400 hover:text-gray-200'
                        }`}
                      >
                        {t('bidding.aggregationBySpec')}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAggregationModeChange('level')}
                        className={`px-2 py-1 rounded-md font-medium transition-colors ${
                          aggregationMode === 'level'
                            ? 'bg-indigo-600 text-white'
                            : 'text-gray-400 hover:text-gray-200'
                        }`}
                      >
                        {t('bidding.aggregationByLevel')}
                      </button>
                    </div>
                  </div>
                </td>
                {footerLeadingColCount > 1 && (
                  <td colSpan={footerLeadingColCount - 1} />
                )}
                {aggregationMode === 'specialization' ? (
                  visibleSpecs.map((spec) => {
                    const index = specializations.indexOf(spec);
                    const totals = specTotals[spec] || { hours: 0, amount: 0 };
                    const bgClass = index % 2 === 0 ? 'bg-gray-800/30' : 'bg-gray-800/50';
                    return (
                      <React.Fragment key={spec}>
                        <td className={`px-2 py-2 text-right text-white font-semibold ${bgClass} border-l border-gray-700/50`}>
                          {getCurrencySymbol(currency)}{(Number(totals.amount) || 0).toFixed(2)}
                        </td>
                        <td className={`px-2 py-2 text-right text-indigo-300 font-semibold ${bgClass} border-r border-gray-700/50`}>
                          {(Number(totals.hours) || 0).toFixed(1)}h
                        </td>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <td colSpan={visibleSpecs.length * 2} className="px-3 py-2 border-l border-r border-gray-700/50">
                    <div className="flex flex-wrap gap-3">
                      {visibleLevels.length > 0 ? (
                        visibleLevels.map((level) => (
                          <div
                            key={level}
                            className="flex items-center gap-2 px-2 py-1 rounded bg-gray-900/40 border border-gray-700/50"
                          >
                            <span className="text-gray-300 font-medium">{getLevelLabel(level)}</span>
                            <span className="text-indigo-300 font-semibold">
                              {(Number(levelTotals[level].hours) || 0).toFixed(1)}h
                            </span>
                            <span className="text-white font-semibold">
                              {getCurrencySymbol(currency)}{(Number(levelTotals[level].amount) || 0).toFixed(2)}
                            </span>
                          </div>
                        ))
                      ) : (
                        <span className="text-gray-500">{t('bidding.noAssignments')}</span>
                      )}
                    </div>
                  </td>
                )}
                {isColumnVisible('total') && (
                <td className="px-2 py-2 text-right text-white font-bold">
                  {getCurrencySymbol(currency)}{(Number(calculateProjectTotal()) || 0).toFixed(2)}
                </td>
                )}
                <td />
              </tr>
            </tfoot>
          )}
        </table>

      {/* Pagination — scrolls with table */}
      {shots.length > 0 && (
        <div className="flex items-center justify-between py-3 px-4 mt-2 bg-gray-800/30 rounded-lg">
          <div className="text-sm text-gray-400">
            {t('bidding.showing')} {startIndex + 1}-{Math.min(endIndex, shots.length)} {t('bidding.of')} {shots.length} {t('bidding.shots')}
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-400 whitespace-nowrap" htmlFor="shots-per-page">
              {t('bidding.perPage')}
            </label>
            <input
              id="shots-per-page"
              type="number"
              min={1}
              max={200}
              value={shotsPerPageInput}
              onChange={(e) => handleShotsPerPageChange(e.target.value)}
              onBlur={commitShotsPerPage}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitShotsPerPage();
                  (e.target as HTMLInputElement).blur();
                }
              }}
              className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-indigo-500"
            />
            {totalPages > 1 && (
              <>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  ←
                </button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 rounded transition-colors ${
                        page === currentPage
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  →
                </button>
              </>
            )}
          </div>
        </div>
      )}
      </div>

      {/* Artist payments + summary — outside table scroll (~33% smaller body; ИТОГО stays prominent) */}
      <div className="shrink-0 space-y-3">
      {/* Artist Payments */}
      {shots.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-2">
          {/* Participant Payments */}
          <div className="bg-gray-800/50 rounded-lg p-2.5 border border-gray-700 text-[11px] leading-snug">
            <h3 className="text-[13px] font-semibold text-white mb-2 flex items-center">
              <ICONS.User className="w-3.5 h-3.5 mr-1.5" />
              {t('bidding.participantPayments')}
            </h3>
            <div className="space-y-1.5 max-h-44 overflow-y-auto">
              {calculateArtistPayments().map((payment, index) => (
                <div key={index} className="py-1.5 px-2 bg-gray-900/30 rounded hover:bg-gray-900/50 transition-colors">
                  <div className="flex items-center justify-between mb-0.5">
                    <button
                      onClick={() => handleOpenParticipantDetails(payment.artistId, payment.name, payment.color)}
                      className="flex items-center space-x-1.5 hover:opacity-80 transition-opacity cursor-pointer"
                    >
                      <div className={`w-2.5 h-2.5 rounded-full ${payment.color}`} />
                      <span className="text-gray-300 font-medium underline decoration-dotted">{payment.name}</span>
                    </button>
                    <span className="text-white font-semibold">
                      {getCurrencySymbol(currency)}{(Number(payment.total) || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-end space-x-3 text-[10px] text-gray-500 ml-4">
                    <span>
                      {payment.shotCount} {payment.shotCount === 1 ? t('bidding.shotUnit') || 'shot' : t('bidding.shotsUnit') || 'shots'}
                    </span>
                    <span>•</span>
                    <span>
                      {(Number(payment.hours) || 0).toFixed(1)}h
                    </span>
                  </div>
                </div>
              ))}
              {calculateArtistPayments().length === 0 && (
                <p className="text-gray-500 text-center py-3">{t('bidding.noAssignments')}</p>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-800/50 rounded-lg p-2.5 border border-gray-700 text-[11px] leading-snug">
            <h3 className="text-[13px] font-semibold text-white mb-2 flex items-center">
              <ICONS.CreditCard className="w-3.5 h-3.5 mr-1.5" />
              {t('bidding.summary')}
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">{t('bidding.totalShots')}:</span>
                <span className="text-white font-medium">{shots.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">{t('bidding.totalParticipants')}:</span>
                <span className="text-white font-medium">{calculateArtistPayments().length}</span>
              </div>
              <div className="flex items-center justify-between pt-1.5 border-t border-gray-700">
                <span className="text-gray-300 font-medium">{t('bidding.subtotal')}:</span>
                <span className="text-white font-semibold">
                  {getCurrencySymbol(currency)}{(Number(calculateProjectTotal()) || 0).toFixed(2)}
                </span>
              </div>
              {(() => {
                const projectTotal = Number(calculateProjectTotal()) || 0;
                const producerFeeAmount = useProducerFixedFee
                  ? Number(producerFixedFee) || 0
                  : projectTotal * ((Number(producerPercentage) || 0) / 100);
                const coordinatorFeeAmount = useCoordinatorFixedFee
                  ? Number(coordinatorFee) || 0
                  : projectTotal * ((Number(coordinatorPercentage) || 0) / 100);
                const supervisorFeeAmount = useSupervisorFixedFee
                  ? Number(supervisorFee) || 0
                  : projectTotal * ((Number(supervisorPercentage) || 0) / 100);
                const finderFeeAmount = useFinderFixedFee
                  ? Number(finderFee) || 0
                  : projectTotal * ((Number(finderPercentage) || 0) / 100);
                return (
                  <>
                    {producerFeeAmount > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">
                          {t('bidding.producerFee')}{!useProducerFixedFee && ` (${producerPercentage}%)`}
                          {activeTable === 'expense' && (
                            <span className="text-gray-500 text-[10px] ml-1">({t('bidding.fromRevenue') || 'с прихода'})</span>
                          )}
                        </span>
                        <span className="text-indigo-400 font-medium">
                          {getCurrencySymbol(currency)}{producerFeeAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {coordinatorFeeAmount > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">{t('bidding.coordinatorFee')}{!useCoordinatorFixedFee && ` (${coordinatorPercentage}%)`}:</span>
                        <span className="text-indigo-400 font-medium">
                          {getCurrencySymbol(currency)}{coordinatorFeeAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {supervisorFeeAmount > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">{t('bidding.supervisorFee') || 'Supervisor Fee'}{!useSupervisorFixedFee && ` (${supervisorPercentage}%)`}:</span>
                        <span className="text-indigo-400 font-medium">
                          {getCurrencySymbol(currency)}{supervisorFeeAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {finderFeeAmount > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">{t('bidding.finderFee') || 'Finder Fee'}{!useFinderFixedFee && ` (${finderPercentage}%)`}:</span>
                        <span className="text-indigo-400 font-medium">
                          {getCurrencySymbol(currency)}{finderFeeAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </>
                );
              })()}
              <div className="flex items-center justify-between pt-2 border-t-2 border-indigo-500/50">
                <span className="text-white font-bold text-lg">{vatRate > 0 ? t('bidding.totalNet') || 'Total Net' : t('bidding.grandTotal')}:</span>
                <span className="text-indigo-400 font-bold text-xl">
                  {getCurrencySymbol(currency)}{(Number(calculateGrandTotal()) || 0).toFixed(2)}
                </span>
              </div>
              
              {vatRate > 0 && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">{t('bidding.vat') || 'VAT'} ({vatRate}%):</span>
                    <span className="text-indigo-400 font-medium">
                      {getCurrencySymbol(currency)}{(calculateVatAmount()).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-1.5 border-t border-indigo-500/30">
                    <span className="text-white font-bold text-lg">{t('bidding.totalGross') || 'Total Gross'}:</span>
                    <span className="text-green-400 font-bold text-xl">
                      {getCurrencySymbol(currency)}{(calculateGrossTotal()).toFixed(2)}
                    </span>
                  </div>
                </>
              )}
              {/* Profit Pool - show ONLY in expense table and ONLY for Producer+ roles */}
              {activeTable === 'expense' && canViewExpense && profitPool !== null && (
                <div className="flex items-center justify-between pt-1.5 border-t-2 border-green-500/50">
                  <span className="text-white font-bold text-lg">{t('bidding.profitPool')}:</span>
                  <span className={`font-bold text-xl ${
                    profitPool >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {getCurrencySymbol(currency)}{(Number(profitPool) || 0).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {shots.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <ICONS.Camera className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{t('bidding.noShots')}</p>
        </div>
      )}
      </div>

      {/* Bidding Settings Modal */}
      <BiddingSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        biddingSheetId={biddingSheetId}
        biddingSheet={currentBiddingSheet}
        projectId={projectId}
        currency={currency}
        onSave={(updatedSheet) => {
          setCurrentBiddingSheet(prev => ({ ...prev, ...updatedSheet }));
          setProducerPercentage(Number(updatedSheet.producerPercentage) || 0);
          setProducerFixedFee(Number(updatedSheet.producerFixedFee) || 0);
          setUseProducerFixedFee(updatedSheet.useProducerFixedFee || false);
          setCoordinatorFee(Number(updatedSheet.coordinatorFee) || 0);
          setCoordinatorPercentage(Number(updatedSheet.coordinatorPercentage) || 0);
          setUseCoordinatorFixedFee(updatedSheet.useCoordinatorFixedFee !== false);
          setSupervisorFee(Number(updatedSheet.supervisorFee) || 0);
          setSupervisorPercentage(Number(updatedSheet.supervisorPercentage) || 0);
          setUseSupervisorFixedFee(updatedSheet.useSupervisorFixedFee !== false);
          setFinderFee(Number(updatedSheet.finderFee) || 0);
          setFinderPercentage(Number(updatedSheet.finderPercentage) || 0);
          setUseFinderFixedFee(updatedSheet.useFinderFixedFee !== false);
          setVatRate(Number(updatedSheet.vatRate) || 0);
          setProducerName(updatedSheet.producerName || '');
          setSupervisorName(updatedSheet.supervisorName || '');
          // Force refresh to ensure all derived data and status are synced
          fetchData();
        }}
      />

      <AiBidPanel
        isOpen={isAiBidOpen}
        onClose={() => setIsAiBidOpen(false)}
        biddingSheetId={biddingSheetId}
        projectBudget={currentBiddingSheet.projectBudget ?? null}
        currency={currency}
        valueColumnMode={valueColumnMode}
        allowedSpecializations={
          visibleSpecs.length > 0 ? visibleSpecs : specializations
        }
        onMutated={async () => {
          await fetchData();
          try {
            const token = localStorage.getItem('spellbook_token');
            const res = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/ai/analyses`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              const data = await res.json();
              const map: Record<string, any> = {};
              for (const a of data.analyses || []) {
                map[a.shotId] = a.tags || {};
              }
              setAiAnalysesByShot(map);
            }
          } catch (e) {
            console.error(e);
          }
        }}
      />

      {/* Artist Rates Modal */}
      <ArtistRatesModal
        isOpen={isRatesModalOpen}
        onClose={() => setIsRatesModalOpen(false)}
        projectId={projectId}
        existingSpecializations={specializations}
        activeTable={activeTable}
        onSpecializationsChange={setSpecializations}
        producerPercentage={producerPercentage}
        coordinatorFee={coordinatorFee}
        supervisorFee={supervisorFee}
        vatRate={vatRate}
        onProducerPercentageChange={handleProducerPercentageChange}
        onCoordinatorFeeChange={handleCoordinatorFeeChange}
        onSupervisorFeeChange={handleSupervisorFeeChange}
        onVatRateChange={handleVatRateChange}
        currency={currency}
        producerName={producerName}
        supervisorName={supervisorName}
        onProducerNameChange={setProducerName}
        onSupervisorNameChange={setSupervisorName}
        onRatesSaved={handleRatesSaved}
      />

      <ExportBiddingExcelModal
        isOpen={isExportExcelOpen}
        onClose={() => setIsExportExcelOpen(false)}
        onExport={handleExportCombined}
      />

      <ExportBidVideoModal
        isOpen={isExportVideoOpen}
        onClose={() => setIsExportVideoOpen(false)}
        biddingSheetId={biddingSheetId}
        projectName={biddingSheet.projectName}
        biddingName={currentBiddingSheet?.name || biddingSheet.name}
        bidVersion={currentBiddingSheet?.rowVersion ?? biddingSheet.rowVersion ?? 0}
        shots={shots.map((s) => ({
          id: s.id,
          shotNumber: s.shotNumber,
          shotName: s.shotName,
          videoUrl: s.videoUrl
        }))}
        onShotsRefreshed={(refreshed) => {
          setShots((prev) =>
            prev.map((s) => {
              const match = refreshed.find((r) => r.id === s.id);
              return match?.videoUrl ? { ...s, videoUrl: match.videoUrl || undefined } : s;
            })
          );
        }}
      />

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-2 z-50"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              if (contextMenu.shotId) {
                handleCopyRowValues(contextMenu.shotId);
              }
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-white hover:bg-gray-700 flex items-center space-x-2"
          >
            <ICONS.Copy className="w-4 h-4" />
            <span>
              {valueColumnMode === 'sum'
                ? t('bidding.copyRowSum') || 'Copy row sums'
                : t('bidding.copyRowHours') || 'Copy row hours'}
            </span>
          </button>
          <button
            onClick={async () => {
              if (contextMenu.shotId && canPasteRowValues) {
                await handlePasteRowValues(contextMenu.shotId);
              }
              setContextMenu(null);
            }}
            disabled={!canPasteRowValues}
            className="w-full px-4 py-2 text-left text-white hover:bg-gray-700 flex items-center space-x-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <ICONS.ClipboardPlus className="w-4 h-4" />
            <span>
              {valueColumnMode === 'sum'
                ? t('bidding.pasteRowSum') || 'Paste row sums'
                : t('bidding.pasteRowHours') || 'Paste row hours'}
            </span>
          </button>
          <button
            onClick={() => {
              const shot = shots.find(s => s.id === contextMenu.shotId);
              if (shot) {
                openAnnotationModal(shot);
              }
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-white hover:bg-gray-700 flex items-center space-x-2"
          >
            <ICONS.Edit className="w-4 h-4" />
            <span>{t('bidding.annotate') || 'Annotate'}</span>
          </button>
          <div className="border-t border-gray-700 my-1"></div>
          <button
            onClick={() => {
              const shot = shots.find(s => s.id === contextMenu.shotId);
              if (shot) {
                setVideoPlayerShot(shot);
              }
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-white hover:bg-gray-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!videoUrl}
          >
            <ICONS.Play className="w-4 h-4" />
            <span>{t('bidding.playShot') || 'Play shot'}</span>
            {!videoUrl && (
              <span className="text-xs text-gray-500 ml-2">(No video)</span>
            )}
          </button>
          <button
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.onchange = async (e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (!file) return;

                const formData = new FormData();
                formData.append('thumbnail', file);

                try {
                  const token = localStorage.getItem('spellbook_token');
                  const response = await fetch(
                    `${API_CONFIG.API_URL}/bidding/shots/${contextMenu.shotId}/thumbnail`,
                    {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` },
                      body: formData
                    }
                  );

                  if (response.ok) {
                    const data = await response.json();
                    setShots(shots.map(s => 
                      s.id === contextMenu.shotId 
                        ? { ...s, thumbnailUrl: data.thumbnailUrl } 
                        : s
                    ));
                  }
                } catch (error) {
                  console.error('Error uploading thumbnail:', error);
                }
              };
              input.click();
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-white hover:bg-gray-700 flex items-center space-x-2"
          >
            <ICONS.Upload className="w-4 h-4" />
            <span>{t('bidding.replaceThumbnail') || 'Replace thumbnail'}</span>
          </button>
          <button
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm,video/mpeg';
              input.onchange = async (e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (!file) return;

                // Validate file type
                const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska', 'video/webm', 'video/mpeg'];
                if (!allowedTypes.includes(file.type)) {
                  alert('Only video files are allowed (MP4, MOV, AVI, MKV, WebM, MPEG)');
                  return;
                }

                // Validate file size (max 500MB)
                if (file.size > 500 * 1024 * 1024) {
                  alert('File size must be less than 500MB');
                  return;
                }

                const formData = new FormData();
                formData.append('video', file);

                try {
                  const token = localStorage.getItem('spellbook_token');
                  const response = await fetch(
                    `${API_CONFIG.API_URL}/bidding/shots/${contextMenu.shotId}/video`,
                    {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` },
                      body: formData
                    }
                  );

                  if (response.ok) {
                    const data = await response.json();
                    setShots(shots.map(s => 
                      s.id === contextMenu.shotId 
                        ? { ...s, videoUrl: data.videoUrl } 
                        : s
                    ));
                    alert(file.type !== 'video/mp4' ? 'Video uploaded and converted to MP4!' : 'Video uploaded!');
                  } else {
                    const errorData = await response.json();
                    alert(`Failed to upload video: ${errorData.error || 'Unknown error'}`);
                  }
                } catch (error) {
                  console.error('Error uploading video:', error);
                  alert('Error uploading video. Please try again.');
                }
              };
              input.click();
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-white hover:bg-gray-700 flex items-center space-x-2"
          >
            <ICONS.Video className="w-4 h-4" />
            <span>{t('bidding.uploadVideo') || 'Upload video'}</span>
          </button>
          <div className="border-t border-gray-700 my-1"></div>
          <button
            onClick={async () => {
              const shot = shots.find(s => s.id === contextMenu.shotId);
              if (shot) {
                await handleInsertRow(shot.shotNumber, 'above');
              }
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-white hover:bg-gray-700 flex items-center space-x-2"
          >
            <ICONS.Plus className="w-4 h-4" />
            <span>{t('bidding.insertRowAbove') || 'Insert row above'}</span>
          </button>
          <button
            onClick={async () => {
              const shot = shots.find(s => s.id === contextMenu.shotId);
              if (shot) {
                await handleInsertRow(shot.shotNumber, 'below');
              }
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-white hover:bg-gray-700 flex items-center space-x-2"
          >
            <ICONS.Plus className="w-4 h-4" />
            <span>{t('bidding.insertRowBelow') || 'Insert row below'}</span>
          </button>
        </div>
      )}

      {columnContextMenu && (
        <ContextMenu
          x={columnContextMenu.x}
          y={columnContextMenu.y}
          items={getColumnContextMenuItems(columnContextMenu.spec)}
          onClose={() => setColumnContextMenu(null)}
        />
      )}

      <ConfirmationModal
        isOpen={fillColumnConfirm !== null}
        onClose={() => setFillColumnConfirm(null)}
        onConfirm={() => {
          if (!fillColumnConfirm) return;
          if (fillColumnConfirm.action === 'clear') {
            void handleClearColumnWithLevel(fillColumnConfirm.spec);
          } else if (fillColumnConfirm.virtualArtistId) {
            void handleFillColumnWithLevel(
              fillColumnConfirm.spec,
              fillColumnConfirm.virtualArtistId
            );
          }
        }}
        title={t('bidding.fillColumnWithLevel')}
        message={
          fillColumnConfirm
            ? fillColumnConfirm.action === 'clear'
              ? t('bidding.confirmClearColumn', {
                  spec: fillColumnConfirm.spec,
                  count: fillColumnConfirm.count,
                })
              : t('bidding.confirmFillColumn', {
                  level: fillColumnConfirm.levelLabel,
                  spec: fillColumnConfirm.spec,
                  count: fillColumnConfirm.count,
                })
            : ''
        }
        confirmText={t('bidding.confirmDelete')}
        cancelText={t('common.cancel')}
        type="warning"
      />

      <ConfirmationModal
        isOpen={deleteColumnConfirm !== null}
        onClose={() => setDeleteColumnConfirm(null)}
        onConfirm={() => {
          if (!deleteColumnConfirm) return;
          void handleDeleteColumn(deleteColumnConfirm.spec);
        }}
        title={t('bidding.deleteColumn')}
        message={
          deleteColumnConfirm
            ? t('bidding.confirmDeleteColumn', {
                spec: deleteColumnConfirm.spec,
                count: deleteColumnConfirm.count,
              })
            : ''
        }
        confirmText={t('bidding.confirmDelete')}
        cancelText={t('common.cancel')}
        type="danger"
      />

      {/* Shot Video Player */}
      {videoPlayerShot && videoUrl && (
        <ShotVideoPlayer
          videoUrl={videoUrl}
          frameIn={videoPlayerShot.frameIn || 1001}
          frameOut={videoPlayerShot.frameOut || 1001}
          shotNumber={videoPlayerShot.shotNumber}
          timecodeInSeconds={videoPlayerShot.timecodeInSeconds}
          timecodeOutSeconds={videoPlayerShot.timecodeOutSeconds}
          onClose={() => setVideoPlayerShot(null)}
        />
      )}

      {/* Conflict Resolution Modal */}
      <ConflictResolutionModal
        isOpen={conflictModal.show}
        assignmentId={conflictModal.assignmentId}
        field={conflictModal.field}
        userValue={conflictModal.userValue}
        serverValue={conflictModal.serverValue}
        currentData={conflictModal.currentData}
        onAcceptServer={async () => {
          // Accept server changes - update local state with server data
          if (conflictModal.currentData) {
            const isRevenue = revenueAssignments.some(a => a.id === conflictModal.assignmentId);
            const updateState = isRevenue ? setRevenueAssignments : setExpenseAssignments;
            
            updateState((prevAssignments: any[]) => prevAssignments.map(a =>
              a.id === conflictModal.assignmentId ? conflictModal.currentData! : a
            ));
            // Update version tracking
            if (conflictModal.currentData.id && conflictModal.currentData.rowVersion !== undefined) {
              assignmentVersionsRef.current.set(conflictModal.currentData.id, conflictModal.currentData.rowVersion);
            }
          }
          setConflictModal({ show: false, assignmentId: '', field: '', userValue: null, serverValue: null, currentData: null });
        }}
        onOverwrite={async () => {
          // Force overwrite - use server's current version
          const assignment = assignments.find(a => a.id === conflictModal.assignmentId);
          if (!assignment) {
            setConflictModal({ show: false, assignmentId: '', field: '', userValue: null, serverValue: null, currentData: null });
            return;
          }

          try {
            const token = localStorage.getItem('spellbook_token');
            const updateData: any = { 
              [conflictModal.field]: conflictModal.userValue,
              // Use server's current version for overwrite
              rowVersion: conflictModal.currentData?.rowVersion
            };
            
            // Determine endpoint based on assignment type
            const isRevenue = revenueAssignments.some(a => a.id === conflictModal.assignmentId);
            const endpoint = isRevenue
                ? `${API_CONFIG.API_URL}/bidding/assignments/${conflictModal.assignmentId}`
                : `${API_CONFIG.API_URL}/bidding/expense-assignments/${conflictModal.assignmentId}`;
            
            const response = await fetch(endpoint, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(updateData)
            });

            if (response.ok) {
              const updated = await response.json();
              const updateState = isRevenue ? setRevenueAssignments : setExpenseAssignments;
              
              updateState((prevAssignments: any[]) => prevAssignments.map(a =>
                a.id === conflictModal.assignmentId ? updated : a
              ));
              // Update version tracking
              if (updated.id && updated.rowVersion !== undefined) {
                assignmentVersionsRef.current.set(updated.id, updated.rowVersion);
              }
              setConflictModal({ show: false, assignmentId: '', field: '', userValue: null, serverValue: null, currentData: null });
            } else {
              const errorText = await response.text();
              console.error('Failed to overwrite:', response.status, errorText);
              alert('Не удалось перезаписать изменения. Попробуйте обновить страницу.');
            }
          } catch (error) {
            console.error('Error overwriting:', error);
            alert('Ошибка при перезаписи. Попробуйте обновить страницу.');
          }
        }}
        onCancel={() => {
          setConflictModal({ show: false, assignmentId: '', field: '', userValue: null, serverValue: null, currentData: null });
        }}
      />

      {/* Delete Confirm Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setShotsToDelete([]);
        }}
        onConfirm={handleDeleteConfirm}
        title={shotsToDelete.length === 1 
          ? t('bidding.confirmDeleteShot') 
          : t('bidding.confirmDeleteShots')}
        message={shotsToDelete.length === 1
          ? `${t('bidding.confirmDeleteShot')} #${shots.find(s => s.id === shotsToDelete[0])?.shotNumber}?`
          : t('bidding.confirmDeleteMultipleShots', { count: shotsToDelete.length })}
        detailMessage={t('bidding.shotWillBeDeleted')}
        itemCount={shotsToDelete.length}
        isDeleting={isDeleting}
      />

      {/* Participant Details Modal */}
      {participantDetailsModal && (
        <ParticipantDetailsModal
          isOpen={participantDetailsModal.isOpen}
          onClose={() => setParticipantDetailsModal(null)}
          participantName={participantDetailsModal.artistName}
          participantColor={participantDetailsModal.artistColor}
          participantId={participantDetailsModal.artistId}
          biddingSheetId={biddingSheetId}
          details={getParticipantDetails(participantDetailsModal.artistId)}
          currency={currency}
        />
      )}

      {/* Bulk Approve Modal */}
      <BulkApproveModal
        isOpen={isBulkApproveModalOpen}
        onClose={() => setIsBulkApproveModalOpen(false)}
        shotIds={Array.from(selectedShots)}
        biddingSheetId={biddingSheetId}
        onApprove={handleBulkApprove}
      />

      {/* Annotation Modal */}
      {annotationModal.show && annotationModal.shot && (
        <AnnotationModal
          shot={annotationModal.shot}
          onClose={closeAnnotationModal}
          onSave={handleSaveAnnotation}
          onCommentsCountChange={handleCommentsCountChange}
          commentId={annotationModal.commentId}
        />
      )}

      {/* Autofill Modal */}
      {autofillModal.show && autofillModal.pattern && (
        <AutofillModal
          shots={shots}
          startIndex={autofillModal.startIndex}
          pattern={autofillModal.pattern}
          onConfirm={handleConfirmAutofill}
          onCancel={handleCancelAutofill}
          isManual={autofillModal.isManual || false}
        />
      )}
    </div>
  );
};

export default ShotTable;

